import React, { useEffect, useState, useCallback, useMemo, KeyboardEvent, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, useLoadScript, Marker, MarkerF, MarkerClustererF, InfoWindow, OverlayView, OverlayViewF, Libraries } from '@react-google-maps/api';
import { API_BASE_URL, GOOGLE_MAPS_API_KEY } from '../config';
import './ProspectingPage.css';
import Alert from './Alert';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import SearchPanel from './SearchPanel';
import NewsFeed, { NewsArticle } from './NewsFeed';
import { ProspectingFilters } from './ProspectingFilterBar';
import AddToSalesforceModal, {
  AddToSalesforcePayload,
  AddToSalesforceResult,
  SF_ACCOUNT_TYPE_OPTIONS,
  SF_TIER_OPTIONS,
  SfAccountType,
  normalizeBaseUrl,
} from './AddToSalesforceModal';
import BulkAddToSalesforceModal, { BulkAddBusiness } from './BulkAddToSalesforceModal';
import PhotoLightbox from './PhotoLightbox';
import DuplicateAlertModal, { DuplicateAlertPayload } from './DuplicateAlertModal';
import { toast } from 'react-hot-toast';

// Import icons for services
import {
  FaShieldAlt,
  FaUserShield,
  FaVideo,
  FaLock,
  FaDoorOpen,
  FaKey,
  FaBell,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaEye,
  FaUsers,
  FaUserSecret,
  FaCar,
  FaBuilding,
  FaIndustry,
  FaStore,
  FaSchool,
  FaHospital,
  FaCamera,
  FaSpinner,
  FaMobileAlt,
  FaFileDownload,
  FaFileExport,
  FaLinkedin,
  FaMapMarked,
  FaFileAlt,
  FaMapMarkerAlt,
  FaSearch,
  FaTimes,
  FaUserTie,
  FaChevronDown,
  FaChevronUp,
  FaFire,
  FaNewspaper,
  FaCrosshairs,
  FaPhoneAlt,
  FaYelp,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaEnvelope,
  FaSync,
  FaCheck
} from 'react-icons/fa';
import { FiGlobe } from 'react-icons/fi';

// Default search radius in meters (5km)
const DEFAULT_RADIUS = 5000;

// Define the libraries we need. ('drawing' was dropped — DrawingManager was
// removed from the Maps JS API in v3.65 and we never used it for real drawing;
// every search circle is created programmatically via new google.maps.Circle.)
const libraries: Libraries = ['visualization'];

interface EnrichmentProgress {
  progress: number;
  currentUrl?: string;
  totalUrls?: number;
  processedUrls?: number;
  successfulUrls?: number;
  failedUrls?: number;
}

interface Customer {
  customer_tag: string;
  stripe: {
    name: string;
    address_line1: string;
    address_line2?: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
    address_country?: string;
    email: string;
    subscription_status: string;
    contract_units: number;
    trial?: {
      end_date: string;
      days_remaining: number;
    };
  };
  description?: string;
  stats?: Record<string, any>;
  account_executive?: string;
}

interface MappedCustomer {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  account_executive?: string;
}

interface Contact {
  name: string;
  title: string;
  email: string;
  linkedin_url: string;
}

interface Business {
  name: string;
  address: string;
  phone?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  // Set by the background website-status check: the homepage is dead/parked.
  // Drives the one-click "DQ (dead site)" action on the card.
  website_dead?: boolean;
  website_dead_phrase?: string;
  // Off-domain redirect target (info only — redirects are usually live
  // businesses that web-consolidated, so this does NOT trigger a DQ).
  website_redirect_to?: string;
  // True once the background website-status check has returned for this row
  // (any verdict). Drives the "Analyzing…" → "Visit Website" button label.
  website_checked?: boolean;
  // Social links + email scraped from the homepage (shown as icons on the card).
  yelp_url?: string;
  instagram_url?: string;
  facebook_url?: string;
  x_url?: string;
  company_email?: string;
  // Postal address scraped from the homepage footer/JSON-LD. Separate from the
  // Places `address` field so the Lead Finder can show "what the site says" as
  // a chip without overwriting the search-time address. Add-to-SF uses this to
  // populate Billing fields when the account has none.
  extracted_address?: string;
  location: {
    lat: number;
    lng: number;
  };
  services?: Service[];
  isEnriched?: boolean;
  isEnriching?: boolean;
  enrichmentError?: string;
  marker?: google.maps.Marker;
  apollo_data?: {
    linkedin_url?: string;
    website_url?: string;
    organization_name?: string;
    employee_count?: number;
    industry?: string;
    annual_revenue?: string;
  };
  linkedin_url?: string;
  has_linkedin?: boolean;
  relevance_score?: number;
  is_existing_customer?: boolean;
  matched_customer_name?: string;
  matched_customer_tag?: string;
  contacts?: Contact[];
  contactsLoading?: boolean;
  contactsExpanded?: boolean;
  // Salesforce match state — populated by /api/prospecting/salesforce/check-websites
  // after each result batch arrives. See getAvailability() for the derived UI bucket.
  salesforce_status?: 'checking' | 'in_sf' | 'not_in_sf' | 'no_website' | 'error';
  salesforce_matches?: SalesforceAccountMatch[];
  // True after a successful /check-websites response has set salesforce_matches.
  // Used to break the auto-refresh loop in the useEffect — without this, rows
  // whose SF Account legitimately has no Type would keep retriggering the call.
  salesforce_type_refreshed?: boolean;
  // Content photos scraped on-demand from the homepage for the photo lightbox.
  // Populated lazily the first time the rep clicks the "Photos" chip.
  site_images?: SiteImage[];
  site_images_loading?: boolean;
  // True once a photo fetch has completed (any result) — distinguishes
  // "not fetched yet" from "fetched, found none".
  site_images_loaded?: boolean;
  // True when the last fetch failed (backend down / network) — drives a distinct
  // "Retry" chip state so a failure isn't silently indistinguishable from idle.
  site_images_error?: boolean;
  // Marks rows loaded from a Salesforce report (vs. Google Places search). The
  // map renders these with a Type-based color palette instead of the standard
  // ownership-based one; the result card also hides "+ Add to Salesforce".
  from_sf_report?: boolean;
  // Google Places business_status — surfaced on the card so reps see CLOSED
  // PERMANENTLY / TEMPORARILY at a glance instead of having to open Google.
  // Missing or 'OPERATIONAL' renders nothing (the implicit healthy default).
  business_status?: 'OPERATIONAL' | 'CLOSED_TEMPORARILY' | 'CLOSED_PERMANENTLY';
  // Days since the most recent Google review. Helps the rep tell a quiet B2B
  // (long gap, otherwise OPERATIONAL) from a likely-defunct row.
  last_review_age_days?: number;
}

export interface SiteImage {
  src: string;
  alt?: string;
}

export interface SalesforceAccountMatch {
  id: string;
  name: string;
  website?: string;
  phone?: string | null;
  owner_id?: string;
  owner_name?: string;
  number_of_employees?: number | null;
  zoominfo_id?: string | null;
  linkedin_url?: string | null;
  instagram_url?: string | null;
  facebook_url?: string | null;
  x_url?: string | null;
  yelp_url?: string | null;
  company_email?: string | null;
  type?: string;
  tier?: string | null;
  // Account.Industry from Salesforce (e.g. "Pest Control",
  // "Security & Investigations"). Populated for SF-report rows.
  industry?: string | null;
}

// Account.Type values that should also disappear from search results when set
// from the Edit modal. Mirrors backend _PROSPECTING_BLOCKED_TYPES.
const SF_BLOCKED_TYPES = new Set(['disqualified', 'do not contact', 'do-not-contact']);

// Single neutral-slate cluster bubble for the Lead Finder result layer. We
// pass one style and MarkerClusterer will use it across every bucket — keeps
// the count the only signal (v1 decision: count only, no per-state coloring).
// SVG is inlined as a data URI so there's no extra asset/hosting dependency.
const CLUSTER_BUBBLE_SVG = encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='44' height='44'>" +
    "<circle cx='22' cy='22' r='20' fill='#334155' stroke='white' stroke-width='2' opacity='0.95'/>" +
    "</svg>",
);
const CLUSTER_STYLES = [
  {
    url: `data:image/svg+xml;utf-8,${CLUSTER_BUBBLE_SVG}`,
    height: 44,
    width: 44,
    textColor: '#ffffff',
    textSize: 13,
    fontWeight: 'bold',
    // 44px icon → 22px anchor centers the count over the bubble.
    anchorText: [0, 0] as [number, number],
  },
];

export type Availability = 'add' | 'yours' | 'claimed' | 'checking' | 'no_website' | 'unknown';

// Mirrors backend/prospecting.py:normalize_website. Used to key into the
// check-websites response map. Must stay in sync with the backend.
const SF_WEBSITE_BLACKLIST = new Set([
  'facebook.com', 'm.facebook.com', 'instagram.com', 'linkedin.com',
  'twitter.com', 'x.com', 'tiktok.com', 'youtube.com', 'm.youtube.com',
  'yelp.com', 'linktr.ee', 'bit.ly', 't.co', 'goo.gl',
  'google.com', 'maps.google.com', 'sites.google.com', 'business.google.com',
  'wa.me',
]);
export function normalizeDomainClient(url: string | null | undefined): string | null {
  if (!url) return null;
  let s = String(url).trim().toLowerCase();
  if (!s) return null;
  const proto = s.indexOf('://');
  if (proto >= 0) s = s.slice(proto + 3);
  for (const sep of ['/', '?', '#']) {
    const i = s.indexOf(sep);
    if (i >= 0) s = s.slice(0, i);
  }
  const colon = s.indexOf(':');
  if (colon >= 0) s = s.slice(0, colon);
  if (s.startsWith('www.')) s = s.slice(4);
  if (!s || !s.includes('.')) return null;
  if (SF_WEBSITE_BLACKLIST.has(s)) return null;
  return s;
}

/**
 * Ensure a URL has a scheme so `window.open` / anchor `href` doesn't treat it
 * as a same-origin relative path. Google Places returns websites with their
 * stored scheme (`https://...`), but SF Account.Website and Apollo data
 * sometimes lack one — landing the user on `summit.plix.ai/www.eagleeyepro.com`
 * instead of the real site. Returns null for empty/invalid input.
 */
// True only for a real {lat, lng} with finite numbers. Guards Google Maps
// overlays: a Places result can arrive with {lat: null, lng: null}, which is a
// truthy object (so `loc || fallback` won't catch it) but makes <Marker> throw
// inside Maps during commit — surfacing as a cross-origin "Script error.".
export function isValidLatLng(
  p: { lat?: number | null; lng?: number | null } | null | undefined,
): p is { lat: number; lng: number } {
  return !!p && typeof p.lat === 'number' && typeof p.lng === 'number'
    && Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

export function externalUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const s = String(url).trim();
  if (!s) return null;
  // Already has a scheme (http, https, mailto, tel, etc.) — pass through.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s) || /^(mailto|tel):/i.test(s)) return s;
  return `https://${s}`;
}

// Display form of a website: scheme + host + trailing slash, dropping any
// path/query/hash. e.g. "https://norcalprivatepro.com/contact-us/" →
// "https://norcalprivatepro.com/". Falls back to the raw string if unparseable.
export function shortenWebsiteDisplay(url: string | null | undefined): string {
  const s = (url || '').trim();
  if (!s) return '';
  try {
    const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
    return `${u.protocol}//${u.hostname}/`;
  } catch {
    return s;
  }
}

// Toast content with a click-through to the new Salesforce record. Returns the
// bare label when no URL is available so callers can pass it through unchanged.
function sfToastWithLink(label: string, url?: string): string | React.ReactElement {
  if (!url) return label;
  return (
    <span>
      {label}{' '}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontWeight: 600, textDecoration: 'underline' }}
      >
        Open in Salesforce →
      </a>
    </span>
  );
}

export function getAvailability(b: Business, currentUserId: string | null): Availability {
  if (b.salesforce_status === 'checking') return 'checking';
  if (b.salesforce_status === 'no_website') return 'no_website';
  if (b.salesforce_status === 'error') return 'unknown';
  if (b.salesforce_status === 'not_in_sf') return 'add';
  if (b.salesforce_status === 'in_sf') {
    // 'yours' ONLY when EVERY SF Account matching this domain is owned by
    // the current user. If any account is owned by another rep, treat the
    // whole business as claimed by them — otherwise a single Johann-owned
    // junk duplicate (e.g. a school district account incorrectly sharing
    // aus.com as its Website) hides the real canonical match owned elsewhere.
    const matches = b.salesforce_matches || [];
    const claimedByOther = matches.some(m => m.owner_id && m.owner_id !== currentUserId);
    return claimedByOther ? 'claimed' : 'yours';
  }
  return 'unknown';
}

// When multiple SF Accounts share a domain, prefer the one we should display
// to the user: for 'claimed' that's the first account NOT owned by the current
// user (so the badge shows the actual blocking rep); for 'yours' / single
// match, just the first one.
export function pickDisplayMatch(b: Business, currentUserId: string | null): SalesforceAccountMatch | undefined {
  const matches = b.salesforce_matches || [];
  if (matches.length === 0) return undefined;
  const other = matches.find(m => m.owner_id && m.owner_id !== currentUserId);
  return other || matches[0];
}

interface Service {
  name: string;
  description: string;
}

interface EnrichmentTask {
  taskId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalUrls: number;
  processed: number;
  currentUrl?: string;
  results: EnrichmentResult[];
  startedAt: string;
  completedAt?: string;
  totalProcessed?: number;
  successful?: number;
  failed?: number;
  error?: string;
}

interface EnrichmentResult {
  url: string;
  services?: Service[];
  error?: string;
  enrichedAt: string;
}

interface Circle extends google.maps.Circle {
  getRadius(): number;
  getCenter(): google.maps.LatLng | null;
  setMap(map: google.maps.Map | null): void;
}

if (!GOOGLE_MAPS_API_KEY) {
  console.error('Google Maps API key is missing. Please check your environment variables.');
}

// Add interface for search parameters
interface AddressSearchParams extends Record<string, string> {
  address: string;
  search_terms: string;
  radius: string;
}

interface CoordinateSearchParams extends Record<string, string> {
  lat: string;
  lng: string;
  search_terms: string;
  radius: string;
}

type SearchParams = AddressSearchParams | CoordinateSearchParams;

// Define interface for saved searches
interface SavedSearch {
  id: string;
  name: string;
  searchTerms: string[];
  radius: number;
  customerId?: string;
  location?: {
    lat: number;
    lng: number;
  };
  createdAt: number;
}

// Update the SearchHistory interface to include location for each business
interface SearchHistoryCompany {
  name: string;
  address: string;
  phone?: string;
  services?: Service[];
  location: {
    lat: number;
    lng: number;
  };
  website?: string;
  rating?: number;
  isEnriched?: boolean;
  apollo_data?: {
    linkedin_url?: string;
    website_url?: string;
    organization_name?: string;
    employee_count?: number;
    industry?: string;
    annual_revenue?: string;
  };
  linkedin_url?: string;
  has_linkedin?: boolean;
}

interface SearchHistory {
  id: string;
  searchDate: string;
  searchTerms: string[];
  radius: number;
  location: {
    lat: number;
    lng: number;
    address?: string;
  };
  results: {
    totalResults: number;
    companies: SearchHistoryCompany[];
  };
}

// Add this interface for search metadata
interface SearchMetadata {
  total_results: number;
  search_radius: number;
  search_radius_miles: number;
}

// Service icon mapping with more comprehensive patterns
const SERVICE_ICON_MAP: Record<string, { icon: React.ReactNode; patterns: string[] }> = {
  'Armed Security': {
    icon: <FaUserShield title="Armed Security" />,
    patterns: ['armed', 'weapon', 'firearm', 'armed security', 'armed guard']
  },
  'Unarmed Security': {
    icon: <FaUsers title="Unarmed Security" />,
    patterns: ['unarmed', 'unarmed security', 'unarmed guard', 'security officer']
  },
  'Surveillance': {
    icon: <FaVideo title="Surveillance" />,
    patterns: ['surveillance', 'cctv', 'camera', 'monitoring', 'video']
  },
  'Access Control': {
    icon: <FaLock title="Access Control" />,
    patterns: ['access control', 'entry', 'access management', 'door control']
  },
  'Patrol': {
    icon: <FaCar title="Patrol" />,
    patterns: ['patrol', 'mobile', 'vehicle', 'patrol service']
  },
  'Commercial Security': {
    icon: <FaBuilding title="Commercial Security" />,
    patterns: ['commercial', 'office', 'business', 'retail']
  },
  'Industrial Security': {
    icon: <FaIndustry title="Industrial Security" />,
    patterns: ['industrial', 'warehouse', 'factory', 'manufacturing']
  },
  'Retail Security': {
    icon: <FaStore title="Retail Security" />,
    patterns: ['retail', 'store', 'shop', 'mall']
  },
  'Educational Security': {
    icon: <FaSchool title="Educational Security" />,
    patterns: ['school', 'education', 'campus', 'university']
  },
  'Healthcare Security': {
    icon: <FaHospital title="Healthcare Security" />,
    patterns: ['hospital', 'medical', 'healthcare', 'clinic']
  },
  'Event Security': {
    icon: <FaUsers title="Event Security" />,
    patterns: ['event', 'concert', 'venue', 'crowd']
  },
  'Executive Protection': {
    icon: <FaUserSecret title="Executive Protection" />,
    patterns: ['executive', 'vip', 'bodyguard', 'protection']
  },
  'Remote Monitoring': {
    icon: <FaEye title="Remote Monitoring" />,
    patterns: ['remote', 'monitoring', 'virtual', 'watch']
  },
  'Mobile Patrol': {
    icon: <FaMobileAlt title="Mobile Patrol" />,
    patterns: ['mobile', 'patrol', 'route', 'vehicle']
  },
  'Emergency Response': {
    icon: <FaExclamationTriangle title="Emergency Response" />,
    patterns: ['emergency', 'response', 'crisis', 'incident']
  },
  'Security Systems': {
    icon: <FaBell title="Security Systems" />,
    patterns: ['system', 'alarm', 'security system', 'equipment']
  }
};

// Default icon for unknown services
const DEFAULT_SERVICE_ICON = <FaShieldAlt title="Other Security Service" />;

// Service Icon component with improved matching
const ServiceIcon: React.FC<{ service: Service }> = ({ service }) => {
  // Find matching icon by checking service name against patterns
  const matchedService = Object.entries(SERVICE_ICON_MAP).find(([_, { patterns }]) =>
    patterns.some(pattern => 
      service.name.toLowerCase().includes(pattern) || 
      (service.description || '').toLowerCase().includes(pattern)
    )
  );
  
  return (
    <div 
      className="service-icon" 
      data-tooltip={service.name}
    >
      {matchedService ? matchedService[1].icon : DEFAULT_SERVICE_ICON}
    </div>
  );
};

// Add utility function for conversion
const metersToMiles = (meters: number): string => {
  return (meters / 1609.34).toFixed(1);
};

interface ApiBusinessResponse {
  name: string;
  address: string;
  services?: Array<{
    name: string;
    description: string;
  }>;
  location?: {
    lat: number;
    lng: number;
  };
  phone?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  isEnriched?: boolean;
  isEnriching?: boolean;
  enrichmentError?: string;
  apollo_data?: {
    linkedin_url?: string;
    website_url?: string;
    organization_name?: string;
    employee_count?: number;
    industry?: string;
    annual_revenue?: string;
  };
  linkedin_url?: string;
}

interface ApiSearchResponse {
  businesses: ApiBusinessResponse[];
  center: {
    lat: number;
    lng: number;
  };
}

interface Vertical {
  id: string;
  name: string;
  description: string;
  terms?: string[];
}

// Result ordering: 'priority' ranks the best accounts to enrich first (most
// social links, then most reviews); 'employees' and 'rating' let the rep sort
// purely by that metric, highest first, with distance as the tiebreaker.
type LeadSortKey = 'priority' | 'reviews' | 'rating' | 'employees';

// Enrichment priority: number of social profiles is the dominant signal, with
// review volume as the secondary sort. Mirrors backend _enrich_priority.
const SOCIAL_FIELDS: (keyof Business)[] = [
  'linkedin_url', 'instagram_url', 'facebook_url', 'x_url', 'yelp_url', 'company_email',
];
const socialLinkCount = (b: Business): number =>
  SOCIAL_FIELDS.reduce((n, f) => n + (String(b[f] || '').trim() ? 1 : 0), 0);

// Hoverable color-legend chip. Renders the popover via a portal into <body> so
// it isn't clipped by sidebar / panel `overflow: hidden` parents (the earlier
// pure-CSS version was unreadable for exactly that reason). Position is
// computed from the icon's bounding rect each time the popover opens, with a
// hard guard that keeps it inside the viewport.
const COLOR_LEGEND_ROWS: Array<[string, string]> = [
  ['#10b981', 'Open — assigned to you'],
  ['#ef4444', 'Assigned — owned by another rep'],
  ['#ec4899', 'Add — not in Salesforce yet'],
  ['#eab308', 'Existing Plix customer'],
  ['#94a3b8', 'Checking / no website'],
];
const ColorLegendTrigger: React.FC = () => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const popoverW = 260;
    const popoverH = 165;
    // Default: above the icon, right-edge aligned with it.
    let top = r.top - popoverH - 8;
    let left = r.right - popoverW;
    // Clamp into the viewport so it never escapes off-screen / behind a panel.
    if (top < 8) top = r.bottom + 8;                                 // flip below if no room above
    if (left < 8) left = 8;
    if (left + popoverW > window.innerWidth - 8) left = window.innerWidth - popoverW - 8;
    setPos({ top, left });
  };
  const hide = () => setPos(null);

  return (
    <>
      <span
        ref={triggerRef}
        className="color-legend"
        tabIndex={0}
        aria-label="Color legend"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <FaQuestionCircle aria-hidden />
      </span>
      {pos && createPortal(
        <div
          className="color-legend-portal"
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="color-legend-title">What the colors mean</div>
          {COLOR_LEGEND_ROWS.map(([hex, label]) => (
            <div key={hex} className="color-legend-row">
              <i style={{ background: hex }} />
              <span>{label}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

// What to call an existing customer in the UI. The backend only matches when
// the two names are equal once normalized, so matched_customer_name can never
// say anything the business name above it didn't -- but customer_tag is the key
// Summit files them under, and for ~30% of customers it is a different company
// name entirely. Returns null when the tag is just the business name again, so
// the popup doesn't state the obvious.
const normalizeCompanyName = (s: string) =>
  s
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\s+(llc|inc|corp|ltd|co|company|services|group)$/, '')
    .trim();

/** `alias` when it genuinely names something else, else null. */
const differentName = (alias?: string, name?: string): string | null => {
  const a = (alias || '').trim();
  if (!a) return null;
  return normalizeCompanyName(a) === normalizeCompanyName(name || '') ? null : a;
};

/** How a customer is named across the dropdown, SearchPanel and map popup. */
const customerLabel = (c: { name: string; description?: string }): string =>
  c.description && c.description !== 'No Description' ? c.description : c.name;

const customerReference = (b: {
  name?: string;
  matched_customer_tag?: string;
}): string | null => differentName(b.matched_customer_tag, b.name);

const ProspectingPage: React.FC = () => {
  const [sortBy, setSortBy] = useState<LeadSortKey>('priority');
  const [customers, setCustomers] = useState<MappedCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<MappedCustomer | null>(null);
  // Whether the pin popup has been dismissed for the current customer. The
  // popup itself is derived from selectedCustomer rather than held as a second
  // copy -- thirteen call sites clear selectedCustomer, and a parallel copy
  // would strand a popup naming a customer the map had already left. Closing it
  // must not clear selectedCustomer either: that anchors the search circle.
  const [pinInfoDismissed, setPinInfoDismissed] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(4);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<Business[]>([]);
  // Hide the bottom search panel after a search completes so the results
  // sidebar gets full attention. User can reopen with the "Edit search"
  // button in the sidebar header, or by clearing results.
  const [searchPanelHidden, setSearchPanelHidden] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  // Photo lightbox: the business whose site photos are open (null = closed).
  // The lightbox owns its own active index + nav.
  const [lightboxBusiness, setLightboxBusiness] = useState<Business | null>(null);
  // Hover-linking between sidebar and map. We key by `name|address` so the
  // value survives result-list re-renders / sort changes.
  const [hoveredBusinessKey, setHoveredBusinessKey] = useState<string | null>(null);
  // Tracks where the most recent hover came from. The map → sidebar scroll
  // effect runs on every hover; the sidebar → map pan effect ONLY runs when
  // the source is 'sidebar' — panning the map every time the rep moves their
  // mouse over a pin would feel awful.
  const hoverSourceRef = useRef<'sidebar' | 'map' | null>(null);
  // Map-marker clustering. Off by default — clustering was reported slow at
  // scale, so reps opt in. The ON path is heavily optimized (icon cache,
  // MarkerF, stable cluster list across hovers).
  const [clusteringEnabled, setClusteringEnabled] = useState(false);
  // Shared icon cache. Markers use a tiny set of unique appearances
  // (5 fill colors × hovered/normal × customer/normal). Caching them keeps
  // MarkerF's prop-equality check stable so the underlying google.maps.Marker
  // is reused across renders instead of being torn down and rebuilt — the
  // single biggest win on hover-heavy interactions.
  const iconCacheRef = useRef<Map<string, google.maps.Symbol>>(new Map());
  const getMarkerIcon = useCallback(
    (fillColor: string, isHovered: boolean, isCustomer: boolean): google.maps.Symbol => {
      const key = `${fillColor}|${isHovered ? 1 : 0}|${isCustomer ? 1 : 0}`;
      const cache = iconCacheRef.current;
      const hit = cache.get(key);
      if (hit) return hit;
      const icon: google.maps.Symbol = {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor,
        fillOpacity: isCustomer ? 0.55 : 0.95,
        scale: isHovered ? 14 : 9,
        strokeColor: 'white',
        strokeWeight: isHovered ? 3 : 2,
      };
      cache.set(key, icon);
      return icon;
    },
    [],
  );
  // True when the current `searchResults` came from a team-activity cluster
  // click (rather than a live search). Surfaces a "Back to Team Activity"
  // affordance so the user can return to the hotspot map view.
  const [resultsFromCluster, setResultsFromCluster] = useState(false);
  // Per-card DOM refs so hovering a pin can scroll the matching card into view.
  const resultRefsMap = useRef<Map<string, HTMLDivElement | null>>(new Map());
  // When the hovered key changes (from either side), scroll the matching
  // sidebar card into view. `block: 'nearest'` makes this a no-op when the
  // card is already visible (e.g. sidebar→sidebar hovers) — only fires for
  // map→sidebar transitions where the card is off-screen.
  useEffect(() => {
    if (!hoveredBusinessKey) return;
    const el = resultRefsMap.current.get(hoveredBusinessKey);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [hoveredBusinessKey]);
  // Sidebar → map pan: when the rep hovers a result card, ensure its pin is
  // on screen. Only pans when the pin is currently off-screen — so a card hover
  // for a pin already in view stays still, and only the "lift out of cluster"
  // visual fires. No auto-zoom (felt disorienting in early try).
  useEffect(() => {
    if (!hoveredBusinessKey || hoverSourceRef.current !== 'sidebar') return;
    if (!mapRef.current) return;
    const b = getCombinedResults().find(
      (x) => `${x.name}|${x.address}` === hoveredBusinessKey,
    );
    if (!b || !isValidLatLng(b.location)) return;
    const bounds = mapRef.current.getBounds();
    if (!bounds || !bounds.contains(b.location)) {
      mapRef.current.panTo(b.location);
    }
    // getCombinedResults intentionally omitted from deps — it's stable enough
    // within a render cycle and including it would re-pan on every state tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredBusinessKey]);
  const [currentInput, setCurrentInput] = useState('');
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [searchRadius, setSearchRadius] = useState(DEFAULT_RADIUS);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [isPinMode, setIsPinMode] = useState(false);
  const [customPin, setCustomPin] = useState<{ lat: number; lng: number } | null>(null);
  const [customPinActive, setCustomPinActive] = useState(false);
  const [isCircleDragging, setIsCircleDragging] = useState(false);
  
  // Lock circle when searching
  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.setDraggable(!isSearching);
      circleRef.current.setEditable(!isSearching);
    }
  }, [isSearching]);

  // Add new state variables for saved searches
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [currentSearchName, setCurrentSearchName] = useState('');
  // (was: activeTab — removed alongside the broken Recent Searches tab.)
  const [editingSavedSearch, setEditingSavedSearch] = useState<SavedSearch | null>(null);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [newSearchTerms, setNewSearchTerms] = useState('');
  const [showSavedSearchesDropdown, setShowSavedSearchesDropdown] = useState(false);
  
  // Vertical search state
  const [selectedVertical, setSelectedVertical] = useState<string>('private_security');
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [searchTermsUsed, setSearchTermsUsed] = useState<string[]>([]);
  const [searchingTerm, setSearchingTerm] = useState<string | null>(null);
  const [searchProgress, setSearchProgress] = useState<{ current: number; total: number } | null>(null);

  // Add new state variables for enrichment
  const [enrichmentTask, setEnrichmentTask] = useState<EnrichmentTask | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isEnrichingApollo, setIsEnrichingApollo] = useState(false);
  const [enrichmentProgress, setEnrichmentProgress] = useState<EnrichmentProgress>({
    progress: 0,
    currentUrl: undefined,
    totalUrls: 0,
    processedUrls: 0,
    successfulUrls: 0,
    failedUrls: 0
  });
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);
  const enrichmentStatusInterval = useRef<number | null>(null);

  // Add new state for search history
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);

  // Add state for sort option
  // Phase 1-6: New state
  // Default to "show only available" — surfaces actionable prospects
  // (not-in-SF or owned by me) and drops claimed-by-others from both the
  // result list and the map markers.
  // Default to no ownership filter — was previously `showOnlySfAvailable: true`,
  // which silently hid every row on a fresh SF report load (the TAM lists are
  // mostly claimed by other reps).
  const [filters, setFilters] = useState<ProspectingFilters>({});
  // Salesforce check state — see getAvailability() for the derived UI bucket per card
  const [currentSfUserId, setCurrentSfUserId] = useState<string | null>(null);
  const [addSfBusiness, setAddSfBusiness] = useState<Business | null>(null);
  // SF DUPLICATES_DETECTED response surfaced as its own modal so the rep sees
  // what was matched and can choose Open existing / Create anyway. `form` is
  // the original payload so "Create anyway" can re-POST with allow_save=true.
  const [dupAlert, setDupAlert] = useState<{
    alert: DuplicateAlertPayload;
    form: AddToSalesforcePayload;
    target: Business;
    targetKey: string;
    saving: boolean;
  } | null>(null);

  // SF report load — paste a Report ID/URL, the backend extracts Account IDs
  // and joins with our geocode cache; the result REPLACES current search results.
  const [sfReportInput, setSfReportInput] = useState('');
  // True while a reverse Google lookup (search bar → business details) runs.
  const [reverseLookupLoading, setReverseLookupLoading] = useState(false);
  // Multi-step progress for the top-bar search. Set while handleReverseLookup
  // is running so the results panel can render an inline placeholder card with
  // a live status message ("Scraping website…", "Finding location…") instead
  // of popping the real card up with half-populated fields.
  const [activeLookup, setActiveLookup] = useState<{ query: string; step: string } | null>(null);
  const [sfReportLoading, setSfReportLoading] = useState(false);
  // Inline error feedback for the top-bar report input. Toasts get missed
  // when the rep is focused on the field — this paints a red border + a
  // small message right below the input until they edit it or load again.
  const [sfReportError, setSfReportError] = useState<string | null>(null);
  const [sfReportMeta, setSfReportMeta] = useState<{
    name: string;
    plotted: number;
    missing: Array<{ id: string; name: string; address: string }>;
    no_address_count: number;
    backfilling?: boolean;
    refreshing?: boolean;
    // True while /api/prospecting/sf-report/find-websites is in flight. Locks
    // the "Enrich" button + shows a spinner on the chip.
    findingWebsites?: boolean;
    // Live per-row progress from the enrichment SSE stream — drives the
    // "Enriching 142/614" label on the button.
    enrichProgress?: { current: number; total: number };
    // The raw input (report Id or Lightning URL) used to load this report.
    // Kept so the chip refresh button can replay the same fetch without
    // making the user re-paste it (the top search bar is hidden while a
    // report is on screen).
    sourceInput: string;
    // Epoch ms of the last successful load. Shown on the chip as a relative
    // timestamp ("Updated 12:34 PM") so the rep can confirm a refresh
    // landed even when the row count is unchanged.
    lastRefreshedAt: number;
    // True for ~1.5s right after a refresh completes — drives the green
    // checkmark + border pulse on the chip. Auto-cleared by a timeout.
    justRefreshed?: boolean;
  } | null>(null);
  // Multi-select for bulk Add-to-Salesforce. Keys are `${name}|${address}`
  // (the same hoverKey pattern used for marker hover state), so selection
  // survives reorder/filter changes without breaking on indices.
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  // True while the "Enrich + Add Selected" flow is firing Waterfall against
  // the selected set. Locks both bulk buttons + Clear so the rep can't double-
  // submit or re-select during the multi-second batch.
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const heatmapLayerRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);
  const [autoEnrichProgress, setAutoEnrichProgress] = useState<{ current: number; total: number } | null>(null);
  const [multiBranchResults, setMultiBranchResults] = useState<Business[]>([]);
  const [multiBranchLoading, setMultiBranchLoading] = useState<string | null>(null);
  // Which franchise card (grouped by website) has its location list expanded.
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  // Type-filter dropdown: open state + draft selections (committed on Apply).
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [typeDraft, setTypeDraft] = useState<string[]>([]);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  // Clicking outside the open Type dropdown clears the filter (and closes it).
  useEffect(() => {
    if (!typeMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDraft([]);
        setFilters(f => ({ ...f, dispositions: undefined }));
        setTypeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [typeMenuOpen]);
  // Tier-filter dropdown: same shape as Type. Values: '1' | '2' | '3' | 'none'.
  const [tierMenuOpen, setTierMenuOpen] = useState(false);
  const [tierDraft, setTierDraft] = useState<string[]>([]);
  const tierDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!tierMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (tierDropdownRef.current && !tierDropdownRef.current.contains(e.target as Node)) {
        setTierDraft([]);
        setFilters(f => ({ ...f, tiers: undefined }));
        setTierMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tierMenuOpen]);
  // Industry-filter dropdown: same shape as Type/Tier. Options are derived from
  // the industries present in the results; 'none' buckets rows with no industry.
  const [industryMenuOpen, setIndustryMenuOpen] = useState(false);
  const [industryDraft, setIndustryDraft] = useState<string[]>([]);
  const industryDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!industryMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(e.target as Node)) {
        setIndustryDraft([]);
        setFilters(f => ({ ...f, industries: undefined }));
        setIndustryMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [industryMenuOpen]);
  const [showCustomerPins, setShowCustomerPins] = useState(true);
  const [showNewsPins, setShowNewsPins] = useState(false);
  const [showNewsHeatmap, setShowNewsHeatmap] = useState(false);
  const newsHeatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [filteredNewsArticles, setFilteredNewsArticles] = useState<NewsArticle[]>([]);
  const [selectedNewsCluster, setSelectedNewsCluster] = useState<{ lat: number; lng: number; articles: NewsArticle[] } | null>(null);

  // Team Activity overlay — AoE-minimap-style view of where the team is
  // searching, sourced from the shared google_search_cache. Default on
  // (the team-focus view is the first thing users see); auto-collapses
  // when the user starts their own search so the result sidebar gets
  // the right-hand real estate.
  const [teamActivity, setTeamActivity] = useState<{
    clusters: Array<{
      lat: number; lng: number; search_count: number;
      top_terms: { term: string; count: number }[];
      top_vertical: string; latest_ts: number; radius_avg_m: number;
      city?: string | null;
    }>;
    recent: Array<{
      lat: number; lng: number; term: string; radius: number;
      created_at: string; created_ts: number; result_count: number;
      vertical: string;
    }>;
  }>({ clusters: [], recent: [] });
  const [showTeamActivity, setShowTeamActivity] = useState(true);
  const [teamActivityWindow, setTeamActivityWindow] = useState<1 | 7 | 30>(7);
  const [hoveredClusterIdx, setHoveredClusterIdx] = useState<number | null>(null);

  // Load the union of all cached results from a team-activity cluster
  // (or any point inside it). Zero API cost — pure cache read. Used by
  // both the cluster pin click and the activity-entry click.
  const openTeamActivityCluster = useCallback(async (lat: number, lng: number) => {
    setCustomPin({ lat, lng });
    setCustomPinActive(true);
    setSelectedCustomer(null);
    setMapCenter({ lat, lng });
    setMapZoom(11);
    setSearchPanelHidden(true);
    if (mapRef.current) mapRef.current.panTo({ lat, lng });
    setStatusMessage('Loading team-cached results…');
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/prospecting/team-activity/cluster` +
          `?lat=${lat}&lng=${lng}&days=${teamActivityWindow}`,
      );
      const d = await r.json();
      if (d.current_sf_user_id) setCurrentSfUserId(d.current_sf_user_id);
      setSearchResults((d.businesses || []) as Business[]);
      setResultsFromCluster(true);
      setStatusMessage(null);
    } catch (e) {
      setStatusMessage(null);
      console.error('Cluster open failed', e);
    }
  }, [teamActivityWindow]);

  // Auto-refresh every 60s so new searches appear without a page reload.
  useEffect(() => {
    if (!showTeamActivity) return;
    const fetchActivity = () => {
      fetch(`${API_BASE_URL}/api/prospecting/team-activity?days=${teamActivityWindow}`)
        .then(r => r.json())
        .then(d => setTeamActivity({ clusters: d.clusters || [], recent: d.recent || [] }))
        .catch(() => { /* non-fatal */ });
    };
    fetchActivity();
    const interval = setInterval(fetchActivity, 60_000);
    return () => clearInterval(interval);
  }, [showTeamActivity, teamActivityWindow]);

  // Stable per-cluster position objects. OverlayViewF recreates its overlay
  // whenever the `position` prop reference changes; an inline `{lat,lng}` would
  // be a new object every render, so a hover (which re-renders the list) would
  // make every pin briefly setMap(null)→setMap and flicker. Memoizing on the
  // clusters array keeps the references stable between data refreshes.
  const teamClusterPositions = useMemo(
    () => teamActivity.clusters.map(c => ({ lat: c.lat, lng: c.lng })),
    [teamActivity.clusters],
  );

  // Pre-populated SF Account layer — zero-API map pins for accounts already
  // in Salesforce + already geocoded. Saves the user from triggering a paid
  // Places search to discover a business that's already known.
  const [sfAccountsNearby, setSfAccountsNearby] = useState<Array<{
    id: string; name: string; type?: string; website?: string; address?: string;
    lat: number; lng: number;
    owner_id?: string; owner_name?: string; number_of_employees?: number | null;
    linkedin_url?: string | null; zoominfo_id?: string | null; distance_m: number;
  }>>([]);

  // Sandbox mode: when on, the backend serves only cached Google results
  // (no live API spend). Banner + cached-area hints surface to the user.
  const [sandboxMode, setSandboxMode] = useState<boolean>(false);
  const [sandboxHints, setSandboxHints] = useState<Array<{
    lat: number; lng: number; term: string; radius: number | null;
    result_count: number; cached_at: string | null;
  }>>([]);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/prospecting/sandbox-status`)
      .then(r => r.json())
      .then(d => {
        setSandboxMode(!!d.sandbox);
        setSandboxHints(d.hints || []);
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  // Pre-populate the map with SF Accounts whenever the search center moves
  // (customer-select / pin-drop / radius change). Zero Google API cost —
  // backend reuses the existing geocoding_cache.
  useEffect(() => {
    const center = selectedCustomer
      ? { lat: selectedCustomer.latitude, lng: selectedCustomer.longitude }
      : customPin
        ? { lat: customPin.lat, lng: customPin.lng }
        : null;
    if (!center || !center.lat || !center.lng) {
      setSfAccountsNearby([]);
      return;
    }
    const controller = new AbortController();
    fetch(
      `${API_BASE_URL}/api/prospecting/sf-accounts-nearby` +
        `?lat=${center.lat}&lng=${center.lng}&radius=${searchRadius}`,
      { signal: controller.signal },
    )
      .then(r => r.json())
      .then(d => {
        setSfAccountsNearby(d.accounts || []);
        // Pick up the current SF user id BEFORE any search fires, so the
        // pre-populated SF Account layer color-codes 'Open' (yours) vs
        // claimed correctly. Without this every account would show as
        // claimed because the comparison falls back to (owner_id !== null).
        if (d.current_sf_user_id) setCurrentSfUserId(d.current_sf_user_id);
      })
      .catch(() => { /* abort/network is non-fatal */ });
    return () => controller.abort();
  }, [selectedCustomer, customPin, searchRadius]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
    libraries: libraries,
  });

  // Manage heatmap layer
  useEffect(() => {
    if (!mapRef.current) return;

    if (showHeatmap && searchResults.length > 0) {
      // Create or update heatmap (skip coordinate-less results — LatLng throws on null)
      const data = searchResults
        .filter(b => isValidLatLng(b.location))
        .map(b => ({
          location: new google.maps.LatLng(b.location.lat, b.location.lng),
          weight: (b.relevance_score || 50) / 100,
        }));

      if (heatmapLayerRef.current) {
        heatmapLayerRef.current.setData(data);
      } else {
        heatmapLayerRef.current = new google.maps.visualization.HeatmapLayer({
          data,
          map: mapRef.current,
          radius: 30,
          opacity: 0.7,
        });
      }
    } else {
      // Remove heatmap
      if (heatmapLayerRef.current) {
        heatmapLayerRef.current.setMap(null);
        heatmapLayerRef.current = null;
      }
    }
  }, [showHeatmap, searchResults]);

  // Manage news heatmap layer
  useEffect(() => {
    if (!mapRef.current) return;

    const mapNews = filteredNewsArticles.length > 0 ? filteredNewsArticles : newsArticles;
    if (showNewsHeatmap && mapNews.length > 0) {
      const sevWeight: Record<string, number> = { 'BREAKING': 1.0, 'HIGH': 0.7, 'MEDIUM': 0.4 };
      const data = mapNews
        .filter(a => a.location?.lat && a.location?.lng)
        .map(a => ({
          location: new google.maps.LatLng(a.location.lat, a.location.lng),
          weight: sevWeight[a.severity || 'MEDIUM'] || 0.4,
        }));

      if (newsHeatmapRef.current) {
        newsHeatmapRef.current.setData(data);
      } else {
        newsHeatmapRef.current = new google.maps.visualization.HeatmapLayer({
          data,
          map: mapRef.current,
          radius: 40,
          opacity: 0.7,
          gradient: [
            'rgba(0,0,0,0)',
            'rgba(255,159,28,0.4)',
            'rgba(255,107,53,0.6)',
            'rgba(255,59,59,0.8)',
            'rgba(255,0,0,1)',
          ],
        });
      }
    } else {
      if (newsHeatmapRef.current) {
        newsHeatmapRef.current.setMap(null);
        newsHeatmapRef.current = null;
      }
    }
  }, [showNewsHeatmap, newsArticles, filteredNewsArticles]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch news events for map pins — DISABLED (news feature off)
  // useEffect(() => {
  //   const fetchNewsForMap = async () => { ... };
  //   fetchNewsForMap();
  // }, []);

  // Fetch verticals on mount
  useEffect(() => {
    const fetchVerticals = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/prospecting/verticals`);
        if (response.ok) {
          const data = await response.json();
          setVerticals(data.verticals || []);
        }
      } catch (e) {
        console.error('Failed to fetch verticals:', e);
      }
    };
    fetchVerticals();
  }, []);

  // Load saved searches when component mounts
  useEffect(() => {
    const loadSavedSearches = () => {
      const saved = localStorage.getItem('prospectingSavedSearches');
      if (saved) {
        try {
          setSavedSearches(JSON.parse(saved));
        } catch (e) {
          console.error('Error parsing saved searches:', e);
          setSavedSearches([]);
        }
      }
    };
    
    loadSavedSearches();
  }, []);


  // Add this useEffect to load search history
  useEffect(() => {
    const loadSearchHistory = () => {
      const saved = localStorage.getItem('prospectingSearchHistory');
      if (saved) {
        try {
          setSearchHistory(JSON.parse(saved));
        } catch (e) {
          console.error('Error parsing search history:', e);
          setSearchHistory([]);
        }
      }
    };
    
    loadSearchHistory();
  }, []);

  // Clear stale session data on mount — don't auto-restore old results
  useEffect(() => {
    localStorage.removeItem('prospectingLastSession');
  }, []);

  // Auto-save current session whenever results change
  useEffect(() => {
    if (searchResults.length === 0) return;

    const session = {
      searchTerms,
      radius: searchRadius,
      location: {
        lat: mapCenter.lat,
        lng: mapCenter.lng,
        address: selectedCustomer
          ? `${selectedCustomer.address}, ${selectedCustomer.city}, ${selectedCustomer.state} ${selectedCustomer.zip}`
          : undefined
      },
      results: searchResults.map(b => ({
        name: b.name,
        address: b.address,
        phone: b.phone,
        website: b.website,
        rating: b.rating,
        location: b.location,
        services: b.services,
        isEnriched: b.isEnriched,
        apollo_data: b.apollo_data,
        linkedin_url: b.linkedin_url,
        has_linkedin: b.has_linkedin
      })),
      customerId: selectedCustomer?.id
    };

    localStorage.setItem('prospectingLastSession', JSON.stringify(session));
  }, [searchResults]);

  // Update search history entry after enrichment completes
  const updateHistoryWithEnrichment = useCallback(() => {
    if (searchResults.length === 0 || searchHistory.length === 0) return;

    // Find the most recent history entry that matches current results
    const latestEntry = searchHistory[0];
    if (!latestEntry) return;

    const updatedEntry: SearchHistory = {
      ...latestEntry,
      results: {
        totalResults: searchResults.length,
        companies: searchResults.map(business => ({
          name: business.name,
          address: business.address,
          phone: business.phone,
          services: business.services,
          location: business.location,
          website: business.website,
          rating: business.rating,
          isEnriched: business.isEnriched,
          apollo_data: business.apollo_data,
          linkedin_url: business.linkedin_url,
          has_linkedin: business.has_linkedin
        }))
      }
    };

    const updatedHistory = [updatedEntry, ...searchHistory.slice(1)];
    setSearchHistory(updatedHistory);
    localStorage.setItem('prospectingSearchHistory', JSON.stringify(updatedHistory));
  }, [searchResults, searchHistory]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/customers`);
      if (!response.ok) {
        throw new Error('Failed to fetch customer data');
      }
      
      const data = await response.json();
      
      // Collect all addresses that need geocoding
      const addressesToGeocode = data.customers
        .filter((customer: Customer) => 
          customer.stripe?.address_line1 && 
          customer.stripe?.address_city && 
          customer.stripe?.address_state && 
          customer.stripe?.address_postal_code
        )
        .map((customer: Customer) => 
          `${customer.stripe.address_line1}, ${customer.stripe.address_city}, ${customer.stripe.address_state} ${customer.stripe.address_postal_code}`
        );

      // Get coordinates for all addresses in one request
      const geocodeResponse = await fetch(`${API_BASE_URL}/api/geocode/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ addresses: addressesToGeocode }),
      });

      if (!geocodeResponse.ok) {
        throw new Error('Failed to geocode addresses');
      }

      const geocodeData = await geocodeResponse.json();
      const addressCoordinates = geocodeData.results;

      // Map customers with their coordinates
      const customersWithCoords = data.customers
        .map((customer: Customer) => {
          if (customer.stripe?.address_line1 && 
              customer.stripe?.address_city && 
              customer.stripe?.address_state && 
              customer.stripe?.address_postal_code) {
            
            const fullAddress = `${customer.stripe.address_line1}, ${customer.stripe.address_city}, ${customer.stripe.address_state} ${customer.stripe.address_postal_code}`;
            const coords = addressCoordinates[fullAddress];
            
            if (coords) {
              return {
                id: customer.customer_tag,
                name: customer.stripe.name,
                address: customer.stripe.address_line1,
                city: customer.stripe.address_city,
                state: customer.stripe.address_state,
                zip: customer.stripe.address_postal_code,
                latitude: coords.lat,
                longitude: coords.lng,
                description: customer.description || 'No Description',
                account_executive: customer.account_executive 
                  ? customer.account_executive.split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ')
                  : 'Not Assigned',
              };
            }
          }
          return null;
        })
        .filter((c: unknown): c is MappedCustomer => c !== null);

      setCustomers(customersWithCoords);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Failed to fetch customers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fix bug #3: Ensure saveToSearchHistory updates both state and localStorage, and triggers UI update
  // Replace the saveToSearchHistory function with a version that always updates state and localStorage
  const saveToSearchHistory = () => {
    if (!searchResults.length) return;

    // Create new search history entry
    const newSearchEntry: SearchHistory = {
      id: Date.now().toString(),
      searchDate: new Date().toISOString(),
      searchTerms: [...searchTerms],
      radius: searchRadius,
      location: {
        lat: mapCenter.lat,
        lng: mapCenter.lng,
        address: selectedCustomer 
          ? `${selectedCustomer.address}, ${selectedCustomer.city}, ${selectedCustomer.state} ${selectedCustomer.zip}`
          : undefined
      },
      results: {
        totalResults: searchResults.length,
        companies: searchResults.map(business => ({
          name: business.name,
          address: business.address,
          phone: business.phone,
          services: business.services,
          location: business.location,
          website: business.website,
          rating: business.rating,
          isEnriched: business.isEnriched,
          apollo_data: business.apollo_data,
          linkedin_url: business.linkedin_url,
          has_linkedin: business.has_linkedin
        }))
      }
    };

    // Add to search history
    setSearchHistory(prevHistory => {
      const updatedHistory = [newSearchEntry, ...prevHistory];
      localStorage.setItem('prospectingSearchHistory', JSON.stringify(updatedHistory));
      return updatedHistory;
    });
  };

  // Add this function near other API calls
  const enrichWithApollo = async (businesses: Business[]): Promise<Business[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/enrich-apollo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companies: businesses }),
      });

      if (!response.ok) {
        throw new Error('Failed to enrich with Apollo');
      }

      const data = await response.json();
      return data.companies;
    } catch (error) {
      console.error('Error enriching with Apollo:', error);
      return businesses;
    }
  };

  // One-click enrich for a single Lead Finder card (firmographics via Waterfall).
  // Patches the card in place by name+address so employee count / industry show
  // and the result re-sorts under the 'priority' ordering.
  const enrichSingleBusiness = async (business: Business) => {
    const matches = (b: Business) => b.name === business.name && b.address === business.address;
    setSearchResults(prev => prev.map(b =>
      matches(b) ? { ...b, isEnriching: true, enrichmentError: undefined } : b));
    try {
      const [enriched] = await enrichWithApollo([business]);
      // Build the post-enrichment shape so we can both update state AND hand
      // the same object to the Add-to-Salesforce modal in one go.
      const merged: Business = {
        ...business,
        isEnriching: false,
        isEnriched: true,
        apollo_data: enriched?.apollo_data ?? business.apollo_data,
        linkedin_url: business.linkedin_url || enriched?.linkedin_url,
        has_linkedin: business.has_linkedin || !!enriched?.linkedin_url,
      };
      setSearchResults(prev => prev.map(b => matches(b) ? merged : b));
      // Auto-open the Add-to-Salesforce modal with the freshly enriched data.
      // Clicking Enrich is now a two-step shortcut: enrich + immediately stage
      // the SF add (rep still confirms in the modal). Saves the second click.
      setAddSfBusiness(merged);
    } catch (err) {
      setSearchResults(prev => prev.map(b => matches(b)
        ? { ...b, isEnriching: false, enrichmentError: err instanceof Error ? err.message : 'enrich failed' }
        : b));
    }
  };

  // Bulk-enrich-then-add: take every row currently checked in the result list,
  // fire Waterfall on the batch, patch the cards in place with isEnriched +
  // apollo_data, then open the bulk Add-to-Salesforce modal so each row's
  // form is prefilled with the firmographics. Locks the action bar via
  // bulkEnriching while the batch is in flight.
  const enrichAndBulkAdd = async () => {
    if (bulkEnriching || bulkSelectedKeys.size === 0) return;
    setBulkEnriching(true);
    // Snapshot the selected Business refs by stable key — searchResults can
    // be rebuilt by background SF checks during the multi-second enrich call,
    // so we match by name|address rather than object identity downstream.
    const keys = new Set(bulkSelectedKeys);
    const selected = searchResults.filter(b => keys.has(`${b.name}|${b.address}`));
    if (selected.length === 0) { setBulkEnriching(false); return; }

    // Optimistic UI: flip each row to isEnriching so the card spinner shows.
    setSearchResults(prev => prev.map(b =>
      keys.has(`${b.name}|${b.address}`)
        ? { ...b, isEnriching: true, enrichmentError: undefined }
        : b));
    try {
      const enriched = await enrichWithApollo(selected);
      // Build a lookup by key so we can apply the right enriched record to
      // each row, regardless of result order from the batch endpoint.
      const byKey = new Map<string, any>();
      for (let i = 0; i < enriched.length; i++) {
        const src = selected[i];                       // input order is preserved
        byKey.set(`${src.name}|${src.address}`, enriched[i]);
      }
      setSearchResults(prev => prev.map(b => {
        const e = byKey.get(`${b.name}|${b.address}`);
        if (!e) return b;
        return {
          ...b,
          isEnriching: false,
          isEnriched: true,
          apollo_data: e?.apollo_data ?? b.apollo_data,
          linkedin_url: b.linkedin_url || e?.linkedin_url,
          has_linkedin: b.has_linkedin || !!e?.linkedin_url,
        };
      }));
      // Open the bulk add modal — it'll re-resolve the selected set from
      // searchResults (where the enrichment now lives) and prefill each
      // row's form with the firmographics.
      setBulkAddOpen(true);
    } catch (err) {
      console.error('Bulk enrich failed:', err);
      setSearchResults(prev => prev.map(b =>
        keys.has(`${b.name}|${b.address}`)
          ? { ...b, isEnriching: false,
              enrichmentError: err instanceof Error ? err.message : 'enrich failed' }
          : b));
    } finally {
      setBulkEnriching(false);
    }
  };

  // Handle vertical search with SSE for incremental results
  const handleVerticalSearch = async () => {
    if (!selectedCustomer && !customPin) return;
    // Collapse the team-activity overlay when a search starts so it doesn't
    // compete with the user's own results.
    setShowTeamActivity(false);

    // Append mode: if we already have results, keep them and dedupe new
    // hits against (name+address). Lets the user move the circle, re-search,
    // and accumulate a wider prospect list across multiple areas.
    const appendMode = searchResults.length > 0;

    try {
      setIsSearching(true);
      setError(null);
      setStatusMessage(appendMode ? 'Adding more results…' : 'Searching nearby businesses...');
      if (!appendMode) {
        setSearchResults([]);
      }
      // Fresh live search — no longer a cluster-loaded view.
      setResultsFromCluster(false);
      setSearchTermsUsed([]);
      setSearchingTerm(null);
      setSearchProgress(null);

      const location = selectedCustomer
        ? { lat: selectedCustomer.latitude!, lng: selectedCustomer.longitude! }
        : customPin!;

      const response = await fetch(`${API_BASE_URL}/api/prospecting/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vertical: selectedVertical,
          lat: location.lat,
          lng: location.lng,
          radius: searchRadius,
          additional_terms: searchTerms,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Vertical search failed');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Streaming not supported');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'terms') {
              setSearchTermsUsed(event.terms);
              setStatusMessage(`Searching ${event.terms.length} categories...`);
            } else if (event.type === 'results') {
              setSearchingTerm(event.term);
              setSearchProgress({ current: event.term_index + 1, total: event.total_terms });
              setStatusMessage(`Searching: ${event.term} (${event.term_index + 1}/${event.total_terms})`);
              // Backend now pre-tags SF status + matches in the SSE event, so
              // the per-batch /api/prospecting/salesforce/check-websites call is
              // no longer needed. Pick up the SF user id when it comes through.
              if (event.current_sf_user_id && event.current_sf_user_id !== currentSfUserId) {
                setCurrentSfUserId(event.current_sf_user_id);
              }

              if (event.businesses && event.businesses.length > 0) {
                const center = event.center;
                const newBiz = event.businesses.map((business: ApiBusinessResponse & { source?: string; relevance_score?: number; is_existing_customer?: boolean; matched_customer_name?: string; salesforce_status?: Business['salesforce_status']; salesforce_matches?: SalesforceAccountMatch[] }): Business => ({
                  name: business.name,
                  address: business.address,
                  phone: business.phone,
                  website: business.website,
                  rating: business.rating,
                  user_ratings_total: business.user_ratings_total,
                  business_status: (business as any).business_status,
                  last_review_age_days: (business as any).last_review_age_days,
                  // {lat:null,lng:null} is truthy, so a plain `|| center` won't
                  // catch a coordinate-less Places result — validate explicitly.
                  location: isValidLatLng(business.location) ? business.location : center,
                  services: business.services,
                  isEnriched: business.isEnriched,
                  apollo_data: business.apollo_data,
                  linkedin_url: business.linkedin_url,
                  has_linkedin: !!business.linkedin_url,
                  relevance_score: business.relevance_score,
                  is_existing_customer: (business as any).is_existing_customer,
                  matched_customer_name: (business as any).matched_customer_name,
                  matched_customer_tag: (business as any).matched_customer_tag,
                  // Backend pre-tags SF status + matches; preserve them so the
                  // useEffect that fires checkSalesforceForBusinesses sees a
                  // populated salesforce_status and skips the round-trip.
                  salesforce_status: (business as any).salesforce_status,
                  salesforce_matches: (business as any).salesforce_matches,
                }));
                // Dedupe by (name|address) — incremental SSE chunks and the
                // append-after-circle-move pattern can otherwise double-insert
                // the same Google Places hit.
                setSearchResults(prev => {
                  const seen = new Set(prev.map(b => `${b.name}|${b.address}`));
                  const fresh = newBiz.filter((b: Business) => !seen.has(`${b.name}|${b.address}`));
                  return fresh.length === 0 ? prev : [...prev, ...fresh];
                });
                if (center) setMapCenter(center);
                // SF match check fires automatically via useEffect on searchResults
              }
            } else if (event.type === 'scoring') {
              setStatusMessage('Scoring relevance...');
            } else if (event.type === 'scores') {
              // Update relevance scores on existing results
              const scoresMap = new Map<string, number>();
              for (const s of event.scores) {
                scoresMap.set(`${s.name}|${s.address}`, s.relevance_score);
              }
              setSearchResults(prev => prev.map(b => {
                const key = `${b.name}|${b.address}`;
                const score = scoresMap.get(key);
                return score !== undefined ? { ...b, relevance_score: score } : b;
              }));
              setStatusMessage(null);
            } else if (event.type === 'done') {
              setSearchingTerm(null);
              setSearchProgress(null);
              setStatusMessage(null);
            }
          } catch {
            // skip malformed SSE lines
          }
        }
      }

      saveToSearchHistory();

      // Auto-collapse the search panel so the results sidebar gets the focus.
      // Use a functional setter so we don't fight an in-flight clear/reopen.
      setSearchResults(prev => { if (prev.length > 0) setSearchPanelHidden(true); return prev; });

      // Auto-trigger enrichment for results with websites
      triggerAutoEnrich();
    } catch (error) {
      console.error('Error in vertical search:', error);
      setError(error instanceof Error ? error.message : 'Vertical search failed');
    } finally {
      setIsSearching(false);
    }
  };

  // Salesforce lookup: bulk-check whether the websites in `newBiz` map to existing
  // SF Accounts, then merge {salesforce_status, salesforce_matches} into state.
  // Idempotent — safe to call repeatedly on the same set; results overwrite prior.
  // Cards with no website resolve immediately to status='no_website' without an
  // API call (the backend would skip them anyway).
  const checkSalesforceForBusinesses = async (newBiz: Business[]) => {
    if (!newBiz || newBiz.length === 0) return;
    // Mark every incoming business as 'checking' or 'no_website' up front so the
    // badge appears instantly, before the network call resolves.
    setSearchResults(prev => prev.map(b => {
      if (!newBiz.includes(b) && !newBiz.some(nb => nb.name === b.name && nb.address === b.address)) {
        return b;
      }
      return { ...b, salesforce_status: b.website ? 'checking' : 'no_website' };
    }));

    const websites = Array.from(new Set(newBiz.map(b => b.website).filter((w): w is string => !!w)));
    if (websites.length === 0) return;

    // Apply a single chunk of matches to searchResults. Called per SSE
    // `matches` event so cards flip from "checking" → resolved state as soon
    // as each ~50-domain SOQL batch returns, rather than waiting for the
    // whole call.
    const applyChunk = (chunkMatches: Record<string, SalesforceAccountMatch[]>, chunkBlocked: string[]) => {
      const chunkBlockedSet = new Set<string>(chunkBlocked);
      // Belt-and-suspenders: also catch DQ/DNC via the type field on each hit.
      for (const [domain, hits] of Object.entries(chunkMatches)) {
        if (hits.some(h => SF_BLOCKED_TYPES.has((h.type || '').toLowerCase()))) {
          chunkBlockedSet.add(domain);
        }
      }
      setSearchResults(prev => prev.flatMap(b => {
        if (!b.website || b.salesforce_status === 'no_website') return [b];
        const domain = normalizeDomainClient(b.website);
        if (!domain) return [{ ...b, salesforce_status: 'no_website' as const }];
        // Only touch rows whose domain appears in this chunk; leave others
        // alone (other chunks will handle them).
        if (!(domain in chunkMatches) && !chunkBlockedSet.has(domain)) return [b];
        if (chunkBlockedSet.has(domain)) return [];  // drop disqualified / DNC
        const hits = chunkMatches[domain] || [];
        return [{
          ...b,
          salesforce_status: (hits.length > 0 ? 'in_sf' : 'not_in_sf') as Business['salesforce_status'],
          salesforce_matches: hits,
          salesforce_type_refreshed: true,
        }];
      }));
    };

    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/salesforce/check-websites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ websites }),
      });
      if (!resp.ok) {
        // 503 = not authenticated; quietly mark as 'error' so the UI shows neutral state
        setSearchResults(prev => prev.map(b =>
          b.salesforce_status === 'checking' ? { ...b, salesforce_status: 'error', salesforce_type_refreshed: true } : b
        ));
        return;
      }

      // Consume the SSE stream. Each `matches` event flips a slice of the
      // result list immediately; the final `done` event triggers cleanup of
      // any still-checking rows that no chunk touched (their domain didn't
      // normalize, or SF returned nothing for them).
      const reader = resp.body?.getReader();
      if (!reader) throw new Error('No SSE reader');
      const decoder = new TextDecoder();
      let buffer = '';
      let streamFinished = false;
      while (!streamFinished) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt: any;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'init') {
            if (evt.current_user_id && evt.current_user_id !== currentSfUserId) {
              setCurrentSfUserId(evt.current_user_id);
            }
          } else if (evt.type === 'matches') {
            applyChunk(evt.matches || {}, evt.blocked || []);
          } else if (evt.type === 'error') {
            console.warn('SF check-websites stream error:', evt.message);
            setSearchResults(prev => prev.map(b =>
              b.salesforce_status === 'checking' ? { ...b, salesforce_status: 'error', salesforce_type_refreshed: true } : b
            ));
            streamFinished = true;
          } else if (evt.type === 'done') {
            // Any row still 'checking' after the stream completes had a
            // website that didn't normalize on the backend (or the stream
            // ended early). Flip those to 'not_in_sf' so the UI doesn't hang
            // on a spinner.
            setSearchResults(prev => prev.map(b =>
              b.salesforce_status === 'checking'
                ? { ...b, salesforce_status: 'not_in_sf', salesforce_matches: [], salesforce_type_refreshed: true }
                : b
            ));
            streamFinished = true;
          }
        }
      }
    } catch (err) {
      console.warn('SF check-websites stream failed:', err);
      setSearchResults(prev => prev.map(b =>
        b.salesforce_status === 'checking' ? { ...b, salesforce_status: 'error', salesforce_type_refreshed: true } : b
      ));
    }
  };

  // Best-effort LinkedIn discovery: scrape each new result's homepage for a
  // LinkedIn company URL and patch it onto the card. Fire-and-forget — a miss
  // (bot block / no link) just leaves the card without a badge. The ref guard
  // ensures each website is only attempted once per session even though the
  // driving useEffect re-fires as SF-check chunks resolve.
  const linkedinAttemptedRef = useRef<Set<string>>(new Set());
  const extractLinkedInForBusinesses = async (newBiz: Business[]) => {
    // Scrape every not-yet-attempted website once — one fetch yields both the
    // LinkedIn and Yelp links. Guarded by the ref so SF-check re-fires don't
    // re-scrape. (No longer gated on missing linkedin_url, since we also want
    // Yelp even when LinkedIn is already known.)
    const websites = Array.from(new Set(
      newBiz
        .filter(b => b.website && !linkedinAttemptedRef.current.has(b.website))
        .map(b => b.website as string),
    ));
    if (websites.length === 0) return;
    websites.forEach(w => linkedinAttemptedRef.current.add(w));
    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/extract-linkedin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ websites }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const results: Record<string, string> = data.results || {};   // linkedin
      const yelp: Record<string, string> = data.yelp || {};
      const ig: Record<string, string> = data.instagram || {};
      const fb: Record<string, string> = data.facebook || {};
      const xm: Record<string, string> = data.x || {};
      const email: Record<string, string> = data.email || {};
      const addr: Record<string, string> = data.address || {};
      const addrGeo: Record<string, { lat: number; lng: number }> = data.address_geo || {};
      if (![results, yelp, ig, fb, xm, email, addr, addrGeo].some(m => Object.keys(m).length)) return;
      // Patch cards with the scraped LinkedIn + socials + email + address. The
      // SF LinkedIn backfill is handled by a dedicated effect (below) so it
      // also covers LinkedIns we already knew from search.
      const recenteredKeys: string[] = [];
      setSearchResults(prev => prev.map(b => {
        if (!b.website) return b;
        const w = b.website;
        let next = b;
        if (results[w] && !b.linkedin_url) next = { ...next, linkedin_url: results[w], has_linkedin: true };
        if (yelp[w] && !b.yelp_url) next = { ...next, yelp_url: yelp[w] };
        if (ig[w] && !b.instagram_url) next = { ...next, instagram_url: ig[w] };
        if (fb[w] && !b.facebook_url) next = { ...next, facebook_url: fb[w] };
        if (xm[w] && !b.x_url) next = { ...next, x_url: xm[w] };
        if (email[w] && !b.company_email) next = { ...next, company_email: email[w] };
        // Surface the scraped footer address as a separate field so the card
        // can show it without overwriting the Google Places `address` we used
        // for the search center. When SF is empty for this account, the add
        // flow uses this value to populate Billing fields automatically.
        if (addr[w] && !b.extracted_address) next = { ...next, extracted_address: addr[w] };
        // Recenter: if the scrape gave us coordinates AND the card's current
        // location is invalid (null, NaN, or null-island ~0,0), trust the
        // scraped address. Avoid yanking pins that already plotted correctly
        // from a Google Places match.
        const geo = addrGeo[w];
        if (geo && typeof geo.lat === 'number' && typeof geo.lng === 'number') {
          const cur = b.location;
          const curBad = !cur || !isValidLatLng(cur) ||
            (Math.abs(cur.lat) < 0.5 && Math.abs(cur.lng) < 0.5);
          if (curBad) {
            next = { ...next, location: { lat: geo.lat, lng: geo.lng } };
            recenteredKeys.push(`${b.name}|${b.address}`);
            // Move the existing marker, if one was already plotted, and pan
            // the map to the corrected position so the rep sees the move.
            if (b.marker) {
              try { b.marker.setPosition({ lat: geo.lat, lng: geo.lng }); } catch {}
            }
          }
        }
        return next;
      }));
      // Pan to the (single, or last) recentered card so the rep sees the fix
      // immediately. Skipped when there's nothing to recenter to avoid
      // hijacking the rep's current map view.
      if (recenteredKeys.length && mapRef.current) {
        // Find the geo for the last key we touched and pan there.
        const lastKey = recenteredKeys[recenteredKeys.length - 1];
        const lastBiz = searchResults.find(b => `${b.name}|${b.address}` === lastKey);
        const w = lastBiz?.website;
        const geo = w ? addrGeo[w] : null;
        if (geo) {
          try { mapRef.current.panTo(geo); } catch {}
        }
      }
    } catch (err) {
      console.warn('LinkedIn/Yelp extract failed:', err);
    }
  };

  // Auto-backfill Company_LinkedIn__c on existing SF accounts. Fires for ANY
  // in-SF row where we have a LinkedIn (from Apollo, search, or the homepage
  // scrape) but the matched account has none on file. Deduped by account id via
  // a ref; the backend re-checks current value and skips if already set, so a
  // re-fire can never clobber a LinkedIn entered elsewhere.
  const linkedinBackfillRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const targets: Array<{ id: string; linkedin: string }> = [];
    for (const b of searchResults) {
      if (b.salesforce_status !== 'in_sf' || !b.linkedin_url) continue;
      const match = b.salesforce_matches?.[0];
      if (!match || !match.id || match.id === 'pending') continue;
      if (match.linkedin_url) continue;            // SF already has one
      if (linkedinBackfillRef.current.has(match.id)) continue;
      targets.push({ id: match.id, linkedin: b.linkedin_url });
    }
    if (targets.length === 0) return;
    targets.forEach(({ id, linkedin }) => {
      linkedinBackfillRef.current.add(id);
      fetch(`${API_BASE_URL}/api/prospecting/salesforce/set-linkedin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, linkedin }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(res => {
          if (res?.updated) {
            setSearchResults(prev => prev.map(b => {
              if (b.salesforce_matches?.[0]?.id !== id) return b;
              return {
                ...b,
                salesforce_matches: (b.salesforce_matches || []).map((m, i) =>
                  i === 0 ? { ...m, linkedin_url: linkedin } : m,
                ),
              };
            }));
          }
        })
        .catch(() => { /* best-effort backfill */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Background website-liveness check: flags dead/parked homepages so the card
  // can offer a one-click "DQ (dead site)" action. Conservative detector
  // (timeouts/SSL/5xx count as alive). Ref-guarded so each site is checked once.
  const websiteStatusRef = useRef<Set<string>>(new Set());
  const checkWebsiteStatus = async (newBiz: Business[]) => {
    const websites = Array.from(new Set(
      newBiz
        .filter(b => b.website && !websiteStatusRef.current.has(b.website))
        .map(b => b.website as string),
    ));
    if (websites.length === 0) return;
    websites.forEach(w => websiteStatusRef.current.add(w));
    const batch = new Set(websites);
    // Mark every row in this batch as checked (clears the "Analyzing…" state),
    // and apply dead/redirect flags. Called on success AND failure so a row
    // never gets stuck analyzing.
    const applyChecked = (dead: Record<string, { phrase: string }>,
                          redirect: Record<string, { to_domain: string }>) => {
      setSearchResults(prev => prev.map(b => {
        if (!b.website || !batch.has(b.website)) return b;
        const d = dead[b.website];
        const r = redirect[b.website];
        let next: Business = { ...b, website_checked: true };
        if (d) next = { ...next, website_dead: true, website_dead_phrase: d.phrase };
        else if (r) next = { ...next, website_redirect_to: r.to_domain };
        return next;
      }));
    };
    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/website-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ websites }),
      });
      if (!resp.ok) { applyChecked({}, {}); return; }
      const data = await resp.json();
      applyChecked(data.dead || {}, data.redirect || {});
    } catch (err) {
      console.warn('website-status check failed:', err);
      applyChecked({}, {});
    }
  };

  // One-click "DQ (dead site)": create the Account as Disqualified with a
  // website-defunct reason note, then drop the card (DQ'd accounts are hidden).
  const handleDqDeadSite = (business: Business) => {
    const date = new Date().toISOString().slice(0, 10);
    const phrase = business.website_dead_phrase || 'website unreachable';
    const note = `[${date}] Auto-disqualified — website defunct: ${phrase} (${business.website || ''}). [Summit]`;
    const targetKey = `${business.name}|${business.address}`;
    // Optimistically remove — a Disqualified account drops out of results.
    setSearchResults(prev => prev.filter(b => `${b.name}|${b.address}` !== targetKey));

    const payload: AddToSalesforcePayload & { address?: string } = {
      name: business.name.trim(),
      website: business.website ? normalizeBaseUrl(business.website) : undefined,
      phone: business.phone,
      type: 'Disqualified',
      ae_notes: note,
      company_linkedin: business.linkedin_url || undefined,
      company_instagram: business.instagram_url || undefined,
      company_facebook: business.facebook_url || undefined,
      company_x: business.x_url || undefined,
      company_yelp: business.yelp_url || undefined,
      company_email: business.company_email || undefined,
      address: business.address,
      billing_latitude: business.location?.lat,
      billing_longitude: business.location?.lng,
    };

    const requestPromise = fetch(`${API_BASE_URL}/api/prospecting/salesforce/create-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(async (resp) => {
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `Salesforce create failed (${resp.status})`);
      return data as AddToSalesforceResult;
    });

    toast.promise(requestPromise, {
      loading: `Disqualifying ${business.name} (dead site)…`,
      success: (result) => sfToastWithLink('Disqualified — website defunct', result.url),
      error: (err) => {
        // Roll back: re-add the card so the rep can retry.
        setSearchResults(prev =>
          prev.some(b => `${b.name}|${b.address}` === targetKey) ? prev : [...prev, business],
        );
        return `Failed to DQ ${business.name}: ${err instanceof Error ? err.message : 'unknown error'}`;
      },
    }, { success: { duration: 8000 } });
  };

  // Inline disposition (Account.Type) edit from the result card. Replaces the
  // old EditSalesforceModal flow — same optimistic semantics:
  //   • DQ / DNC → drop the row from results (mirror of SF_BLOCKED_TYPES).
  //   • Anything else → update the match in place so the pill recolors.
  // Rolls back on API failure. Disabled while the SF id is 'pending'.
  const handleInlineDispositionChange = (
    business: Business,
    match: SalesforceAccountMatch,
    nextType: SfAccountType,
  ) => {
    const matchId = match.id;
    if (!matchId || matchId === 'pending') return;
    const previousType = match.type;
    if (nextType === previousType) return;  // no-op

    const blocked = SF_BLOCKED_TYPES.has(nextType.toLowerCase());
    const hasMatchId = (b: Business) =>
      (b.salesforce_matches || []).some(m => m.id === matchId);

    // Snapshot the row so we can restore it on failure even after removal.
    const blockedSnapshot: Business | undefined = blocked
      ? (searchResults.find(hasMatchId) || business)
      : undefined;

    if (blocked) {
      setSearchResults(prev => prev.filter(b => !hasMatchId(b)));
    } else {
      setSearchResults(prev => prev.map(b => {
        if (!hasMatchId(b)) return b;
        return {
          ...b,
          salesforce_matches: (b.salesforce_matches || []).map(m =>
            m.id === matchId ? { ...m, type: nextType } : m,
          ),
        };
      }));
    }

    const requestPromise = fetch(`${API_BASE_URL}/api/prospecting/salesforce/update-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: matchId, type: nextType }),
    }).then(async (resp) => {
      const payload = await resp.json();
      if (!resp.ok) throw new Error(payload.error || `Salesforce update failed (${resp.status})`);
      return payload;
    });

    toast.promise(requestPromise, {
      loading: `Updating ${business.name} to ${nextType}…`,
      success: () => blocked ? `Set to ${nextType} — hidden` : `Updated to ${nextType}`,
      error: (err) => {
        if (blocked && blockedSnapshot) {
          setSearchResults(prev =>
            prev.some(hasMatchId) ? prev : [...prev, blockedSnapshot],
          );
        } else if (!blocked) {
          setSearchResults(prev => prev.map(b => {
            if (!hasMatchId(b)) return b;
            return {
              ...b,
              salesforce_matches: (b.salesforce_matches || []).map(m =>
                m.id === matchId ? { ...m, type: previousType } : m,
              ),
            };
          }));
        }
        return `Failed to update ${business.name}: ${err instanceof Error ? err.message : 'unknown error'}`;
      },
    });
  };

  // Inline Tier edit from the result card. Unlike disposition (which can hide
  // the row or change the marker color), Tier is a quick "1/2/3 or none"
  // toggle, so we surface it as a <select> on the card itself rather than
  // making the rep open the disposition modal. Optimistic update + rollback.
  const handleInlineTierChange = (
    business: Business,
    match: SalesforceAccountMatch,
    rawNext: string,
  ) => {
    const matchId = match.id;
    if (!matchId || matchId === 'pending') return;
    const previousTier = match.tier ?? '';
    const next = rawNext.trim();                 // '' | '1' | '2' | '3'
    if (next === (previousTier || '')) return;   // no-op

    const hasMatchId = (b: Business) =>
      (b.salesforce_matches || []).some(m => m.id === matchId);

    // Optimistic: '' means "clear the field" — store as null on the row.
    setSearchResults(prev => prev.map(b => {
      if (!hasMatchId(b)) return b;
      return {
        ...b,
        salesforce_matches: (b.salesforce_matches || []).map(m =>
          m.id === matchId ? { ...m, tier: next === '' ? null : next } : m,
        ),
      };
    }));

    const requestPromise = fetch(`${API_BASE_URL}/api/prospecting/salesforce/update-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: matchId, tier: next === '' ? null : next }),
    }).then(async (resp) => {
      const payload = await resp.json();
      if (!resp.ok) throw new Error(payload.error || `Salesforce update failed (${resp.status})`);
      return payload;
    });

    toast.promise(requestPromise, {
      loading: `Updating ${business.name} tier…`,
      success: () => next === '' ? `Tier cleared` : `Set to Tier ${next}`,
      error: (err) => {
        // Roll back to the captured prior value (store null when prior was unset).
        setSearchResults(prev => prev.map(b => {
          if (!hasMatchId(b)) return b;
          return {
            ...b,
            salesforce_matches: (b.salesforce_matches || []).map(m =>
              m.id === matchId ? { ...m, tier: previousTier === '' ? null : previousTier } : m,
            ),
          };
        }));
        return `Failed to update ${business.name}: ${err instanceof Error ? err.message : 'unknown error'}`;
      },
    });
  };

  // Auto-backfill Account.Phone on existing SF accounts. Mirrors the LinkedIn
  // backfill: fires for any in-SF row where we have a phone (from Google Places)
  // but the matched account has no mainline. Deduped by id; backend re-checks
  // and skips if a phone is already set.
  const phoneBackfillRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const targets: Array<{ id: string; phone: string }> = [];
    for (const b of searchResults) {
      if (b.salesforce_status !== 'in_sf' || !b.phone) continue;
      const match = b.salesforce_matches?.[0];
      if (!match || !match.id || match.id === 'pending') continue;
      if (match.phone && String(match.phone).trim()) continue;  // SF already has one
      if (phoneBackfillRef.current.has(match.id)) continue;
      targets.push({ id: match.id, phone: b.phone });
    }
    if (targets.length === 0) return;
    targets.forEach(({ id, phone }) => {
      phoneBackfillRef.current.add(id);
      fetch(`${API_BASE_URL}/api/prospecting/salesforce/set-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, phone }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(res => {
          if (res?.updated) {
            setSearchResults(prev => prev.map(b => {
              if (b.salesforce_matches?.[0]?.id !== id) return b;
              return {
                ...b,
                salesforce_matches: (b.salesforce_matches || []).map((m, i) =>
                  i === 0 ? { ...m, phone } : m,
                ),
              };
            }));
          }
        })
        .catch(() => { /* best-effort backfill */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Auto-upsert scraped socials (Instagram/Facebook/X/Yelp/email) onto existing
  // SF accounts that are missing them. Mirrors the LinkedIn/phone backfill; the
  // backend re-checks and writes only empty fields. Deduped per (account, field-set).
  const socialsBackfillRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const SOCIALS: Array<[keyof Business, keyof SalesforceAccountMatch, string]> = [
      ['instagram_url', 'instagram_url', 'company_instagram'],
      ['facebook_url', 'facebook_url', 'company_facebook'],
      ['x_url', 'x_url', 'company_x'],
      ['yelp_url', 'yelp_url', 'company_yelp'],
      ['company_email', 'company_email', 'company_email'],
    ];
    const sfFieldToMatchKey: Record<string, keyof SalesforceAccountMatch> = {
      Company_Instagram__c: 'instagram_url',
      Company_Facebook__c: 'facebook_url',
      Company_X__c: 'x_url',
      Company_Yelp__c: 'yelp_url',
      Company_Email__c: 'company_email',
    };
    const targets: Array<{ id: string; socials: Record<string, string> }> = [];
    for (const b of searchResults) {
      if (b.salesforce_status !== 'in_sf') continue;
      const match = b.salesforce_matches?.[0];
      if (!match || !match.id || match.id === 'pending') continue;
      const missing: Record<string, string> = {};
      for (const [bizKey, matchKey, payloadKey] of SOCIALS) {
        const val = b[bizKey] as string | undefined;
        const existing = match[matchKey] as string | null | undefined;
        if (val && !(existing && String(existing).trim())) missing[payloadKey] = val;
      }
      if (Object.keys(missing).length === 0) continue;
      const sig = `${match.id}:${Object.keys(missing).sort().join(',')}`;
      if (socialsBackfillRef.current.has(sig)) continue;
      socialsBackfillRef.current.add(sig);
      targets.push({ id: match.id, socials: missing });
    }
    if (targets.length === 0) return;
    targets.forEach(({ id, socials }) => {
      fetch(`${API_BASE_URL}/api/prospecting/salesforce/set-socials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, socials }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(res => {
          const written: string[] = res?.updated || [];
          if (written.length === 0) return;
          // Patch the match's social fields so the card reflects them.
          setSearchResults(prev => prev.map(b => {
            if (b.salesforce_matches?.[0]?.id !== id) return b;
            const m: SalesforceAccountMatch = { ...(b.salesforce_matches[0]) };
            for (const sfField of written) {
              const mk = sfFieldToMatchKey[sfField];
              const payloadKey = SOCIALS.find(([, mkey]) => mkey === mk)?.[2];
              if (mk && payloadKey && socials[payloadKey]) {
                (m as unknown as Record<string, unknown>)[mk] = socials[payloadKey];
              }
            }
            return { ...b, salesforce_matches: [m, ...b.salesforce_matches!.slice(1)] };
          }));
        })
        .catch(() => { /* best-effort backfill */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Refresh SF state for every newly-arrived row via a live SOQL call. This is
  // the "read side" of the write-through cache contract:
  //   - Local edits via our Edit modal already patch `_ACCOUNT_INDEX` in-place
  //     (see salesforce_client.update_account_type) → next search sees the new
  //     value from cache.
  //   - External edits (a rep changing Type directly in SF) bypass our cache.
  //     The in-memory index could be up to ~1h stale, so we always re-validate
  //     each result against a live `find_accounts_by_websites` SOQL on first
  //     display. One bulk call per search batch — cheap, and means external
  //     edits show up within ~1s of a new search instead of waiting for the
  //     warmer's next refresh cycle.
  // `salesforce_type_refreshed` is the once-per-row flag that prevents the
  // useEffect from re-firing on every render. EVERY terminal path in
  // checkSalesforceForBusinesses must set it, failures included: this effect
  // re-queues any row without it, and each attempt calls setSearchResults
  // (to paint 'checking'), which re-runs the effect. A path that ends without
  // the flag is therefore an unthrottled fetch loop, not a one-off miss —
  // a 503 from an unauthenticated SF session spun it at ~1k req/s.
  useEffect(() => {
    const pending = searchResults.filter(b => {
      if (b.salesforce_status === undefined) return true;
      if (b.salesforce_status === 'no_website') return false;       // no domain to look up
      return !b.salesforce_type_refreshed;
    });
    if (pending.length === 0) return;
    checkSalesforceForBusinesses(pending);
    extractLinkedInForBusinesses(pending);
    // Website-status ("analyzing") runs in a separate effect, gated on
    // not-in-SF — accounts already in Salesforce are assumed enriched, and the
    // dead/DQ action only applies to the 'add' bucket anyway.
    // checkSalesforceForBusinesses is stable for this effect's needs; intentionally
    // not in deps to avoid the loop you'd get from setSearchResults inside it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Website-status "analyzing" — ONLY for rows confirmed not-in-SF. Accounts
  // already in Salesforce are assumed to have correct info, and the dead/DQ
  // action only applies to the 'add' bucket. Runs once SF status resolves to
  // 'not_in_sf'; ref-guarded inside checkWebsiteStatus so each site checks once.
  useEffect(() => {
    const notInSf = searchResults.filter(
      b => b.salesforce_status === 'not_in_sf' && b.website && !websiteStatusRef.current.has(b.website),
    );
    if (notInSf.length > 0) checkWebsiteStatus(notInSf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Auto-enrich function: enriches businesses via SSE
  const triggerAutoEnrich = async () => {
    // Apollo enrichment is disabled — every search would otherwise fire one
    // Apollo credit per result with a website (~50–100 per search). Reps can
    // get the same employee count from the SF match on rows that are in
    // Salesforce, and LinkedIn URLs are surfaced from SF as well.
    // Re-enable by removing this early return.
    return;
    // eslint-disable-next-line no-unreachable
    setTimeout(async () => {
      const currentResults = searchResults;
      const enrichable = currentResults.filter(b => b.website && !b.isEnriched && !b.apollo_data);
      if (enrichable.length === 0) return;

      setAutoEnrichProgress({ current: 0, total: enrichable.length });

      try {
        const response = await fetch(`${API_BASE_URL}/api/prospecting/auto-enrich`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            businesses: enrichable.map(b => ({
              name: b.name,
              website: b.website,
              address: b.address,
              relevance_score: b.relevance_score,
            })),
          }),
        });

        if (!response.ok) return;

        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'enriched') {
                setAutoEnrichProgress({ current: event.index + 1, total: event.total });
                const enrichedBiz = event.business;
                setSearchResults(prev => prev.map(b => {
                  if (b.name === enrichedBiz.name && b.website === enrichedBiz.website) {
                    return {
                      ...b,
                      apollo_data: enrichedBiz.apollo_data,
                      linkedin_url: enrichedBiz.linkedin_url,
                      has_linkedin: enrichedBiz.has_linkedin,
                      isEnriched: true,
                    };
                  }
                  return b;
                }));
              } else if (event.type === 'done') {
                setAutoEnrichProgress(null);
              }
            } catch {}
          }
        }
      } catch (e) {
        console.error('Auto-enrich error:', e);
      } finally {
        setAutoEnrichProgress(null);
      }
    }, 500);
  };

  // Convert one Account row from /api/prospecting/sf-report into the Business
  // shape the rest of the page already knows how to render (markers, cards,
  // hovers). Sets `from_sf_report: true` so the marker uses the Type palette
  // and the result-card hides "+ Add to Salesforce" (this row is already in SF).
  //
  // Report rows come straight from a live SOQL — every enrichment side-effect
  // is therefore a no-op or worse:
  //   • salesforce_type_refreshed: true → suppresses /check-websites and the
  //     /extract-linkedin homepage scrape on report load.
  //   • Mirror phone + all socials onto the match object too. The phone /
  //     socials backfill effects compare b.<field> against match.<field>;
  //     if the match object is bare the comparison says "SF is missing this"
  //     and fires a writeback that the backend ends up no-op'ing. Copying
  //     the SF values into the match makes the comparison correctly skip.
  const reportAccountToBusiness = useCallback((a: any): Business => ({
    name: a.name || '(unnamed)',
    address: a.address || '',
    phone: a.phone || undefined,
    website: a.website || undefined,
    // Loose-typed billing_city / billing_state used by the Find Websites pass
    // (Google Places searchText is much more accurate with a city/state hint).
    // Not on the Business interface today; cast through `any` at read time.
    ...(a.billing_city ? { billing_city: a.billing_city } : {}),
    ...(a.billing_state ? { billing_state: a.billing_state } : {}),
    location: { lat: a.lat ?? 0, lng: a.lng ?? 0 },
    instagram_url: a.instagram_url || undefined,
    facebook_url: a.facebook_url || undefined,
    x_url: a.x_url || undefined,
    yelp_url: a.yelp_url || undefined,
    company_email: a.company_email || undefined,
    linkedin_url: a.linkedin_url || undefined,
    has_linkedin: !!a.linkedin_url,
    from_sf_report: true,
    // Already in SF: mark in_sf so the card renders "Open / Edit" instead of
    // "+ Add to Salesforce", and supply the matched account id+type for the
    // Type-based marker color.
    salesforce_status: 'in_sf',
    salesforce_type_refreshed: true,
    salesforce_matches: [{
      id: a.id,
      name: a.name,
      website: a.website || undefined,
      type: a.type || undefined,
      tier: a.tier ?? null,
      industry: a.industry ?? null,
      owner_id: a.owner_id || undefined,
      owner_name: a.owner_name || undefined,
      number_of_employees: a.number_of_employees ?? null,
      // Mirror onto the match so the phone/socials backfill effects see
      // these fields as already present and skip the write-back PATCH.
      phone: a.phone || null,
      linkedin_url: a.linkedin_url || null,
      instagram_url: a.instagram_url || null,
      facebook_url: a.facebook_url || null,
      x_url: a.x_url || null,
      yelp_url: a.yelp_url || null,
      company_email: a.company_email || null,
    }],
    website_checked: true,
  }), []);

  // Reverse Google lookup: a website or business name typed into the search
  // bar → Places/website-schema lookup → plot the resolved business as a result
  // card (with its address, phone, and SF status). Replaces the current result
  // set (and clears any loaded report) so the lookup is the focused view.
  const handleReverseLookup = async (query: string) => {
    const q = (query || '').trim();
    if (!q) return;
    setReverseLookupLoading(true);
    setActiveLookup({ query: q, step: 'Looking up business…' });
    const tId = toast.loading(`Looking up “${q}”…`);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/reverse-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      const data = await resp.json();
      if (!resp.ok) { toast.error(data.error || `Lookup failed (${resp.status})`, { id: tId }); return; }
      const raw = data.results || [];
      if (raw.length === 0) { toast.error(`No match found for “${q}”`, { id: tId }); return; }
      const businesses: Business[] = raw.map((b: any): Business => ({
        name: b.name,
        address: b.address || '',
        phone: b.phone || undefined,
        website: b.website || undefined,
        rating: b.rating || undefined,
        user_ratings_total: b.user_ratings_total || undefined,
        business_status: b.business_status,
        last_review_age_days: b.last_review_age_days,
        location: isValidLatLng(b.location) ? b.location : { lat: 0, lng: 0 },
        salesforce_status: b.salesforce_status,
        salesforce_matches: b.salesforce_matches,
        website_checked: true,
      }));

      // Hold the card off-screen and run the post-fetch enrichment that the
      // normal Lead-Finder flow runs *after* paint. Doing it here lets the card
      // render once, fully populated — no flash of placeholder pin / "unknown
      // address" mid-paint. Capped at 10s so a slow homepage doesn't deadlock
      // the search: if the scrape isn't done by then, we paint with what we
      // have and let the background flow fill the rest in.
      const sites = businesses.map(b => b.website).filter(Boolean) as string[];
      if (sites.length) {
        setActiveLookup({ query: q, step: 'Scraping website for address, socials & location…' });
        try {
          const scrape = await Promise.race([
            fetch(`${API_BASE_URL}/api/prospecting/extract-linkedin`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ websites: sites }),
            }).then(r => r.ok ? r.json() : null),
            new Promise<null>(res => setTimeout(() => res(null), 10000)),
          ]) as any;
          if (scrape) {
            const li = scrape.results || {};
            const yelp = scrape.yelp || {};
            const ig = scrape.instagram || {};
            const fb = scrape.facebook || {};
            const xm = scrape.x || {};
            const email = scrape.email || {};
            const addr = scrape.address || {};
            const geo = scrape.address_geo || {};
            // Avoid the background effect re-running these same scrapes by
            // marking each website as attempted up-front.
            sites.forEach(w => linkedinAttemptedRef.current.add(w));
            for (const b of businesses) {
              const w = b.website;
              if (!w) continue;
              if (li[w]) { b.linkedin_url = li[w]; b.has_linkedin = true; }
              if (yelp[w]) b.yelp_url = yelp[w];
              if (ig[w]) b.instagram_url = ig[w];
              if (fb[w]) b.facebook_url = fb[w];
              if (xm[w]) b.x_url = xm[w];
              if (email[w]) b.company_email = email[w];
              if (addr[w]) b.extracted_address = addr[w];
              // Recenter the card to the scraped address when the Places-derived
              // location is invalid (null-island) or missing entirely.
              const g = geo[w];
              if (g && typeof g.lat === 'number' && typeof g.lng === 'number') {
                const cur = b.location;
                const curBad = !cur || !isValidLatLng(cur) ||
                  (Math.abs(cur.lat) < 0.5 && Math.abs(cur.lng) < 0.5);
                if (curBad) b.location = { lat: g.lat, lng: g.lng };
              }
            }
          }
        } catch {
          // Best-effort enrichment — if the scrape fails, paint the card with
          // what reverse-lookup gave us; the background effects still run.
        }
      }

      // Clear any loaded report + show the lookup result(s) as the result set.
      setSfReportMeta(null);
      setSearchResults(businesses);
      setCustomerSearch('');
      // Recenter on the resolved business(es) that have coords.
      const plottable = businesses.filter(b => isValidLatLng(b.location));
      if (plottable.length && mapRef.current) {
        if (plottable.length === 1) {
          mapRef.current.panTo(plottable[0].location);
          mapRef.current.setZoom(14);
        } else {
          const bounds = new google.maps.LatLngBounds();
          plottable.forEach(b => bounds.extend(new google.maps.LatLng(b.location.lat, b.location.lng)));
          mapRef.current.fitBounds(bounds);
        }
      }
      toast.success(
        raw.length === 1
          ? `Found: ${businesses[0].name}`
          : `Found ${raw.length} matches for “${q}”`,
        { id: tId },
      );
    } catch (e) {
      toast.error(`Lookup failed: ${e instanceof Error ? e.message : 'unknown error'}`, { id: tId });
    } finally {
      setReverseLookupLoading(false);
      setActiveLookup(null);
    }
  };

  const loadSfReport = async (raw: string, opts?: { force?: boolean }) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return;
    // Replace-mode confirmation when there are unsaved results on screen.
    // Skipped when `force` is set (e.g. the chip-level refresh, where the
    // rep is intentionally re-pulling the same report they're viewing).
    if (!opts?.force && searchResults.length > 0 &&
        !window.confirm(`Loading this report will clear your current ${searchResults.length} result${searchResults.length === 1 ? '' : 's'}. Continue?`)) {
      return;
    }
    setSfReportLoading(true);
    setSfReportError(null);   // clear any prior error before retrying
    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/sf-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `force` skips the backend's 5-minute report cache so the refresh
        // button on the chip actually picks up disposition / tier edits made
        // directly in Salesforce since the initial load.
        body: JSON.stringify({ report: trimmed, force: !!opts?.force }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        // Inline error AND a toast — the inline message persists until they
        // edit the field, so the rep can read it without missing the toast.
        const msg = data.error || `Report load failed (${resp.status})`;
        setSfReportError(msg);
        toast.error(msg);
        return;
      }
      // Pick up the current SF user id BEFORE building rows so getAvailability()
      // can correctly classify Johann-owned accounts as 'yours' (Open) instead
      // of falling back to 'claimed'. Normally this is set by the SSE init of
      // /check-websites, which we now skip for report rows (those are already
      // resolved). Without this every report row would render as red/claimed.
      if (data.current_sf_user_id && data.current_sf_user_id !== currentSfUserId) {
        setCurrentSfUserId(data.current_sf_user_id);
      }
      const businesses: Business[] = (data.accounts || []).map(reportAccountToBusiness);
      // Drop those without coords from the rendered list — the marker layer
      // would render at (0,0) (Atlantic Ocean) which is worse than nothing.
      // They're counted separately in `missing_geocode` so the user knows.
      const plottable = businesses.filter(b => isValidLatLng(b.location));
      setSearchResults(plottable);
      setSfReportMeta({
        name: data.report_name,
        plotted: plottable.length,
        missing: data.missing_geocode || [],
        no_address_count: data.no_address_count || 0,
        sourceInput: trimmed,
        lastRefreshedAt: Date.now(),
        justRefreshed: !!opts?.force,
      });
      // Recenter the map on the bounding box of the loaded accounts. Skipped
      // on refresh so the rep doesn't lose their current pan/zoom.
      if (!opts?.force && plottable.length && mapRef.current) {
        const bounds = new google.maps.LatLngBounds();
        plottable.forEach(b => bounds.extend(new google.maps.LatLng(b.location.lat, b.location.lng)));
        mapRef.current.fitBounds(bounds);
      }
      toast.success(opts?.force
        ? `Refreshed: ${plottable.length} of ${data.total_rows} accounts from "${data.report_name}"`
        : `Loaded ${plottable.length} of ${data.total_rows} accounts from "${data.report_name}"`);
    } catch (e) {
      const msg = `Report load failed: ${e instanceof Error ? e.message : 'unknown error'}`;
      setSfReportError(msg);
      toast.error(msg);
    } finally {
      setSfReportLoading(false);
    }
  };

  // Unload the report: clears the result list and brings the top bar back.
  // Used by the small floating chip's × button (the top bar is hidden while a
  // report is loaded — without this the user has no way to restore it).
  const clearSfReport = () => {
    setSfReportMeta(null);
    setSfReportInput('');
    setSearchResults([]);
  };

  // Re-pull the currently-loaded report from Salesforce. Used by the chip's
  // refresh button after the rep changed disposition/tier in SF and wants the
  // map to reflect the new state without having to re-paste the report URL.
  // Force-mode skips the "clear current results?" prompt (we're replacing the
  // report's own rows, so the prompt would be noise).
  const refreshSfReport = async () => {
    if (!sfReportMeta || sfReportMeta.refreshing) return;
    setSfReportMeta(m => m && { ...m, refreshing: true });
    try {
      await loadSfReport(sfReportMeta.sourceInput, { force: true });
    } finally {
      // loadSfReport replaces sfReportMeta on success — guard against a null
      // here by using the functional setter form.
      setSfReportMeta(m => m && { ...m, refreshing: false });
    }
  };

  // Auto-clear the post-refresh "✓ pulse" flag so the chip stops glowing
  // after ~1.6s. Tied to lastRefreshedAt so re-clicking the button retriggers
  // the animation cleanly even before the previous one expires.
  useEffect(() => {
    if (!sfReportMeta?.justRefreshed) return;
    const refreshedAt = sfReportMeta.lastRefreshedAt;
    const t = setTimeout(() => {
      // Only clear if this is still the same refresh event — otherwise we'd
      // race against a brand-new refresh that already set its own flag.
      setSfReportMeta(m =>
        m && m.lastRefreshedAt === refreshedAt ? { ...m, justRefreshed: false } : m,
      );
    }, 1600);
    return () => clearTimeout(t);
  }, [sfReportMeta?.justRefreshed, sfReportMeta?.lastRefreshedAt]);

  // Find websites for SF-report rows that came in with Account.Website empty.
  // Fans out to Google Places searchText per row, then PATCHes SF Account.Website
  // (and Phone when also empty). Updates the corresponding Business rows in
  // place so cards re-render with site-images, classifier, and the rest of the
  // website-driven UI without a full reload. Non-destructive — backend skips
  // any Account that's gained a website concurrent with this call.
  const findReportWebsites = async () => {
    if (!sfReportMeta || sfReportMeta.findingWebsites) return;
    // Funnel: send EVERY report row, regardless of website state. The backend
    // routes each through the right phases — Places lookup is skipped for rows
    // that already have a `website` (those go straight to the socials scrape +
    // liveness + classifier). A report with a mix of "has website, missing
    // socials", "has partial socials", and "no website at all" gets the right
    // enrichment per row from the same call.
    const candidates = searchResults
      .filter(b => b.from_sf_report)
      .map(b => {
        const m = b.salesforce_matches?.[0];
        return m?.id ? {
          id: m.id,
          name: b.name,
          city: (b as any).billing_city || '',
          state: (b as any).billing_state || '',
          website: b.website || '',
        } : null;
      })
      .filter((x): x is { id: string; name: string; city: string; state: string; website: string } => !!x);
    if (candidates.length === 0) {
      toast(`No report rows to enrich`);
      return;
    }
    setSfReportMeta(m => m && { ...m, findingWebsites: true, enrichProgress: { current: 0, total: candidates.length } });
    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/sf-report/find-websites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accounts: candidates }),
      });
      if (!resp.ok || !resp.body) {
        const data = await resp.json().catch(() => ({}));
        toast.error(data.error || `Enrich failed (${resp.status})`);
        return;
      }
      // Consume the SSE stream: one progress event per row, merging each row's
      // enrichment into the matching card as it lands (so cards light up live),
      // plus a running progress counter on the chip.
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let done: any = null;
      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let ev: any;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }
          if (ev.type === 'progress') {
            setSfReportMeta(m => m && { ...m, enrichProgress: { current: ev.index + 1, total: ev.total } });
            // Merge this row's results into the matching card by SF id.
            if (ev.id) {
              setSearchResults(prev => prev.map(b => {
                if (b.salesforce_matches?.[0]?.id !== ev.id) return b;
                return {
                  ...b,
                  website: b.website || ev.website || undefined,
                  phone: b.phone || ev.phone || undefined,
                  linkedin_url: b.linkedin_url || ev.linkedin_url || undefined,
                  has_linkedin: b.has_linkedin || !!ev.linkedin_url,
                  instagram_url: b.instagram_url || ev.instagram_url || undefined,
                  facebook_url: b.facebook_url || ev.facebook_url || undefined,
                  x_url: b.x_url || ev.x_url || undefined,
                  yelp_url: b.yelp_url || ev.yelp_url || undefined,
                  company_email: b.company_email || ev.company_email || undefined,
                  // DQ landed → reflect the new Type on the match so the card
                  // pill + marker recolor without a reload.
                  salesforce_matches: ev.dq
                    ? (b.salesforce_matches || []).map((mm, idx) =>
                        idx === 0 ? { ...mm, type: 'Disqualified' } : mm)
                    : b.salesforce_matches,
                };
              }));
            }
          } else if (ev.type === 'done') {
            done = ev;
          }
        }
      }
      const t = (done && done.tally) || {};
      const parts: string[] = [];
      if (t.website) parts.push(`${t.website} new websites`);
      if (t.phone) parts.push(`${t.phone} phones`);
      if (t.linkedin) parts.push(`${t.linkedin} LinkedIn`);
      const socialCount = (t.instagram || 0) + (t.facebook || 0) + (t.x || 0) + (t.yelp || 0);
      if (socialCount) parts.push(`${socialCount} other socials`);
      if (t.email) parts.push(`${t.email} emails`);
      const dqs = (t.dq_dead || 0) + (t.dq_systems || 0);
      if (dqs) parts.push(`${dqs} DQ'd`);
      const scrapeable = (done && done.with_website) || 0;
      const totalN = (done && done.total) || candidates.length;
      const noMatch = (done && done.no_match) || 0;
      toast.success(
        `Enriched ${scrapeable} of ${totalN} accounts` +
        (parts.length ? ` · wrote ${parts.join(', ')}` : '') +
        (noMatch > 0 ? ` · ${noMatch} Places no match` : ''),
        { duration: 9000 },
      );
    } catch (e) {
      toast.error(`Enrich failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setSfReportMeta(m => m && { ...m, findingWebsites: false, enrichProgress: undefined });
    }
  };

  // Backfill geocodes for the report rows our cache doesn't have. Calls the
  // existing /api/geocode/bulk (it cache-checks then Google-geocodes the
  // miss), then merges the freshly-resolved coords into searchResults so the
  // markers appear without a full reload.
  const backfillReportGeocodes = async () => {
    if (!sfReportMeta || sfReportMeta.missing.length === 0) return;
    setSfReportMeta(m => m && { ...m, backfilling: true });
    try {
      const addresses = Array.from(new Set(
        sfReportMeta.missing.map(m => m.address).filter(Boolean),
      ));
      const resp = await fetch(`${API_BASE_URL}/api/geocode/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addresses }),
      });
      const data = await resp.json();
      const coords: Record<string, { lat: number; lng: number }> = data.results || {};
      const resolvedCount = Object.keys(coords).length;
      // Build new Business rows for the newly-geocoded missing accounts and
      // append them to searchResults.
      const newRows: Business[] = sfReportMeta.missing
        .filter(m => coords[m.address])
        .map(m => reportAccountToBusiness({
          id: m.id, name: m.name, address: m.address,
          lat: coords[m.address].lat, lng: coords[m.address].lng,
        }));
      if (newRows.length) {
        setSearchResults(prev => [...prev, ...newRows]);
      }
      setSfReportMeta(m => m && ({
        ...m,
        plotted: m.plotted + newRows.length,
        missing: m.missing.filter(x => !coords[x.address]),
        backfilling: false,
      }));
      toast.success(`Geocoded ${resolvedCount} of ${addresses.length} missing addresses`);
    } catch (e) {
      setSfReportMeta(m => m && ({ ...m, backfilling: false }));
      toast.error(`Backfill failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  };

  // Contact discovery for a business
  const loadContacts = async (business: Business) => {
    if (!business.website) return;

    const domain = new URL(business.website).hostname.replace('www.', '');

    setSearchResults(prev => prev.map(b =>
      b === business ? { ...b, contactsLoading: true, contactsExpanded: true } : b
    ));

    try {
      const response = await fetch(`${API_BASE_URL}/api/prospecting/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, org_name: business.name }),
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(prev => prev.map(b =>
          b === business ? { ...b, contacts: data.contacts, contactsLoading: false } : b
        ));
      }
    } catch (e) {
      console.error('Contact discovery error:', e);
      setSearchResults(prev => prev.map(b =>
        b === business ? { ...b, contactsLoading: false } : b
      ));
    }
  };

  // Lazy-load the homepage content photos for a prospect, then open the lightbox.
  // Fetched on-demand (first click) and cached on the business object so re-opens
  // are instant; the backend also caches per-host for 24h.
  const openSitePhotos = async (business: Business, force = false) => {
    if (!business.website || business.website_dead) return;

    // Already have them — just open. `force` (the "No photos" retry) skips this so a
    // previously-empty/failed row re-fetches instead of being stuck forever.
    if (!force && business.site_images_loaded) {
      if ((business.site_images?.length ?? 0) > 0) {
        setLightboxBusiness(business);
      }
      return;
    }
    if (business.site_images_loading) return;

    // Match rows by a stable key, NOT object reference: background work (SF check,
    // website-status, auto-enrich) rebuilds Business objects during the multi-second
    // fetch, so `b === business` would miss the row — leaving the spinner stuck
    // forever and the result never applied. The key survives those rebuilds.
    const targetKey = `${business.name}|${business.address}`;

    // Clear any prior error and show the spinner immediately for instant feedback.
    setSearchResults(prev => prev.map(b =>
      `${b.name}|${b.address}` === targetKey
        ? { ...b, site_images_loading: true, site_images_error: false }
        : b
    ));

    let images: SiteImage[] = [];
    let ok = false;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/prospecting/website-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: business.website }),
      });
      if (resp.ok) {
        const data = await resp.json();
        images = Array.isArray(data.images) ? data.images : [];
        ok = true;
      }
    } catch (err) {
      console.warn('website-images fetch failed:', err);
    }

    // Only mark the row "loaded" on a SUCCESSFUL fetch. A transient failure (backend
    // down, network blip) sets an explicit error flag → a distinct "Retry" chip,
    // rather than silently reverting to an idle "Photos" that looks like nothing happened.
    setSearchResults(prev => prev.map(b =>
      `${b.name}|${b.address}` === targetKey
        ? {
            ...b,
            site_images: ok ? images : b.site_images,
            site_images_loading: false,
            site_images_loaded: ok ? true : b.site_images_loaded,
            site_images_error: !ok,
          }
        : b
    ));
    if (images.length > 0) {
      // Open against a fresh snapshot (the `business` closure may be stale now).
      setLightboxBusiness({ ...business, site_images: images, site_images_loaded: true });
    }
  };

  // Multi-branch search
  // Find more locations of a franchise nationwide and MERGE the new locations
  // into the franchise card (rather than the old separate side-panel). Newly-
  // discovered locations inherit the primary's website so the existing
  // domain-based grouping picks them up automatically. Existing addresses are
  // skipped to avoid duplicating rows the rep already sees.
  const handleMultiBranchSearch = async (primary: Business) => {
    const businessName = primary.name;
    setMultiBranchLoading(businessName);

    try {
      const response = await fetch(`${API_BASE_URL}/api/prospecting/multi-branch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_name: businessName }),
      });
      if (!response.ok) return;
      const reader = response.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type !== 'results') continue;
            // Build the set of addresses currently on this franchise card so we
            // don't append duplicates of locations the rep already sees.
            const existing = new Set<string>();
            setSearchResults(prev => {
              for (const b of prev) {
                if (b.website && primary.website && normalizeDomainClient(b.website) === normalizeDomainClient(primary.website)) {
                  existing.add((b.address || '').trim().toLowerCase());
                }
              }
              const fresh: Business[] = (event.locations || [])
                .filter((loc: any) => loc.address && !existing.has(loc.address.trim().toLowerCase()))
                .map((loc: any): Business => ({
                  name: loc.name,
                  address: loc.address,
                  location: loc.location,
                  rating: loc.rating,
                  user_ratings_total: loc.user_ratings_total,
                  phone: loc.phone || undefined,
                  // Inherit website so domain-based grouping pulls these into
                  // the SAME card as the primary location.
                  website: primary.website,
                  salesforce_status: 'checking',
                }));
              if (fresh.length === 0) {
                toast(`No new locations found for "${businessName}"`);
                return prev;
              }
              toast.success(`Added ${fresh.length} more location${fresh.length === 1 ? '' : 's'} to ${businessName}`);
              return [...prev, ...fresh];
            });
          } catch {}
        }
      }
    } catch (e) {
      console.error('Multi-branch search error:', e);
      toast.error('Failed to search for more locations');
    } finally {
      setMultiBranchLoading(null);
    }
  };

  // Handle news "Prospect This Area" action
  const handleProspectArea = (location: { lat: number; lng: number }, terms: string[]) => {
    // Set map center to the incident location
    setMapCenter(location);
    setMapZoom(11);
    if (mapRef.current) {
      mapRef.current.panTo(location);
      mapRef.current.setZoom(11);
    }

    // Drop a pin
    setCustomPin(location);
    setCustomPinActive(true);
    setSelectedCustomer(null);

    // Create circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (mapRef.current) {
      const circle = new google.maps.Circle({
        map: mapRef.current,
        center: location,
        radius: DEFAULT_RADIUS,
        fillColor: '#e3f2fd',
        fillOpacity: 0.2,
        strokeColor: '#1976d2',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        editable: true,
        draggable: true,
      });
      handleCircleComplete(circle as Circle);
    }

    // Pre-fill search terms
    setSearchTerms(terms);
  };

  // Unified search handler: all searches go through the vertical SSE endpoint
  const handleSearchDispatch = () => {
    if (selectedVertical === 'custom') {
      handleVerticalSearch();
    } else if (selectedVertical.startsWith('saved:')) {
      // Load saved terms then run custom search
      const savedId = selectedVertical.replace('saved:', '');
      const saved = savedSearches.find(s => s.id === savedId);
      if (saved) {
        setSearchTerms([...saved.searchTerms]);
        setSelectedVertical('custom');
        // Search will be triggered manually after terms load
      }
    } else {
      handleVerticalSearch();
    }
  };

  // Handle vertical dropdown change
  const handleVerticalChange = (value: string) => {
    if (value.startsWith('saved:')) {
      // Load saved terms into custom mode
      const savedId = value.replace('saved:', '');
      const saved = savedSearches.find(s => s.id === savedId);
      if (saved) {
        setSearchTerms([...saved.searchTerms]);
        setSelectedVertical('custom');
      }
    } else {
      setSelectedVertical(value);
      if (value !== 'custom') {
        setSearchTermsUsed([]);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentInput.trim()) {
      e.preventDefault();
      setSearchTerms([...searchTerms, currentInput.trim()]);
      setCurrentInput('');
    } else if (e.key === 'Backspace' && !currentInput && searchTerms.length > 0) {
      e.preventDefault();
      const newTerms = [...searchTerms];
      newTerms.pop();
      setSearchTerms(newTerms);
    }
  };

  const removeSearchTerm = (indexToRemove: number) => {
    setSearchTerms(searchTerms.filter((_, index) => index !== indexToRemove));
  };

  const handleBusinessClick = (business: Business) => {
    setSelectedBusiness(business);
    if (isValidLatLng(business.location)) {
      setMapCenter(business.location);
      setMapZoom(15);
      if (mapRef.current) {
        mapRef.current.panTo(business.location);
      }
    }
  };

  const handleCircleComplete = (circle: Circle) => {
    // Remove any existing circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
    }
    
    circleRef.current = circle;
    setSearchRadius(Math.round(circle.getRadius()));
    
    // Add radius change listener
    circle.addListener('radius_changed', () => {
      const newRadius = Math.round(circle.getRadius());
      setSearchRadius(newRadius);
      // Resizing the search area implies the user wants to re-search — pop
      // the panel back so they can click Search without manual reopening.
      setSearchPanelHidden(false);
    });

    // Add center change listener that only updates when dragging is finished
    let dragTimeout: number | null = null;
    circle.addListener('center_changed', () => {
      // Clear any existing timeout
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
      
      // Set a timeout to update the map center only after dragging stops
      dragTimeout = window.setTimeout(() => {
        const center = circle.getCenter();
        if (center) {
          const newCenter = { lat: center.lat(), lng: center.lng() };
          setMapCenter(newCenter);
          // Update search location to circle's new position
          setCustomPin(newCenter);
          setCustomPinActive(true);
          setSelectedCustomer(null);
        }
        dragTimeout = null;
        setIsCircleDragging(false);
      }, 300); // Wait 300ms after the last center change
    });

    // Add drag start and end listeners for better UX
    circle.addListener('dragstart', () => {
      setIsCircleDragging(true);
    });

    circle.addListener('dragend', () => {
      setIsCircleDragging(false);
      const center = circle.getCenter();
      if (center) {
        const newCenter = { lat: center.lat(), lng: center.lng() };
        setMapCenter(newCenter);
        // Update search location to circle's new position
        setCustomPin(newCenter);
        setCustomPinActive(true);
        setSelectedCustomer(null);
      }
      // Moving the circle implies "search a new area" — reopen the panel
      // so the next Search appends to existing results.
      setSearchPanelHidden(false);
    });
  };

  const handleCustomerClick = (customer: MappedCustomer) => {
    // Guard: a customer without a geocoded address would otherwise silently
    // navigate the map to {lat: undefined, lng: undefined} (≈ 0,0).
    if (!customer.latitude || !customer.longitude) {
      console.warn(`Customer ${customer.name} (id=${customer.id}) has no lat/lng — skipping map navigation`);
      setError(`${customer.description || customer.name} doesn't have a geocoded address — can't navigate the map.`);
      return;
    }

    // Clear search terms when selecting a new customer, but keep search results
    setSearchTerms([]);
    setCurrentInput('');
    setSelectedBusiness(null);

    // Name the pin. Clicking one used to only draw a search circle, which left
    // no way to tell which customer you had just clicked.
    setSelectedCustomer(customer);
    // Clicking a pin IS the intent to open the popup, so it clears any prior
    // dismissal here rather than relying on the effect below. That effect keys
    // on the customer's id, which does not change when you re-click the pin you
    // just closed -- so the popup stayed shut until you clicked away first.
    setPinInfoDismissed(false);

    // Always show the customer pin layer when navigating to one, so the
    // user doesn't land on a center with no visible pin if they previously
    // toggled the layer off.
    setShowCustomerPins(true);
    // Reopen the search panel — clicking a customer post-search means
    // the user wants to start a new search around this location.
    setSearchPanelHidden(false);
    // Clear any prior search context — otherwise the append-mode kicks
    // in on the next search and stale results from the previous customer
    // leak into the new sidebar (and onto the map, beyond the new radius).
    setSearchResults([]);
    setMultiBranchResults([]);
    setResultsFromCluster(false);

    // Center the map on the customer and zoom to fit the circle
    const customerCenter = { lat: customer.latitude, lng: customer.longitude };
    setMapCenter(customerCenter);
    setMapZoom(11);
    if (mapRef.current) {
      mapRef.current.panTo(customerCenter);
      mapRef.current.setZoom(11);
    }
    
    // Remove any existing circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    // Create the circle programmatically if we have the map reference
    if (mapRef.current) {
      const circle = new google.maps.Circle({
        map: mapRef.current,
        center: { lat: customer.latitude!, lng: customer.longitude! },
        radius: DEFAULT_RADIUS,
        fillColor: '#e3f2fd',
        fillOpacity: 0.2,
        strokeColor: '#1976d2',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        editable: true,
        draggable: true
      });

      // Handle the circle as if it was drawn by the user
      handleCircleComplete(circle as Circle);
    }
  };

  // Covers the entry points that set selectedCustomer directly instead of going
  // through handleCustomerClick. Dismissing silences the popup for that one
  // customer; moving to a different one brings it back.
  useEffect(() => {
    setPinInfoDismissed(false);
  }, [selectedCustomer?.id]);

  const dropPinAt = (pos: { lat: number; lng: number }) => {
    setCustomPin(pos);
    // Intentionally NOT calling setMapCenter/setMapZoom here — the pin should
    // land exactly under the user's cursor. Re-centering or re-zooming pulls
    // the map out from under the click and the pin appears off-target.
    setIsPinMode(false);
    setCustomPinActive(true);
    setSelectedCustomer(null);
    setSearchPanelHidden(false);
    // Same as handleCustomerClick — clear stale results when explicitly
    // re-anchoring the search to a new pin location.
    setSearchResults([]);
    setMultiBranchResults([]);
    setResultsFromCluster(false);

    // Remove any existing circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    // Create a new circle at the pin location
    if (mapRef.current) {
      const circle = new google.maps.Circle({
        map: mapRef.current,
        center: pos,
        radius: DEFAULT_RADIUS,
        fillColor: '#e3f2fd',
        fillOpacity: 0.2,
        strokeColor: '#1976d2',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        editable: true,
        draggable: true
      });

      handleCircleComplete(circle as Circle);
    }
  };

  const handleMapRightClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    dropPinAt({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (isPinMode) {
      dropPinAt({ lat: e.latLng!.lat(), lng: e.latLng!.lng() });
    } else {
      // If not in pin mode and not dragging a circle, clear selected customer and business when clicking on map
      if (!isCircleDragging) {
        setSelectedCustomer(null);
        setSelectedBusiness(null);
        setSearchTerms([]);
        setCurrentInput('');
        
        // Remove any existing circle
        if (circleRef.current) {
          circleRef.current.setMap(null);
          circleRef.current = null;
        }
        // Do NOT clear searchResults here!
      }
    }
  };

  const handleDeletePin = () => {
    setCustomPin(null);
    setCustomPinActive(false);
    setIsPinMode(false);
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    setSearchResults([]);
  };

  // Function to handle enrichment of search results
  const handleEnrichResults = async () => {
    if (searchResults.length === 0) return;
    
    try {
      setIsEnriching(true);
      setEnrichmentError(null);
      
      // Get all business websites
      const businessUrls = searchResults
        .filter(business => business.website)
        .map(business => business.website as string);
      
      if (businessUrls.length === 0) {
        setEnrichmentError('No business websites found to enrich');
        setIsEnriching(false);
        return;
      }
      
      // Start enrichment task
      const response = await fetch(`${API_BASE_URL}/api/enrich-companies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ urls: businessUrls }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start enrichment task');
      }
      
      const data = await response.json();
      
      // Initialize enrichment task
      const task: EnrichmentTask = {
        taskId: data.task_id,
        status: data.status,
        totalUrls: businessUrls.length,
        processed: 0,
        results: [],
        startedAt: data.started_at,
      };
      
      setEnrichmentTask(task);
      
      // Start polling for task status
      startEnrichmentStatusPolling(task.taskId);
      
    } catch (error) {
      console.error('Error starting enrichment:', error);
      setEnrichmentError(error instanceof Error ? error.message : 'Failed to start enrichment');
      setIsEnriching(false);
    }
  };
  
  // Function to start polling for enrichment task status
  const startEnrichmentStatusPolling = (taskId: string) => {
    // Clear any existing interval
    if (enrichmentStatusInterval.current) {
      window.clearInterval(enrichmentStatusInterval.current);
    }
    
    // Set up new interval to poll for status
    enrichmentStatusInterval.current = window.setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/enrichment-status/${taskId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch enrichment status');
        }
        
        const data = await response.json();
        
        // Update enrichment task
        setEnrichmentTask(prevTask => {
          if (!prevTask) return null;
          
          const updatedTask: EnrichmentTask = {
            ...prevTask,
            status: data.status,
            processed: data.processed,
            currentUrl: data.current_url,
            results: data.results || [],
            completedAt: data.completed_at,
            totalProcessed: data.total_processed,
            successful: data.successful,
            failed: data.failed,
            error: data.error,
          };
          
          return updatedTask;
        });
        
        // Update progress
        if (data.total_urls > 0) {
          setEnrichmentProgress({
            progress: Math.round((data.processed / data.total_urls) * 100),
            currentUrl: data.current_url,
            totalUrls: data.total_urls,
            processedUrls: data.processed,
            successfulUrls: data.successful,
            failedUrls: data.failed
          });
        }
        
        // Update search results with enriched data
        if (data.results && data.results.length > 0) {
          setSearchResults(prevResults => {
            return prevResults.map(business => {
              // Find matching result
              const result = data.results.find((r: EnrichmentResult) => r.url === business.website);
              
              if (result) {
                if (result.error) {
                  return {
                    ...business,
                    isEnriched: false,
                    isEnriching: false,
                    enrichmentError: result.error,
                  };
                } else {
                  return {
                    ...business,
                    services: result.services,
                    isEnriched: true,
                    isEnriching: false,
                  };
                }
              }
              
              return business;
            });
          });
        }
        
        // If task is completed or failed, stop polling
        if (data.status === 'completed' || data.status === 'failed') {
          if (enrichmentStatusInterval.current) {
            window.clearInterval(enrichmentStatusInterval.current);
            enrichmentStatusInterval.current = null;
          }
          setIsEnriching(false);

          if (data.status === 'completed') {
            updateHistoryWithEnrichment();
          } else if (data.status === 'failed') {
            setEnrichmentError(data.error || 'Enrichment task failed');
          }
        }
        
      } catch (error) {
        console.error('Error polling enrichment status:', error);
        setEnrichmentError(error instanceof Error ? error.message : 'Failed to check enrichment status');
        
        // Stop polling on error
        if (enrichmentStatusInterval.current) {
          window.clearInterval(enrichmentStatusInterval.current);
          enrichmentStatusInterval.current = null;
        }
        setIsEnriching(false);
      }
    }, 2000); // Poll every 2 seconds
  };
  
  // Clean up interval on component unmount
  useEffect(() => {
    return () => {
      if (enrichmentStatusInterval.current) {
        window.clearInterval(enrichmentStatusInterval.current);
      }
    };
  }, []);

  // Save current search
  const saveCurrentSearch = () => {
    if (!currentSearchName.trim()) {
      setError('Please enter a name for this search');
      return;
    }
    
    const newSearch: SavedSearch = {
      id: Date.now().toString(),
      name: currentSearchName,
      searchTerms: [...searchTerms],
      radius: searchRadius,
      customerId: selectedCustomer?.id,
      location: selectedCustomer ? { lat: selectedCustomer.latitude!, lng: selectedCustomer.longitude! } : undefined,
      createdAt: Date.now()
    };
    
    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    localStorage.setItem('prospectingSavedSearches', JSON.stringify(updatedSearches));
    setCurrentSearchName('');
    setError(null);
  };

  // Apply a saved search
  const applySavedSearch = (search: SavedSearch) => {
    setSearchTerms(search.searchTerms);
    setSearchRadius(search.radius);
    if (search.customerId) {
      const customer = customers.find(c => c.id === search.customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setShowCustomerPins(true);
        setSearchPanelHidden(false);
      }
    }
    if (search.location) {
      setMapCenter(search.location);
      setMapZoom(11);
    }
    setShowSaveSearchModal(false);
    setCurrentSearchName('');
  };

  // Delete a saved search
  const deleteSavedSearch = (id: string) => {
    const updatedSearches = savedSearches.filter(search => search.id !== id);
    setSavedSearches(updatedSearches);
    localStorage.setItem('prospectingSavedSearches', JSON.stringify(updatedSearches));
  };

  // Edit a saved search
  const editSavedSearch = (search: SavedSearch) => {
    setEditingSavedSearch(search);
    setCurrentSearchName(search.name);
    setNewSearchTerms(search.searchTerms.join(', '));
    setCurrentInput('');
  };

  // Update a saved search
  const updateSavedSearch = () => {
    if (!editingSavedSearch || !currentSearchName.trim()) {
      setError('Please enter a name for this search');
      return;
    }

    if (!newSearchTerms.trim()) {
      setError('Please enter at least one search term');
      return;
    }

    const terms = newSearchTerms.split(',').map(term => term.trim()).filter(term => term);
    
    if (terms.length === 0) {
      setError('Please enter at least one search term');
      return;
    }
    
    const updatedSearches = savedSearches.map(search => 
      search.id === editingSavedSearch.id 
        ? { 
            ...search, 
            name: currentSearchName,
            searchTerms: terms,
            updatedAt: Date.now()
          } 
        : search
    );
    
    setSavedSearches(updatedSearches);
    localStorage.setItem('prospectingSavedSearches', JSON.stringify(updatedSearches));
    setCurrentSearchName('');
    setCurrentInput('');
    setNewSearchTerms('');
    setEditingSavedSearch(null);
    setError(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingSavedSearch(null);
    setCurrentSearchName('');
    setCurrentInput('');
    setNewSearchTerms('');
    setError(null);
  };

  // Create a new saved search
  const createNewSavedSearch = () => {
    if (!currentSearchName.trim()) {
      setError('Please enter a name for this search');
      return;
    }
    
    if (!newSearchTerms.trim()) {
      setError('Please enter search terms');
      return;
    }
    
    const terms = newSearchTerms.split(',').map(term => term.trim()).filter(term => term);
    
    if (terms.length === 0) {
      setError('Please enter at least one search term');
      return;
    }
    
    const newSearch: SavedSearch = {
      id: Date.now().toString(),
      name: currentSearchName,
      searchTerms: terms,
      radius: DEFAULT_RADIUS,
      createdAt: Date.now()
    };
    
    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    localStorage.setItem('prospectingSavedSearches', JSON.stringify(updatedSearches));
    setCurrentSearchName('');
    setNewSearchTerms('');
    setError(null);
  };

  // Render the saved searches tab
  const renderSavedSearchesTab = () => {
    return (
      <div className="saved-searches-container">
        <div className="create-search-form">
          <h3>{editingSavedSearch ? 'Edit Saved Terms' : 'Create New Saved Terms'}</h3>
          <div className="form-group">
            <label htmlFor="search-name">Terms Name</label>
            <input
              id="search-name"
              type="text"
              value={currentSearchName}
              onChange={(e) => setCurrentSearchName(e.target.value)}
              placeholder="Enter a name for these terms"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="search-terms">Terms</label>
            <div className="search-terms-editor">
              <div className="search-terms-chips">
                {newSearchTerms.split(',')
                  .map(term => term.trim())
                  .filter(term => term)
                  .map((term, index) => (
                    <div key={index} className="search-term-chip">
                      {term}
                      <button
                        onClick={() => {
                          const terms = newSearchTerms.split(',').map(t => t.trim()).filter(t => t);
                          terms.splice(index, 1);
                          setNewSearchTerms(terms.join(', '));
                        }}
                        className="remove-term"
                      >
                        ×
                      </button>
                    </div>
                  ))
                }
              </div>
              <input
                type="text"
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && currentInput.trim()) {
                    e.preventDefault();
                    const terms = newSearchTerms ? newSearchTerms.split(',').map(t => t.trim()).filter(t => t) : [];
                    terms.push(currentInput.trim());
                    setNewSearchTerms(terms.join(', '));
                    setCurrentInput('');
                  }
                }}
                placeholder="Type a term and press Enter"
                className="term-input"
              />
            </div>
          </div>
          {editingSavedSearch ? (
            <div className="edit-actions">
              <Button
                variant="primary"
                onClick={updateSavedSearch}
                disabled={!currentSearchName.trim() || !newSearchTerms.trim()}
                className="save-button"
              >
                Save Changes
              </Button>
              <Button
                variant="secondary"
                onClick={cancelEditing}
                className="cancel-button"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={createNewSavedSearch}
              disabled={!currentSearchName.trim() || !newSearchTerms.trim()}
              className="create-search-button"
            >
              Save Terms
            </Button>
          )}
        </div>

        <div className="saved-searches-list">
          <h3>Your Saved Terms</h3>
          {savedSearches.length === 0 ? (
            <div className="no-searches">You don't have any saved terms yet.</div>
          ) : (
            <div className="search-history-list">
              {savedSearches.map(search => (
                <div 
                  key={search.id} 
                  className="search-history-item"
                  onClick={() => {
                    editSavedSearch(search);
                  }}
                >
                  <div className="search-history-header">
                    <div className="search-history-info">
                      <div className="search-history-date">
                        {new Date(search.createdAt).toLocaleDateString()}
                      </div>
                      <div className="search-history-terms">
                        {search.name}
                      </div>
                      <div className="search-history-location">
                        {search.searchTerms.join(', ')}
                      </div>
                    </div>
                    <div className="search-history-actions">
                      {editingSavedSearch?.id === search.id ? (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            className="save-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateSavedSearch();
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="cancel-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelEditing();
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            className="edit-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              editSavedSearch(search);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSavedSearch(search.id);
                            }}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Add this function to export search history
  const handleExportSearchHistory = (searchEntry: SearchHistory) => {
    // Create CSV header
    const headers = [
      'Company',
      'Address',
      'Phone',
      'Website',
      'Rating',
      'Services',
      'LinkedIn',
      'Employees',
      'Industry',
      'Revenue',
      'Search Date',
      'Search Terms',
      'Search Radius (miles)',
      'Location'
    ].join(',');

    // Create CSV rows
    const rows = searchEntry.results.companies.map(company => {
      const services = company.services?.map(s => typeof s === 'string' ? s : s.name).join('; ') || '';
      return [
        `"${company.name || ''}"`,
        `"${company.address || ''}"`,
        `"${company.phone || ''}"`,
        `"${company.website || ''}"`,
        company.rating || '',
        `"${services}"`,
        `"${company.linkedin_url || company.apollo_data?.linkedin_url || ''}"`,
        company.apollo_data?.employee_count || '',
        `"${company.apollo_data?.industry || ''}"`,
        `"${company.apollo_data?.annual_revenue || ''}"`,
        `"${new Date(searchEntry.searchDate).toLocaleString()}"`,
        `"${searchEntry.searchTerms.join('; ')}"`,
        metersToMiles(searchEntry.radius),
        `"${searchEntry.location.address || `${searchEntry.location.lat}, ${searchEntry.location.lng}`}"`
      ].join(',');
    });

    // Combine header and rows
    const csvContent = [headers, ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `search_history_${new Date(searchEntry.searchDate).toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add this function back near the other export functions
  const handleExportResults = () => {
    if (!searchResults.length) return;

    // Create CSV header
    const headers = [
      'Company',
      'Website',
      'Industry',
      '# employees',
      'Annual revenue',
      'Total funding',
      'Company phone',
      'Company linkedin url',
      'Company street',
      'Company city',
      'Company state',
      'Company postal code',
      'Company country',
      'Company founded year',
      'Latitude',
      'Longitude',
      'Google Rating',
      'Services',
      'Service Descriptions',
      'Enrichment Status',
      'Enrichment Date'
    ].join(',');

    // Create CSV rows
    const rows = searchResults.map(business => {
      const services = business.services?.map(s => s.name).join('; ') || '';
      const serviceDescriptions = business.services?.map(s => s.description).join('; ') || '';
      
      // Split address into components if possible
      const addressParts = (business.address || '').split(',').map(part => part.trim());
      const street = addressParts[0] || '';
      const city = addressParts[1] || '';
      const stateZip = (addressParts[2] || '').split(' ');
      const state = stateZip[0] || '';
      const postalCode = stateZip[1] || '';
      
      return [
        `"${business.name || ''}"`,
        `"${business.website || ''}"`,
        '', // Industry (from enrichment)
        '', // # employees (from enrichment)
        '', // Annual revenue (from enrichment)
        '', // Total funding (from enrichment)
        `"${business.phone || ''}"`,
        '', // Company linkedin url (from enrichment)
        `"${street}"`,
        `"${city}"`,
        `"${state}"`,
        `"${postalCode}"`,
        '', // Company country (from enrichment)
        '', // Company founded year (from enrichment)
        business.location.lat,
        business.location.lng,
        business.rating || '',
        `"${services}"`,
        `"${serviceDescriptions}"`,
        business.isEnriched ? 'Yes' : 'No',
        business.isEnriched ? new Date().toISOString() : ''
      ].join(',');
    });

    // Combine header and rows
    const csvContent = [headers, ...rows].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const timestamp = new Date().toISOString();
    link.href = URL.createObjectURL(blob);
    link.download = `prospecting_results_${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Add this function to remove search history entry
  const removeSearchHistoryEntry = (entryId: string) => {
    const updatedHistory = searchHistory.filter(entry => entry.id !== entryId);
    setSearchHistory(updatedHistory);
    localStorage.setItem('prospectingSearchHistory', JSON.stringify(updatedHistory));
  };

  // Update reloadSearchFromHistory to use the saved locations and additional fields
  const reloadSearchFromHistory = async (entry: SearchHistory) => {
    try {
      setIsReloading(true);
      setError(null);
      
      // Set search terms
      setSearchTerms(entry.searchTerms);
      
      // Set search radius
      setSearchRadius(entry.radius);
      
      // Set map center and zoom
      setMapCenter(entry.location);
      setMapZoom(11);
      
      // Clear any existing custom pin
      setCustomPin(null);
      setCustomPinActive(false);
      
      // If there's a customer address, try to find and select that customer
      if (entry.location.address) {
        const customer = customers.find(c => {
          const customerAddress = `${c.address}, ${c.city}, ${c.state} ${c.zip}`;
          return customerAddress === entry.location.address;
        });
        if (customer) {
          setSelectedCustomer(customer);
          setShowCustomerPins(true);
        } else {
          // If no matching customer found, set custom pin
          setCustomPin(entry.location);
          setCustomPinActive(true);
          setSelectedCustomer(null);
        }
      } else {
        // If no address, set custom pin
        setCustomPin(entry.location);
        setCustomPinActive(true);
        setSelectedCustomer(null);
      }
      
      // Create a new circle at the location with the original radius
      if (mapRef.current) {
        // Remove any existing circle
        if (circleRef.current) {
          circleRef.current.setMap(null);
          circleRef.current = null;
        }

        // Create new circle
        const circle = new google.maps.Circle({
          map: mapRef.current,
          center: entry.location,
          radius: entry.radius,
          fillColor: '#e3f2fd',
          fillOpacity: 0.2,
          strokeColor: '#1976d2',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          editable: true,
          draggable: true
        });

        // Store the circle reference
        circleRef.current = circle;

        // Add radius change listener
        circle.addListener('radius_changed', () => {
          const newRadius = Math.round(circle.getRadius());
          setSearchRadius(newRadius);
        });

        circle.addListener('center_changed', () => {
          const center = circle.getCenter();
          if (center) {
            setMapCenter({ lat: center.lat(), lng: center.lng() });
          }
        });
      }
      
      // Convert saved results to the format expected by the map
      const convertedResults: Business[] = entry.results.companies.map(company => ({
        name: company.name,
        address: company.address,
        phone: company.phone,
        services: company.services || [],
        location: company.location,
        website: company.website,
        rating: company.rating,
        isEnriched: company.isEnriched,
        apollo_data: company.apollo_data,
        linkedin_url: company.linkedin_url,
        has_linkedin: company.has_linkedin
      }));
      
      // Set the search results
      setSearchResults(convertedResults);
      
      // Update the map markers
      if (mapRef.current) {
        // Clear existing markers
        searchResults.forEach(result => {
          if (result.marker) {
            result.marker.setMap(null);
          }
        });

        // Create new markers for the results
        convertedResults.forEach(result => {
          const marker = new google.maps.Marker({
            position: result.location,
            map: mapRef.current,
            title: result.name,
            animation: google.maps.Animation.DROP,
            icon: {
              url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
            }
          });

          // Add click listener to the marker
          marker.addListener('click', () => {
            setSelectedBusiness(result);
            mapRef.current?.panTo(result.location);
          });

          // Store the marker reference
          result.marker = marker;
        });

        // Pan to the search center after all markers are placed
        mapRef.current.panTo(entry.location);
      }
      
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to reload search');
    } finally {
      setIsReloading(false);
    }
  };

  // Add this function to group search history by time period
  const groupSearchHistoryByTime = (history: SearchHistory[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    const groups = {
      today: [] as SearchHistory[],
      yesterday: [] as SearchHistory[],
      thisWeek: [] as SearchHistory[],
      thisMonth: [] as SearchHistory[],
      older: [] as SearchHistory[]
    };

    history.forEach(entry => {
      const entryDate = new Date(entry.searchDate);
      
      if (entryDate >= today) {
        groups.today.push(entry);
      } else if (entryDate >= yesterday && entryDate < today) {
        groups.yesterday.push(entry);
      } else if (entryDate >= lastWeek) {
        groups.thisWeek.push(entry);
      } else if (entryDate >= lastMonth) {
        groups.thisMonth.push(entry);
      } else {
        groups.older.push(entry);
      }
    });

    return groups;
  };

  // (was: renderSearchHistoryTab — removed alongside the broken Recent Searches
  //  tab. Team-wide activity is now an in-map overlay; see the .team-activity-*
  //  classes and the GET /api/prospecting/team-activity endpoint.)

  // First, add the clear search function near the other handler functions
  const handleClearSearch = () => {
    setSearchTerms([]);
    setSearchResults([]);
    setCurrentInput('');
    setSelectedBusiness(null);
    setSearchPanelHidden(false);
  };

  // Full reset for the sidebar's × button. Unlike handleClearSearch (which the
  // SearchPanel uses to "start over with my search" but keeps the rep's
  // customer/pin context), this is "close everything I'm looking at" so it
  // also clears the customer + pin that drive sfAccountsNearby via the
  // selectedCustomer/customPin useEffect — otherwise the sidebar refuses to
  // close because sfAccountsNearby stays populated and the visibility check
  // (`searchResults.length > 0 || sfAccountsNearby.length > 0`) remains true.
  const handleCloseResultsSidebar = () => {
    handleClearSearch();
    setMultiBranchResults([]);
    setResultsFromCluster(false);
    setSelectedCustomer(null);
    setCustomPin(null);
    setCustomPinActive(false);
  };

  // Add function to calculate distance between two points
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lng2 - lng1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in km
    return distance;
  };
  
  const deg2rad = (deg: number): number => {
    return deg * (Math.PI/180);
  };
  
  // Apply filters to results
  // Merge nearby SF Accounts into the result set as Business-shaped cards
  // so the sidebar surfaces them too, not just the map. SF Accounts already
  // matched to a Places result (via salesforce_matches[].id) are skipped
  // to avoid double-listing the same business.
  // Memoized layers. Each layer is built from the layer above plus the
  // relevant filter / sort / group state. With 2,000+ rows in a TAM report,
  // call sites were re-running the full chain ~14× per render (no caching);
  // memoizing collapses that to one compute per layer, and hover-only
  // re-renders (which don't touch these deps) skip recomputation entirely.
  const combinedResults = useMemo((): Business[] => {
    const matchedSfIds = new Set<string>();
    for (const b of searchResults) {
      for (const m of (b.salesforce_matches || [])) matchedSfIds.add(m.id);
    }
    const sfOnly: Business[] = sfAccountsNearby
      .filter(a => !matchedSfIds.has(a.id))
      .map(a => ({
        name: a.name,
        address: a.address || '',
        website: a.website || '',
        location: { lat: a.lat, lng: a.lng },
        salesforce_status: 'in_sf',
        salesforce_matches: [{
          id: a.id,
          name: a.name,
          type: a.type,
          website: a.website || undefined,
          owner_id: a.owner_id,
          owner_name: a.owner_name,
          number_of_employees: a.number_of_employees ?? undefined,
          zoominfo_id: a.zoominfo_id ?? undefined,
          linkedin_url: a.linkedin_url ?? undefined,
        }],
      } as Business));
    return [...searchResults, ...sfOnly];
  }, [searchResults, sfAccountsNearby]);
  // Back-compat shim so existing `getCombinedResults()` call sites keep working
  // and return the memoized array. Recreating the thunk per render is free; its
  // identity doesn't matter to callers.
  const getCombinedResults = (): Business[] => combinedResults;

  // The disposition shown/filtered for a result: "Not in SF" when it's a net-new
  // prospect, else the matched Account.Type (New/Prospect/Nurture/Customer/…).
  // Defined ABOVE filteredResults because the useMemo below calls it — keeping
  // it after the memo triggered a temporal-dead-zone ReferenceError under the
  // 'Not in SF' filter path.
  const dispositionOf = (b: Business): string => {
    if (getAvailability(b, currentSfUserId) === 'add') return 'Not in SF';
    const m = pickDisplayMatch(b, currentSfUserId);
    return (m?.type || '').trim() || 'Unknown';
  };
  // The industry shown/filtered for a result. Prefer the Salesforce
  // Account.Industry on the matched account (set on SF-report rows); fall back
  // to Apollo enrichment industry for Google-search results. '' = no industry.
  const industryOf = (b: Business): string => {
    const m = pickDisplayMatch(b, currentSfUserId);
    return (m?.industry || '').trim() || (b.apollo_data?.industry || '').trim();
  };

  const filteredResults = useMemo((): Business[] => {
    // Default-on filters: drop existing Plix customers and require either a
    // website or an SF match. The user can disable them via the empty-state
    // "Show everything" button when these filters hide every result.
    const hideExistingCustomers = filters.hideExistingCustomers !== false;
    const requireWebsite = filters.requireWebsite !== false;
    // Hide DQ / DNC by default. Skipped automatically when the rep explicitly
    // ticks 'Disqualified' or 'Do Not Contact' in the Type multi-select — the
    // dispositions filter below restricts the set, so this guard would just be
    // redundant work and would suppress the very rows the rep asked for.
    const hasExplicitBlockedDisposition = (filters.dispositions || []).some(d =>
      SF_BLOCKED_TYPES.has(d.toLowerCase()),
    );
    const hideBlockedDispositions =
      filters.hideBlockedDispositions !== false && !hasExplicitBlockedDisposition;
    let results = combinedResults;
    if (hideExistingCustomers) results = results.filter(b => !b.is_existing_customer);
    if (requireWebsite) results = results.filter(b => !!b.website || b.salesforce_status === 'in_sf');
    if (hideBlockedDispositions) {
      results = results.filter(b => {
        // Keep in-flight rows so they don't flicker out then back in once the
        // SF lookup resolves.
        if (b.salesforce_status === undefined || b.salesforce_status === 'checking') return true;
        const m = pickDisplayMatch(b, currentSfUserId);
        const t = (m?.type || '').toLowerCase().trim();
        return !SF_BLOCKED_TYPES.has(t);
      });
    }

    // Defensive geo-filter: hard-bound to the search radius (10% slack so
    // edge-of-circle businesses don't bounce in/out). Catches stale cache
    // entries from before the backend's locationRestriction fix, and any
    // accidental cross-customer state leaks.
    //
    // Skip it for Team Activity clusters: those are an aggregated union of many
    // team searches across a region, not a single radius search, so bounding
    // them to searchRadius around the clicked point hides everything (and the
    // "Show everything" button can't recover them since it only resets the
    // user-facing filters).
    const searchCenter = selectedCustomer
      ? { lat: selectedCustomer.latitude || 0, lng: selectedCustomer.longitude || 0 }
      : customPin
        ? { lat: customPin.lat, lng: customPin.lng }
        : null;
    if (searchCenter && searchCenter.lat && searchCenter.lng && !resultsFromCluster) {
      const cutoffKm = (searchRadius * 1.1) / 1000;
      results = results.filter(b => {
        const loc = b.location;
        if (!loc || !loc.lat || !loc.lng) return true;  // unknown loc → keep
        return calculateDistance(searchCenter.lat, searchCenter.lng, loc.lat, loc.lng) <= cutoffKm;
      });
    }

    if (filters.minEmployees !== undefined) {
      results = results.filter(b => (b.apollo_data?.employee_count || 0) >= filters.minEmployees!);
    }
    if (filters.maxEmployees !== undefined) {
      results = results.filter(b => (b.apollo_data?.employee_count || 0) <= filters.maxEmployees!);
    }
    if (filters.searchText) {
      // Fast literal (substring) match — case-insensitive, no fuzzy ranking —
      // over the fields a rep would scan for: name, address, website.
      const text = filters.searchText.trim().toLowerCase();
      if (text) {
        results = results.filter(b =>
          b.name.toLowerCase().includes(text) ||
          b.address.toLowerCase().includes(text) ||
          (b.website || '').toLowerCase().includes(text)
        );
      }
    }
    if (filters.onlyNotInSf) {
      // Only net-new prospects not yet in Salesforce. Keep in-flight checks
      // visible so rows don't flicker out then back in.
      results = results.filter(b => {
        const a = getAvailability(b, currentSfUserId);
        return a === 'add' || a === 'checking';
      });
    }
    if (filters.dispositions && filters.dispositions.length > 0) {
      const allowed = new Set(filters.dispositions);
      results = results.filter(b => {
        // Keep rows still resolving so they don't flicker out then back in.
        if (b.salesforce_status === undefined || b.salesforce_status === 'checking') return true;
        return allowed.has(dispositionOf(b));
      });
    }
    if (filters.tiers && filters.tiers.length > 0) {
      const allowed = new Set(filters.tiers);
      results = results.filter(b => {
        if (b.salesforce_status === undefined || b.salesforce_status === 'checking') return true;
        // 'Not in SF' rows have no tier — treat as 'none'.
        const m = pickDisplayMatch(b, currentSfUserId);
        const tier = (m?.tier || '').trim();
        return allowed.has(tier === '' ? 'none' : tier);
      });
    }
    if (filters.industries && filters.industries.length > 0) {
      const allowed = new Set(filters.industries);
      results = results.filter(b => {
        const industry = industryOf(b);
        return allowed.has(industry === '' ? 'none' : industry);
      });
    }
    if (filters.ownership === 'open') {
      // Open = assigned to me (Johann). 'checking' rows stay visible so they
      // don't flicker out then back in once the SF lookup resolves.
      results = results.filter(b => {
        const a = getAvailability(b, currentSfUserId);
        return a === 'yours' || a === 'checking';
      });
    } else if (filters.ownership === 'assigned') {
      // Assigned = owned by another rep specifically. NOT the same as
      // "not-mine" — net-new prospects (`add`) and not-in-SF rows are
      // excluded, since they're not assigned to anyone.
      results = results.filter(b => {
        const a = getAvailability(b, currentSfUserId);
        return a === 'claimed' || a === 'checking';
      });
    }

    return results;
    // dispositionOf / industryOf / pickDisplayMatch / getAvailability are
    // intentionally NOT in deps: they're stable closures over the same state
    // already listed below (currentSfUserId), so re-including them would force
    // a re-run every render and defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedResults, filters, selectedCustomer, customPin, searchRadius, resultsFromCluster, currentSfUserId]);
  const getFilteredResults = (): Business[] => filteredResults;

  // dispositionOf + industryOf moved ABOVE filteredResults to fix a TDZ
  // ReferenceError when the rep filtered by 'Not in SF'.

  // Add function to sort search results
  const sortedResults = useMemo((): Business[] => {
    const filtered = filteredResults;

    // Get the search center coordinates
    const searchCenter = selectedCustomer
      ? { lat: selectedCustomer.latitude || 0, lng: selectedCustomer.longitude || 0 }
      : customPin
        ? { lat: customPin.lat, lng: customPin.lng }
        : { lat: 0, lng: 0 };

    const secondary = (a: Business, b: Business): number => {
      // Distance from the search center is the tiebreaker for every sort mode.
      return calculateDistance(searchCenter.lat, searchCenter.lng, a.location.lat, a.location.lng)
           - calculateDistance(searchCenter.lat, searchCenter.lng, b.location.lat, b.location.lng);
    };

    // Best available headcount for a result — prefer the SF match, fall back to
    // any Apollo enrichment, else 0.
    const employeesFor = (b: Business): number => {
      const sfMatch = pickDisplayMatch(b, currentSfUserId);
      return sfMatch?.number_of_employees || b.apollo_data?.employee_count || 0;
    };

    return [...filtered].sort((a, b) => {
      if (sortBy === 'employees') {
        const d = employeesFor(b) - employeesFor(a);
        return d !== 0 ? d : secondary(a, b);
      }
      if (sortBy === 'rating') {
        const d = (b.rating || 0) - (a.rating || 0);
        return d !== 0 ? d : secondary(a, b);
      }
      if (sortBy === 'priority') {
        // best to enrich first: most social links, then most reviews
        const d = socialLinkCount(b) - socialLinkCount(a);
        if (d !== 0) return d;
        const r = (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
        return r !== 0 ? r : secondary(a, b);
      }
      // reviews — most-reviewed first
      const d = (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
      return d !== 0 ? d : secondary(a, b);
    });
    // pickDisplayMatch is a stable closure over currentSfUserId (in deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredResults, sortBy, selectedCustomer, customPin, currentSfUserId]);
  const getSortedResults = (): Business[] => sortedResults;

  // Group results that share an EXACT website domain into one franchise card.
  // Name can differ between locations — domain is the grouping key. Results with
  // no website (or a unique domain) stay as singleton groups. Order follows the
  // sorted list (first occurrence wins); the first member is the card's primary.
  const groupedResults = useMemo((): Array<{ key: string; primary: Business; locations: Business[] }> => {
    const sorted = sortedResults;
    const byKey = new Map<string, Business[]>();
    const order: string[] = [];
    for (const b of sorted) {
      const domain = b.website ? normalizeDomainClient(b.website) : null;
      const key = domain ? `domain:${domain}` : `solo:${b.name}|${b.address}`;
      if (!byKey.has(key)) { byKey.set(key, []); order.push(key); }
      byKey.get(key)!.push(b);
    }
    return order.map(key => {
      const members = byKey.get(key)!;
      return { key, primary: members[0], locations: members };
    });
  }, [sortedResults]);
  const getGroupedResults = (): Array<{ key: string; primary: Business; locations: Business[] }> => groupedResults;

  const handleEnrichWithApollo = async () => {
    if (!searchResults.length) return;
    
    setIsEnrichingApollo(true);
    try {
      const enrichedCompanies = await enrichWithApollo(searchResults);
      if (enrichedCompanies) {
        setSearchResults(enrichedCompanies);
        updateHistoryWithEnrichment();
        toast.success('Successfully enriched results with Apollo data');
      }
    } catch (error) {
      console.error('Error enriching with Apollo:', error);
      toast.error('Failed to enrich results with Apollo data');
    } finally {
      setIsEnrichingApollo(false);
    }
  };



  if (loadError) {
    return (
      <Alert type="error">Error loading Google Maps: {loadError.message}</Alert>
    );
  }

  if (!isLoaded) {
    return <LoadingSpinner message="Loading Google Maps..." />;
  }

  if (loading) {
    return <LoadingSpinner message="Loading customers..." />;
  }

  if (error) {
    return (
      <Alert type="error">{error}</Alert>
    );
  }

  // Main component render
  return (
    <div className="prospecting-page">
      {sandboxMode && (
        <div className="sandbox-banner" role="status">
          <strong>[SANDBOX]</strong> Live Google Geocoding + Places calls are disabled in this environment.
          Searches will only return results that are already cached. Click a cached area below to load it,
          or set <code>PROSPECTING_SANDBOX=false</code> in your env to make real calls.
          {sandboxHints.length > 0 && (
            <div className="sandbox-hints">
              <span className="sandbox-hints-label">Cached areas:</span>
              {sandboxHints.slice(0, 12).map((h, i) => (
                <button
                  key={i}
                  className="sandbox-hint-chip"
                  onClick={() => {
                    // Drop a custom pin at the cached center + zoom + reopen the search panel
                    const pos = { lat: h.lat, lng: h.lng };
                    setCustomPin(pos);
                    setCustomPinActive(true);
                    setSelectedCustomer(null);
                    setMapCenter(pos);
                    setMapZoom(11);
                    setSearchPanelHidden(false);
                    if (mapRef.current) mapRef.current.panTo(pos);
                  }}
                  title={`Recenter on cached search: ${h.term} (${h.result_count} results, cached ${h.cached_at?.slice(0, 10)})`}
                >
                  {h.term} <span className="sandbox-hint-count">({h.result_count})</span>
                </button>
              ))}
              {sandboxHints.length > 12 && (
                <span className="sandbox-hint-more">+ {sandboxHints.length - 12} more</span>
              )}
            </div>
          )}
        </div>
      )}
      {statusMessage && (
        <div className="status-message">
          {statusMessage}
        </div>
      )}
      {/* Single-pane map view — broken "Recent Searches" tab removed; team
          activity now surfaces as a toggleable map overlay + right-side feed
          (see the "Team Activity" layer toggle and the .team-activity-sidebar). */}
      <div className="map-and-news">
          {/* News feed disabled — needs quality tuning before re-enabling
          {searchResults.length === 0 && (
            <div className="news-landing">
              <NewsFeed onProspectArea={handleProspectArea} onArticlesLoaded={setNewsArticles} onFilteredArticlesChanged={setFilteredNewsArticles} />
            </div>
          )}
          */}

          <div className="map-container">
            <div className="pin-drop-hint">Right click to drop pin</div>
            {/* Loaded-report chip — floats top-left while a report is on the
                map and the top bar is hidden. Surfaces the report name + the
                Backfill action + an × to clear and restore the top bar. */}
            {sfReportMeta && (
              <div
                className={`sf-report-chip${sfReportMeta.justRefreshed ? ' is-just-refreshed' : ''}`}
                title={sfReportMeta.name}
              >
                <FaFileAlt aria-hidden />
                <span className="sf-report-chip-name">{sfReportMeta.name}</span>
                <span className="sf-report-chip-stats">
                  {sfReportMeta.findingWebsites && sfReportMeta.enrichProgress ? (
                    // Live enrichment progress takes over the stats line while
                    // the stream is running — count + percent so the rep knows
                    // exactly how far along + how much is left.
                    <span className="sf-report-chip-enriching">
                      Enriching {sfReportMeta.enrichProgress.current}/{sfReportMeta.enrichProgress.total}
                      {' '}({Math.round((sfReportMeta.enrichProgress.current / Math.max(1, sfReportMeta.enrichProgress.total)) * 100)}%)
                    </span>
                  ) : (
                    <>
                      {sfReportMeta.plotted}
                      {sfReportMeta.no_address_count > 0 && ` · ${sfReportMeta.no_address_count} no addr`}
                      {' · Updated '}
                      {new Date(sfReportMeta.lastRefreshedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </>
                  )}
                </span>
                {sfReportMeta.missing.length > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={sfReportMeta.backfilling}
                    disabled={sfReportMeta.backfilling}
                    onClick={backfillReportGeocodes}
                    title={`Geocode ${sfReportMeta.missing.length} missing accounts via Google`}
                    className="sf-report-chip-backfill"
                  >
                    Backfill {sfReportMeta.missing.length}
                  </Button>
                )}
                {/* Full enrichment funnel — runs on every report row:
                      • Places lookup (rows missing a website)
                      • Homepage scrape → socials + email
                      • Liveness check → DQ dead/parked sites
                      • Security classifier → DQ camera-installer-only
                      • Non-destructive PATCH to SF
                    Rows that already have a website skip the Places call and
                    flow straight to the scrape + classifier stages. */}
                {(() => {
                  const reportRows = searchResults.filter(b => b.from_sf_report).length;
                  const missingSite = searchResults.filter(b => b.from_sf_report && !b.website).length;
                  if (reportRows === 0) return null;
                  const placesCost = (missingSite * 0.03).toFixed(2);
                  return (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={sfReportMeta.findingWebsites}
                      disabled={sfReportMeta.findingWebsites}
                      onClick={findReportWebsites}
                      title={
                        `Enrich all ${reportRows} report rows · ` +
                        `Places lookup for ${missingSite} missing websites (≈ $${placesCost}) + ` +
                        `homepage scrape for socials + liveness + classifier. ` +
                        `Writes back to SF where empty.`
                      }
                      className="sf-report-chip-backfill"
                    >
                      {sfReportMeta.findingWebsites && sfReportMeta.enrichProgress
                        ? `Enriching ${sfReportMeta.enrichProgress.current}/${sfReportMeta.enrichProgress.total}`
                        : `Enrich (${reportRows})`}
                    </Button>
                  );
                })()}
                {/* Force-refresh — re-pulls the report from SF so disposition /
                    tier changes made directly in Salesforce show up without
                    re-pasting the report URL. After a successful refresh the
                    icon swaps to a green ✓ for 1.6s so the rep gets explicit
                    "it worked" feedback even when the row count is unchanged. */}
                <button
                  type="button"
                  className={
                    `sf-report-chip-refresh${
                      sfReportMeta.refreshing || sfReportLoading ? ' is-spinning' : ''
                    }${sfReportMeta.justRefreshed ? ' is-just-refreshed' : ''}`
                  }
                  onClick={refreshSfReport}
                  disabled={sfReportMeta.refreshing || sfReportLoading}
                  aria-label={sfReportMeta.justRefreshed ? 'Just refreshed' : 'Refresh report from Salesforce'}
                  title={sfReportMeta.justRefreshed ? 'Refreshed just now' : 'Refresh report from Salesforce'}
                >
                  {sfReportMeta.justRefreshed
                    ? <FaCheck aria-hidden />
                    : <FaSync aria-hidden />}
                </button>
                <button
                  type="button"
                  className="sf-report-chip-close"
                  onClick={clearSfReport}
                  aria-label="Clear loaded report"
                  title="Clear loaded report (restores the top bar)"
                >×</button>
                {/* Thin live progress bar pinned to the bottom edge of the chip
                    while the enrichment stream runs. */}
                {sfReportMeta.findingWebsites && sfReportMeta.enrichProgress && (
                  <div className="sf-report-chip-progress" aria-hidden>
                    <div
                      className="sf-report-chip-progress-fill"
                      style={{ width: `${Math.round((sfReportMeta.enrichProgress.current / Math.max(1, sfReportMeta.enrichProgress.total)) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            {!sfReportMeta && (
            <div className="map-top-bar">
              <div className={`customer-quick-search${(reverseLookupLoading || sfReportLoading) ? ' is-loading' : ''}`}>
                {(reverseLookupLoading || sfReportLoading)
                  ? <FaSpinner className="customer-search-icon customer-search-spinner" aria-hidden />
                  : <FaSearch className="customer-search-icon" />}
                <input
                  // type="search" + the autocomplete/ignore attrs below stop
                  // Safari iCloud Passwords / Chrome / 1Password / LastPass from
                  // popping their autofill dropdown over this field — it's a
                  // jump/report box, never a login. `autoComplete="off"` alone is
                  // ignored by modern browsers, so we also pass the password-
                  // manager-specific opt-out data attributes + a non-login name.
                  type="search"
                  name="leadfinder-jump"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerSearch(true);
                    // Clear any inline report error as soon as they edit — they're
                    // about to try again with new content; the stale red box would
                    // just be noise at that point.
                    if (sfReportError) setSfReportError(null);
                  }}
                  onFocus={() => setShowCustomerSearch(true)}
                  onBlur={() => setTimeout(() => setShowCustomerSearch(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    const val = customerSearch.trim();
                    if (!val) return;
                    // SF Report Id (15 or 18 chars, starts with 00O). Works for
                    // a raw Id or a Lightning URL pasted in.
                    const m = val.match(/\b(00O[A-Za-z0-9]{12,15})\b/);
                    if (m) {
                      if (!sfReportLoading) { loadSfReport(customerSearch); setShowCustomerSearch(false); }
                      return;
                    }
                    // Otherwise Enter triggers a Google reverse lookup of the
                    // website/name typed.
                    if (!reverseLookupLoading) {
                      handleReverseLookup(val);
                      setShowCustomerSearch(false);
                    }
                  }}
                  placeholder="Jump to customer or paste SF report ID/URL…"
                  className={`customer-search-input${sfReportError ? ' has-error' : ''}`}
                  aria-invalid={sfReportError ? true : undefined}
                  disabled={sfReportLoading}
                />
                {sfReportError && (
                  <div className="customer-search-error" role="alert">
                    <span className="customer-search-error-icon" aria-hidden>⚠</span>
                    {sfReportError}
                  </div>
                )}
                {showCustomerSearch && customerSearch.trim() && (() => {
                  // Pattern-match first: an SF report Id swaps the dropdown
                  // contents to a single "Load report" action.
                  const reportMatch = customerSearch.match(/\b(00O[A-Za-z0-9]{12,15})\b/);
                  if (reportMatch) {
                    const reportId = reportMatch[1];
                    return (
                      <div className="customer-search-results">
                        <div
                          className="customer-search-result"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (sfReportLoading) return;
                            loadSfReport(customerSearch);
                            setShowCustomerSearch(false);
                          }}
                        >
                          <div className="customer-search-name">
                            {sfReportLoading ? 'Loading report…' : `Load Salesforce report ${reportId}`}
                          </div>
                          <div className="customer-search-meta">
                            Plots the report's accounts on the map
                          </div>
                        </div>
                      </div>
                    );
                  }
                  // Otherwise: customer matches + a persistent Google reverse-
                  // lookup action. The lookup row is always offered (it's the
                  // main path for a website/name that isn't an existing
                  // customer); customer matches list above it when present.
                  const q = customerSearch.toLowerCase();
                  const matches = customers
                    .filter(c =>
                      (c.name || '').toLowerCase().includes(q) ||
                      (c.description || '').toLowerCase().includes(q) ||
                      (c.city || '').toLowerCase().includes(q) ||
                      (c.state || '').toLowerCase().includes(q)
                    )
                    .slice(0, 6);
                  const looksUrl = /^(https?:\/\/|[\w.-]+\.[a-z]{2,})/i.test(customerSearch.trim());
                  const lookupLabel = reverseLookupLoading
                    ? 'Looking up…'
                    : looksUrl
                      ? `Find business at ${customerSearch.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '')}`
                      : `Look up “${customerSearch.trim()}” on Google`;
                  return (
                    <div className="customer-search-results">
                      <div
                        className="customer-search-result customer-search-lookup"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (reverseLookupLoading) return;
                          handleReverseLookup(customerSearch.trim());
                          setShowCustomerSearch(false);
                        }}
                      >
                        <div className="customer-search-name">🌐 {lookupLabel}</div>
                        <div className="customer-search-meta">
                          Find the business address, name &amp; SF status via Google
                        </div>
                      </div>
                      {matches.map(c => (
                        <div
                          key={c.id}
                          className="customer-search-result"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleCustomerClick(c);
                            setCustomerSearch('');
                            setShowCustomerSearch(false);
                          }}
                        >
                          <div className="customer-search-name">{c.description || c.name}</div>
                          <div className="customer-search-meta">{c.city}, {c.state}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* SF report input merged into the customer-quick-search above —
                  paste an Id or Lightning URL and the dropdown switches to a
                  "Load Salesforce report …" action. */}

              <div className="map-layer-toggle">
                <button
                  className={`layer-btn ${showCustomerPins ? 'active' : ''}`}
                  onClick={() => setShowCustomerPins(!showCustomerPins)}
                >
                  <FaMapMarkerAlt /> Customers
                </button>
                <button
                  className={`layer-btn ${showTeamActivity ? 'active' : ''}`}
                  onClick={() => setShowTeamActivity(!showTeamActivity)}
                  title="Toggle team-wide search activity overlay"
                >
                  🔥 Team Activity
                </button>
              </div>
            </div>
            )}

            {/* Team Activity feed — right-side sidebar, AoE-style action log.
                Visible when overlay is on AND user isn't running their own search
                (results sidebar takes priority for right-hand real estate). */}
            {showTeamActivity && searchResults.length === 0 && (
              <div className="team-activity-sidebar">
                <div className="team-activity-header">
                  <h2>🔥 Team Activity</h2>
                  <div className="team-activity-window">
                    {([1, 7, 30] as const).map(d => (
                      <button
                        key={d}
                        className={`win-btn ${teamActivityWindow === d ? 'active' : ''}`}
                        onClick={() => setTeamActivityWindow(d)}
                      >
                        {d === 1 ? '24h' : `${d}d`}
                      </button>
                    ))}
                  </div>
                </div>
                {teamActivity.clusters.length === 0 ? (
                  <div className="team-activity-empty">
                    No team activity in the selected window.
                  </div>
                ) : (
                  <div className="team-activity-feed">
                    {teamActivity.clusters.map((c, i) => {
                      const ageMs = Date.now() - c.latest_ts * 1000;
                      const ageMin = Math.round(ageMs / 60_000);
                      const ageStr = ageMin < 1 ? 'just now'
                                  : ageMin < 60 ? `${ageMin}m ago`
                                  : ageMin < 1440 ? `${Math.round(ageMin / 60)}h ago`
                                  : `${Math.round(ageMin / 1440)}d ago`;
                      // One sidebar entry = one cluster = one grid cell.
                      // Render only the top 2 terms; "+N more" if there are more.
                      const visibleTerms = c.top_terms.slice(0, 2);
                      const extraCount = c.top_terms.length - visibleTerms.length;
                      // Friendly vertical label for the header — from the
                      // backend list, so a new vertical never shows as "Other"
                      const verticalLabel =
                        verticals.find(v => v.id === c.top_vertical)?.name || 'Other';
                      return (
                        <div
                          key={i}
                          className={`team-activity-entry vertical-${c.top_vertical} ${hoveredClusterIdx === i ? 'highlighted' : ''}`}
                          onMouseEnter={() => setHoveredClusterIdx(i)}
                          onMouseLeave={() => setHoveredClusterIdx(prev => (prev === i ? null : prev))}
                          onClick={() => openTeamActivityCluster(c.lat, c.lng)}
                          title={`Click to load all ${c.search_count} cached searches for this area`}
                        >
                          <div className="team-activity-entry-header">
                            <span className="team-activity-vertical">{verticalLabel}</span>
                            <span className="team-activity-badge">{c.search_count} {c.search_count === 1 ? 'search' : 'searches'}</span>
                          </div>
                          <div className="team-activity-terms">
                            {visibleTerms.map((t, ti) => (
                              <span key={ti} className="team-activity-term-chip">{t.term}</span>
                            ))}
                            {extraCount > 0 && <span className="team-activity-term-more">+{extraCount} more</span>}
                          </div>
                          <div className="team-activity-meta">
                            {c.city || `${c.lat.toFixed(2)}, ${c.lng.toFixed(2)}`} · {ageStr}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {(searchResults.length > 0 || sfAccountsNearby.length > 0) && (
              <div className="results-sidebar">
                <div className="sidebar-header">
                  <h2>Results ({getFilteredResults().length})</h2>
                  <div className="sidebar-actions">
                    {resultsFromCluster && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSearchResults([]);
                          setMultiBranchResults([]);
                          setResultsFromCluster(false);
                          setCustomPin(null);
                          setCustomPinActive(false);
                          setSelectedCustomer(null);
                          setSearchPanelHidden(false);
                        }}
                        title="Return to the team activity map view"
                      >
                        ← Team Activity
                      </Button>
                    )}
                    {searchPanelHidden && !resultsFromCluster && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setSearchPanelHidden(false)}
                        title="Reopen search panel to refine"
                      >
                        Edit search
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      className="export-button"
                      onClick={handleExportResults}
                      disabled={searchResults.length === 0}
                      title="Export to CSV"
                    >
                      <FaFileDownload />
                    </Button>
                    <button
                      className="close-sidebar"
                      onClick={handleCloseResultsSidebar}
                      title="Close results and reset"
                    >
                      x
                    </button>
                  </div>
                </div>

                <>
                  {/* Auto-enrich progress */}
                  {autoEnrichProgress && (
                      <div className="enrichment-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${Math.round((autoEnrichProgress.current / autoEnrichProgress.total) * 100)}%` }}
                          ></div>
                        </div>
                        <div className="progress-text">
                          Enriching {autoEnrichProgress.current}/{autoEnrichProgress.total}...
                        </div>
                      </div>
                    )}

                    {isEnrichingApollo && (
                      <div className="enrichment-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${enrichmentProgress.progress}%` }}
                          ></div>
                        </div>
                        <div className="progress-text">
                          {enrichmentProgress.progress}% Complete
                        </div>
                      </div>
                    )}

                    {enrichmentError && (
                      <div className="enrichment-error">
                        {enrichmentError}
                      </div>
                    )}

                    {/* Quick toggles — both default OFF so the list stays
                     * focused on workable prospects; reps can opt-in to see
                     * everything when they want a wider view. */}
                    <div className="results-toggles">
                      {/* Fast client-side text filter across the loaded results.
                          Case-insensitive literal (substring) match — no fuzzy
                          ranking — over name, address and website. */}
                      <div className="results-search">
                        <FaSearch className="results-search-icon" aria-hidden />
                        <input
                          type="text"
                          className="results-search-input"
                          placeholder="Search results…"
                          value={filters.searchText || ''}
                          onChange={e => setFilters(f => ({ ...f, searchText: e.target.value || undefined }))}
                          aria-label="Search results"
                        />
                        {filters.searchText && (
                          <button
                            type="button"
                            className="results-search-clear"
                            onClick={() => setFilters(f => ({ ...f, searchText: undefined }))}
                            title="Clear search"
                            aria-label="Clear search"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                      <div className="results-toggle-group">
                      {/* Type (disposition) multi-select — check off multiple types,
                          commit on Apply. No selection = all types. */}
                      {(() => {
                        // Ticking Disqualified / Do Not Contact here also unhides
                        // them — getFilteredResults turns the default-hide off when
                        // either is in the selected dispositions. So this multi-select
                        // is the single rep-facing knob for blocked dispositions.
                        const TYPE_OPTIONS = ['Not in SF', 'New', 'Prospect', 'Customer', 'Disqualified', 'Do Not Contact'];
                        const selected = filters.dispositions || [];
                        const toggleDraft = (t: string) => setTypeDraft(d =>
                          d.includes(t) ? d.filter(x => x !== t) : [...d, t]);
                        return (
                          <div className="filter-dropdown" ref={typeDropdownRef}>
                            <button
                              type="button"
                              className={`results-toggle-chip${selected.length ? ' is-on' : ''}`}
                              onClick={() => { setTypeDraft(selected); setTypeMenuOpen(o => !o); }}
                              title="Filter by Account type"
                            >
                              Type{selected.length ? ` (${selected.length})` : ''} ▾
                            </button>
                            {typeMenuOpen && (
                              <div className="filter-popover" onClick={e => e.stopPropagation()}>
                                <label className="filter-popover-row">
                                  <input
                                    type="checkbox"
                                    checked={typeDraft.length === 0}
                                    onChange={() => setTypeDraft([])}
                                  />
                                  <span>All</span>
                                </label>
                                {TYPE_OPTIONS.map(t => (
                                  <label key={t} className="filter-popover-row">
                                    <input
                                      type="checkbox"
                                      checked={typeDraft.includes(t)}
                                      onChange={() => toggleDraft(t)}
                                    />
                                    <span>{t}</span>
                                  </label>
                                ))}
                                <div className="filter-popover-actions">
                                  <button type="button" className="filter-popover-cancel"
                                    onClick={() => setTypeMenuOpen(false)}>Cancel</button>
                                  <button type="button" className="filter-popover-apply"
                                    onClick={() => {
                                      setFilters(f => ({ ...f, dispositions: typeDraft.length ? typeDraft : undefined }));
                                      setTypeMenuOpen(false);
                                    }}>Apply</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {/* Tier multi-select — same shape as Type. 'none' bucket
                          catches matches with no Tier set so reps can isolate
                          tierless accounts as a batch to grade. */}
                      {(() => {
                        const TIER_OPTIONS: { value: string; label: string }[] = [
                          { value: '1', label: 'Tier 1' },
                          { value: '2', label: 'Tier 2' },
                          { value: '3', label: 'Tier 3' },
                          { value: 'none', label: 'No tier' },
                        ];
                        const selected = filters.tiers || [];
                        const toggleDraft = (t: string) => setTierDraft(d =>
                          d.includes(t) ? d.filter(x => x !== t) : [...d, t]);
                        return (
                          <div className="filter-dropdown" ref={tierDropdownRef}>
                            <button
                              type="button"
                              className={`results-toggle-chip${selected.length ? ' is-on' : ''}`}
                              onClick={() => { setTierDraft(selected); setTierMenuOpen(o => !o); }}
                              title="Filter by Account tier"
                            >
                              Tier{selected.length ? ` (${selected.length})` : ''} ▾
                            </button>
                            {tierMenuOpen && (
                              <div className="filter-popover" onClick={e => e.stopPropagation()}>
                                <label className="filter-popover-row">
                                  <input
                                    type="checkbox"
                                    checked={tierDraft.length === 0}
                                    onChange={() => setTierDraft([])}
                                  />
                                  <span>All</span>
                                </label>
                                {TIER_OPTIONS.map(({ value, label }) => (
                                  <label key={value} className="filter-popover-row">
                                    <input
                                      type="checkbox"
                                      checked={tierDraft.includes(value)}
                                      onChange={() => toggleDraft(value)}
                                    />
                                    <span>{label}</span>
                                  </label>
                                ))}
                                <div className="filter-popover-actions">
                                  <button type="button" className="filter-popover-cancel"
                                    onClick={() => setTierMenuOpen(false)}>Cancel</button>
                                  <button type="button" className="filter-popover-apply"
                                    onClick={() => {
                                      setFilters(f => ({ ...f, tiers: tierDraft.length ? tierDraft : undefined }));
                                      setTierMenuOpen(false);
                                    }}>Apply</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {/* Industry multi-select — options derived from the
                          enrichment industry present in the results. 'none'
                          buckets rows without industry data (unenriched). */}
                      {(() => {
                        const counts = new Map<string, number>();
                        let noneCount = 0;
                        getCombinedResults().forEach(b => {
                          const ind = industryOf(b);
                          if (ind === '') noneCount += 1;
                          else counts.set(ind, (counts.get(ind) || 0) + 1);
                        });
                        const INDUSTRY_OPTIONS: { value: string; label: string }[] = [
                          ...Array.from(counts.keys()).sort((a, b) => a.localeCompare(b))
                            .map(v => ({ value: v, label: v })),
                          ...(noneCount > 0 ? [{ value: 'none', label: 'No industry' }] : []),
                        ];
                        const selected = filters.industries || [];
                        const toggleDraft = (t: string) => setIndustryDraft(d =>
                          d.includes(t) ? d.filter(x => x !== t) : [...d, t]);
                        // Nothing to filter on yet (no enriched rows) — hide the chip.
                        if (INDUSTRY_OPTIONS.length === 0) return null;
                        return (
                          <div className="filter-dropdown" ref={industryDropdownRef}>
                            <button
                              type="button"
                              className={`results-toggle-chip${selected.length ? ' is-on' : ''}`}
                              onClick={() => { setIndustryDraft(selected); setIndustryMenuOpen(o => !o); }}
                              title="Filter by industry"
                            >
                              Industry{selected.length ? ` (${selected.length})` : ''} ▾
                            </button>
                            {industryMenuOpen && (
                              <div className="filter-popover filter-popover-scroll" onClick={e => e.stopPropagation()}>
                                <label className="filter-popover-row">
                                  <input
                                    type="checkbox"
                                    checked={industryDraft.length === 0}
                                    onChange={() => setIndustryDraft([])}
                                  />
                                  <span>All</span>
                                </label>
                                {INDUSTRY_OPTIONS.map(({ value, label }) => (
                                  <label key={value} className="filter-popover-row">
                                    <input
                                      type="checkbox"
                                      checked={industryDraft.includes(value)}
                                      onChange={() => toggleDraft(value)}
                                    />
                                    <span>{label}</span>
                                  </label>
                                ))}
                                <div className="filter-popover-actions">
                                  <button type="button" className="filter-popover-cancel"
                                    onClick={() => setIndustryMenuOpen(false)}>Cancel</button>
                                  <button type="button" className="filter-popover-apply"
                                    onClick={() => {
                                      setFilters(f => ({ ...f, industries: industryDraft.length ? industryDraft : undefined }));
                                      setIndustryMenuOpen(false);
                                    }}>Apply</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {/* Ownership in Salesforce — 3-state:
                          All      = no filter (every row)
                          Open     = SF Account assigned to me (Johann) — my book
                          Assigned = SF Account assigned to another rep (don't poach) */}
                      <label className="results-sort">
                        Ownership
                        <select
                          value={filters.ownership || 'all'}
                          onChange={e => setFilters(f => ({
                            ...f,
                            ownership: e.target.value === 'all' ? undefined : (e.target.value as 'open' | 'assigned'),
                          }))}
                        >
                          <option value="all">All</option>
                          <option value="open">Open</option>
                          <option value="assigned">Assigned</option>
                        </select>
                      </label>
                      {/* Cluster pins toggle — off by default. When on, dense
                          marker stacks (NJ/NY in the screenshot) collapse into
                          neutral count bubbles; click to zoom-and-fit. */}
                      <button
                        type="button"
                        className={`results-toggle-chip${clusteringEnabled ? ' is-on' : ''}`}
                        onClick={() => setClusteringEnabled(v => !v)}
                        title="Group nearby pins into clusters at low zoom"
                      >
                        Cluster: {clusteringEnabled ? 'On' : 'Off'}
                      </button>
                      <ColorLegendTrigger />
                      </div>
                      <label className="results-sort">
                        Sort
                        <select
                          value={sortBy}
                          onChange={e => setSortBy(e.target.value as LeadSortKey)}
                        >
                          <option value="priority">Priority</option>
                          <option value="reviews">Reviews</option>
                          <option value="rating">Rating</option>
                          <option value="employees">Employees</option>
                        </select>
                      </label>
                      {(() => {
                        // Select-all toggles every "Not in SF" franchise card shown
                        // (one key per group primary — only those have checkboxes).
                        const addableKeys = getGroupedResults()
                          .map(g => g.primary)
                          .filter(b => getAvailability(b, currentSfUserId) === 'add')
                          .map(b => `${b.name}|${b.address}`);
                        if (addableKeys.length === 0) return null;
                        const allSelected = addableKeys.every(k => bulkSelectedKeys.has(k));
                        return (
                          <button
                            type="button"
                            className="results-toggle-chip"
                            onClick={() => {
                              const next = new Set(bulkSelectedKeys);
                              if (allSelected) {
                                addableKeys.forEach(k => next.delete(k));
                              } else {
                                addableKeys.forEach(k => next.add(k));
                              }
                              setBulkSelectedKeys(next);
                            }}
                            title="Select/deselect all addable accounts shown"
                          >
                            {allSelected ? 'Deselect all' : `Select all (${addableKeys.length})`}
                          </button>
                        );
                      })()}
                    </div>

                    <div className="results-list">
                      {/* In-flight top-bar search: render a card-shaped
                          placeholder above the (empty) results so the rep sees
                          exactly what's happening — and not a half-populated
                          card that flashes in then re-renders three times as
                          background effects complete. */}
                      {activeLookup && (
                        <div className="lookup-placeholder" role="status" aria-live="polite">
                          <div className="lookup-placeholder-spinner" aria-hidden />
                          <div className="lookup-placeholder-body">
                            <div className="lookup-placeholder-query">{activeLookup.query}</div>
                            <div className="lookup-placeholder-step">{activeLookup.step}</div>
                          </div>
                        </div>
                      )}
                      {getSortedResults().length === 0 && !activeLookup && (() => {
                        // Filters wiped out every result — explain WHY each one was hidden so
                        // the user can decide whether to relax filters, search elsewhere, or
                        // just acknowledge that this metro is well-worked.
                        const total = searchResults.length;
                        const existingCount = searchResults.filter(b => b.is_existing_customer).length;
                        const noWebsiteCount = searchResults.filter(
                          b => !b.is_existing_customer && !b.website && b.salesforce_status !== 'in_sf',
                        ).length;
                        const filterBlockedCount = total - existingCount - noWebsiteCount - getSortedResults().length;
                        const activeFilterLabels: string[] = [];
                        if (filters.ownership === 'open') activeFilterLabels.push('Ownership: Open');
                        else if (filters.ownership === 'assigned') activeFilterLabels.push('Ownership: Assigned');
                        if (filters.onlyNotInSf) activeFilterLabels.push('Not in SF only');
                        if (filters.minEmployees !== undefined) activeFilterLabels.push(`Employees ≥ ${filters.minEmployees}`);
                        if (filters.maxEmployees !== undefined) activeFilterLabels.push(`Employees ≤ ${filters.maxEmployees}`);
                        if (filters.searchText) activeFilterLabels.push(`Search "${filters.searchText}"`);
                        if (filters.industries && filters.industries.length > 0) activeFilterLabels.push(`Industry: ${filters.industries.length} selected`);
                        const reasonLines: string[] = [];
                        if (existingCount)     reasonLines.push(`${existingCount} already a Plix customer`);
                        if (noWebsiteCount)    reasonLines.push(`${noWebsiteCount} no website on file`);
                        if (filterBlockedCount > 0) reasonLines.push(`${filterBlockedCount} hidden by your active filters`);
                        return (
                          <div className="empty-state">
                            <div style={{ marginBottom: 12 }}>
                              All {total} result{total === 1 ? '' : 's'} are hidden:
                            </div>
                            <ul style={{ marginBottom: 12, paddingLeft: 18, textAlign: 'left', fontSize: 'var(--font-size-sm)' }}>
                              {reasonLines.map((r, i) => (<li key={i}>{r}</li>))}
                            </ul>
                            {activeFilterLabels.length > 0 && (
                              <div style={{ marginBottom: 16, fontSize: 'var(--font-size-sm)' }}>
                                Active: {activeFilterLabels.join(' · ')}
                              </div>
                            )}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setFilters({
                                hideExistingCustomers: false,
                                requireWebsite: false,
                                hideBlockedDispositions: false,
                              })}
                            >
                              Show everything ({total})
                            </Button>
                          </div>
                        );
                      })()}
                      {getGroupedResults().map((group, index) => {
                        const business = group.primary;
                        const locations = group.locations;
                        const isFranchise = locations.length > 1;
                        const groupExpanded = expandedGroupKey === group.key;
                        const sfAvailability = getAvailability(business, currentSfUserId);
                        const sfMatch = pickDisplayMatch(business, currentSfUserId);
                        const hoverKey = `${business.name}|${business.address}`;
                        return (
                        <div
                          key={index}
                          ref={(el) => { resultRefsMap.current.set(hoverKey, el); }}
                          className={`result-item sf-${sfAvailability} ${selectedBusiness === business ? 'selected' : ''} ${business.is_existing_customer ? 'existing-customer' : ''} ${hoveredBusinessKey === hoverKey ? 'hovered' : ''}`}
                          onClick={() => handleBusinessClick(business)}
                          onMouseEnter={() => { hoverSourceRef.current = 'sidebar'; setHoveredBusinessKey(hoverKey); }}
                          onMouseLeave={() => { hoverSourceRef.current = null; setHoveredBusinessKey(prev => (prev === hoverKey ? null : prev)); }}
                        >
                          <div className="result-item-header">
                            {sfAvailability === 'add' && (
                              <input
                                type="checkbox"
                                className="result-item-bulk-check"
                                checked={bulkSelectedKeys.has(hoverKey)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const next = new Set(bulkSelectedKeys);
                                  if (e.target.checked) next.add(hoverKey); else next.delete(hoverKey);
                                  setBulkSelectedKeys(next);
                                }}
                                aria-label={`Select ${business.name} for bulk add to Salesforce`}
                                title="Include in bulk add to Salesforce"
                              />
                            )}
                            <h3>
                              {business.name}
                              {isFranchise && (
                                <span className="franchise-badge" title="Same website — grouped as one franchise">
                                  {locations.length} locations
                                </span>
                              )}
                            </h3>
                            <div className="result-item-badges">
                              {sfAvailability === 'yours' && (
                                <span className="sf-badge sf-badge-yours" title={`In Salesforce — assigned to ${sfMatch?.owner_name || 'you'}`}>Open</span>
                              )}
                              {sfAvailability === 'claimed' && sfMatch?.owner_name && (
                                <span className="sf-badge sf-badge-claimed" title={`In Salesforce — assigned to ${sfMatch.owner_name}`}>
                                  {sfMatch.owner_name}
                                </span>
                              )}
                              {sfAvailability === 'add' && (
                                <span className="sf-badge sf-badge-add" title="Not in Salesforce yet">Not in SF</span>
                              )}
                              {sfAvailability === 'checking' && (
                                <span className="sf-badge sf-badge-checking" title="Checking Salesforce…">SF…</span>
                              )}
                              {business.is_existing_customer && (
                                <span
                                  className="existing-customer-badge"
                                  title={business.matched_customer_tag || business.matched_customer_name
                                    ? `Matches customer: ${business.matched_customer_tag || business.matched_customer_name}`
                                    : undefined}
                                >Existing Customer</span>
                              )}
                              {/* Google Places business_status — only render the unhappy paths.
                                  OPERATIONAL is the implicit healthy default and showing it
                                  for every row would be noise. */}
                              {business.business_status === 'CLOSED_PERMANENTLY' && (
                                <span className="status-badge status-badge--closed-permanent"
                                  title="Google: permanently closed">
                                  ⚠ Closed permanently
                                </span>
                              )}
                              {business.business_status === 'CLOSED_TEMPORARILY' && (
                                <span className="status-badge status-badge--closed-temp"
                                  title="Google: temporarily closed">
                                  Closed temporarily
                                </span>
                              )}
                              {(() => {
                                // Each social prefers the value scraped from the homepage,
                                // then falls back to whatever the SF Account has stored.
                                // Website goes first so the order on the card matches the
                                // visit-flow the rep follows: site → LinkedIn → socials.
                                const socials: { url: string; icon: React.ReactElement; cls: string; title: string }[] = [];
                                const siteUrl = business.website ? (externalUrl(business.website) || business.website) : null;
                                if (siteUrl) {
                                  const analyzing = sfAvailability === 'add' && !business.website_checked;
                                  socials.push({
                                    url: siteUrl,
                                    // ~58% of the 24px tile — a hair smaller than the chip
                                    // so the white background frames the glyph as breathing
                                    // room, mirroring the inset feel of the FA brand icons.
                                    icon: <FiGlobe size={22} strokeWidth={2.25} />,
                                    cls: 'social-badge website',
                                    title: business.website_dead
                                      ? `Website (dead — ${business.website_dead_phrase || 'unreachable'})`
                                      : analyzing
                                        ? 'Website (analyzing… click to open anyway)'
                                        : 'Open website',
                                  });
                                }
                                const li = business.linkedin_url || sfMatch?.linkedin_url;
                                if (li) socials.push({ url: li, icon: <FaLinkedin />, cls: 'social-badge linkedin', title: 'LinkedIn' });
                                const ig = business.instagram_url || sfMatch?.instagram_url;
                                if (ig) socials.push({ url: ig, icon: <FaInstagram />, cls: 'social-badge instagram', title: 'Instagram' });
                                const fb = business.facebook_url || sfMatch?.facebook_url;
                                if (fb) socials.push({ url: fb, icon: <FaFacebook />, cls: 'social-badge facebook', title: 'Facebook' });
                                const xu = business.x_url || sfMatch?.x_url;
                                if (xu) socials.push({ url: xu, icon: <FaTwitter />, cls: 'social-badge x', title: 'X (Twitter)' });
                                const yl = business.yelp_url || sfMatch?.yelp_url;
                                if (yl) socials.push({ url: yl, icon: <FaYelp />, cls: 'social-badge yelp', title: 'Yelp' });
                                const em = business.company_email || sfMatch?.company_email;
                                // Open in a new TAB (not a popup window) but try to keep focus
                                // on Summit — mimic the right-click "open in new tab" flow as
                                // closely as the platform allows. Browsers reserve true
                                // background-tab opening for actual user gestures (Ctrl/Cmd-
                                // click, middle-click), so we approximate by opening with
                                // window.open then immediately blurring the new window /
                                // focusing this one. Modifier-click and middle-click fall
                                // through to the anchor's native behavior (which already
                                // backgrounds correctly), so power users keep the gold-standard
                                // path. Plain links (target=_blank) stay on the anchor as a
                                // last-resort fallback if window.open is blocked.
                                const openKeepFocus = (url: string) => (e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  // Honor the user's own modifier/middle-click intent.
                                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                  e.preventDefault();
                                  const win = window.open(url, '_blank', 'noopener,noreferrer');
                                  if (!win) return; // popup blocker — anchor would have fallen back
                                  try { win.blur(); } catch { /* noop */ }
                                  try { window.focus(); } catch { /* noop */ }
                                };
                                return (
                                  <>
                                    {socials.map((s, i) => (
                                      <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                                        className={s.cls} title={s.title}
                                        onClick={openKeepFocus(s.url)}>
                                        {s.icon}
                                      </a>
                                    ))}
                                    {em && (
                                      <a href={`mailto:${em}`} className="social-badge email" title={em}
                                        onClick={(e) => e.stopPropagation()}>
                                        <FaEnvelope />
                                      </a>
                                    )}
                                  </>
                                );
                              })()}
                              <a
                                href={
                                  sfMatch?.zoominfo_id
                                    ? `https://app.zoominfo.com/#/apps/main/profile/company/${sfMatch.zoominfo_id}`
                                    : `https://www.zoominfo.com/companies-search?keyword=${encodeURIComponent(business.name)}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="zoominfo-badge"
                                onClick={(e) => e.stopPropagation()}
                                title={sfMatch?.zoominfo_id ? 'Open ZoomInfo profile' : 'Find on ZoomInfo'}
                              >
                                ZI
                              </a>
                              {business.phone && (
                                <a
                                  href={`tel:${business.phone.replace(/[^\d+]/g, '')}`}
                                  className="phone-badge"
                                  onClick={(e) => e.stopPropagation()}
                                  title={`Call ${business.phone}`}
                                >
                                  <FaPhoneAlt aria-hidden />
                                  <span>{business.phone}</span>
                                </a>
                              )}
                              {/* Site photos — lazy-loaded on first click, opens a lightbox.
                                  Distinct states give immediate feedback: loading spinner,
                                  count when ready, "No photos" / "Retry" otherwise. */}
                              {business.website && !business.website_dead && (() => {
                                const loaded = business.site_images_loaded;
                                const count = business.site_images?.length ?? 0;
                                const loading = business.site_images_loading;
                                const error = business.site_images_error;

                                if (loading) {
                                  return (
                                    <span className="photos-badge photos-badge-loading" title="Loading photos…">
                                      <FaSpinner className="photos-badge-spin" aria-hidden /> Loading…
                                    </span>
                                  );
                                }
                                if (error) {
                                  return (
                                    <button
                                      type="button"
                                      className="photos-badge photos-badge-error"
                                      onClick={(e) => { e.stopPropagation(); openSitePhotos(business, true); }}
                                      title="Couldn’t load photos — click to retry"
                                    >
                                      <FaExclamationTriangle aria-hidden /> Retry
                                    </button>
                                  );
                                }
                                if (loaded && count === 0) {
                                  return (
                                    <button
                                      type="button"
                                      className="photos-badge photos-badge-empty"
                                      onClick={(e) => { e.stopPropagation(); openSitePhotos(business, true); }}
                                      title="No photos found on this site — click to retry"
                                    >
                                      <FaCamera aria-hidden /> No photos
                                    </button>
                                  );
                                }
                                if (loaded && count > 0) {
                                  return (
                                    <button
                                      type="button"
                                      className="photos-badge photos-badge-ready"
                                      onClick={(e) => { e.stopPropagation(); openSitePhotos(business); }}
                                      title={`View ${count} photos from this site`}
                                    >
                                      <FaCamera aria-hidden /> Photos ({count})
                                    </button>
                                  );
                                }
                                return (
                                  <button
                                    type="button"
                                    className="photos-badge"
                                    onClick={(e) => { e.stopPropagation(); openSitePhotos(business); }}
                                    title="Load photos from this site"
                                  >
                                    <FaCamera aria-hidden /> Photos
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                          {/* Guard with explicit > 0 so a falsy 0 doesn't render the
                              literal "0" via React's `value && <JSX>` quirk. */}
                          {((business.rating ?? 0) > 0 || business.last_review_age_days !== undefined) && (
                            <p className="result-rating-line">
                              {(business.rating ?? 0) > 0 && (
                                <>
                                  Rating: {business.rating}
                                  {(business.user_ratings_total ?? 0) > 0 && (
                                    <span className="result-review-count">
                                      {' '}({business.user_ratings_total} {business.user_ratings_total === 1 ? 'review' : 'reviews'})
                                    </span>
                                  )}
                                </>
                              )}
                              {business.last_review_age_days !== undefined && business.last_review_age_days !== null && (() => {
                                const d = business.last_review_age_days;
                                // Format: "today" / "5d ago" / "3w ago" / "8mo ago" / "2y ago".
                                // Stale (>365d) gets a muted color so reps see "OPERATIONAL but
                                // hasn't been reviewed since 2022" without having to do mental
                                // date math.
                                let s: string;
                                if (d < 1) s = 'today';
                                else if (d < 30) s = `${d}d ago`;
                                else if (d < 90) s = `${Math.round(d/7)}w ago`;
                                else if (d < 365) s = `${Math.round(d/30)}mo ago`;
                                else s = `${(d/365).toFixed(1)}y ago`;
                                const stale = d > 365;
                                return (
                                  <span
                                    className={`result-last-review${stale ? ' result-last-review--stale' : ''}`}
                                    title={`Most recent Google review · ${d} days ago`}
                                  >
                                    {(business.rating ?? 0) > 0 ? ' · ' : ''}last review {s}
                                  </span>
                                );
                              })()}
                            </p>
                          )}
                          {business.website && (
                            <p className="result-website">
                              <a
                                href={externalUrl(business.website) || business.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title={business.website}
                              >
                                {shortenWebsiteDisplay(business.website)}
                              </a>
                              {business.website_dead && (
                                <span className="result-website-dead" title={business.website_dead_phrase || 'website defunct'}>
                                  {' '}· dead
                                </span>
                              )}
                              {!business.website_dead && business.website_redirect_to && (
                                <span className="result-website-redirect" title={`Redirects off-domain to ${business.website_redirect_to}`}>
                                  {' '}· → {business.website_redirect_to}
                                </span>
                              )}
                            </p>
                          )}
                          {/* Address extracted from the homepage footer/JSON-LD.
                              Shown when (a) we got one AND (b) it doesn't already
                              match the Places address (no point repeating). When
                              the rep clicks Add to SF, this is what populates
                              Billing — so surfacing it builds trust in what'll
                              get written. */}
                          {business.extracted_address &&
                            !(business.address || '').toLowerCase().includes((business.extracted_address.split(',')[0] || '').toLowerCase()) && (
                            <p className="result-extracted-address" title="Address extracted from the company website — used to populate Salesforce Billing when adding">
                              <FaMapMarkerAlt aria-hidden /> {business.extracted_address}
                            </p>
                          )}
                          {/* Locations + Find-more controls. Always render
                              the row when there's a website to search by —
                              even single-location cards get the "+ Find more
                              nationwide" affordance (the new locations merge
                              into this card via the inherited domain key).
                              The Show/Hide locations button only renders for
                              actual multi-location cards. */}
                          {(isFranchise || !!business.website) && (
                            <div className="franchise-locations">
                              <div className="franchise-toggle-row">
                                {isFranchise ? (
                                  <button
                                    type="button"
                                    className="franchise-toggle"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedGroupKey(groupExpanded ? null : group.key);
                                    }}
                                  >
                                    {groupExpanded ? 'Hide' : 'Show'} {locations.length} locations
                                  </button>
                                ) : (
                                  <span className="franchise-single-hint" aria-hidden>
                                    1 location
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="franchise-find-more"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMultiBranchSearch(business);
                                  }}
                                  disabled={multiBranchLoading === business.name}
                                  title={`Search Google Places nationwide for more "${business.name}" locations and add them to this card`}
                                >
                                  {multiBranchLoading === business.name ? 'Searching…' : '+ Find more nationwide'}
                                </button>
                              </div>
                              {groupExpanded && (
                                <ul className="franchise-location-list">
                                  {locations.map((loc, li) => (
                                    <li key={`${loc.name}|${loc.address}|${li}`} className="franchise-location">
                                      <span className="franchise-location-name">{loc.name}</span>
                                      <span className="franchise-location-addr">{loc.address}</span>
                                      {(loc.rating ?? 0) > 0 && (
                                        <span className="franchise-location-rating">
                                          {loc.rating}★{(loc.user_ratings_total ?? 0) > 0 ? ` (${loc.user_ratings_total})` : ''}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                          {sfMatch?.number_of_employees != null && (
                            <p className="sf-employee-count">
                              <span className="apollo-data-label">SF Employees:</span>
                              {sfMatch.number_of_employees.toLocaleString()}
                            </p>
                          )}

                          {/* Apollo data section */}
                          {business.apollo_data && (
                            <div className="apollo-data">
                              {(business.apollo_data.employee_count ?? 0) > 0 && (
                                <p>
                                  <span className="apollo-data-label">Employees:</span>
                                  {business.apollo_data.employee_count!.toLocaleString()}
                                </p>
                              )}
                              {business.apollo_data.annual_revenue && Number(String(business.apollo_data.annual_revenue).replace(/[^0-9.]/g, '')) > 0 && (
                                <p>
                                  <span className="apollo-data-label">Revenue:</span>
                                  ${business.apollo_data.annual_revenue.toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}

                          {/* SF action row — editing chips only (DQ / View in SF /
                              Disposition / Tier). The Visit Website button moved
                              up to the social chip row next to LinkedIn. */}
                          <div className="business-actions">
                            {/* Salesforce actions — depend on the SF availability bucket */}
                            {sfAvailability === 'add' && business.website_dead && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDqDeadSite(business);
                                }}
                                title={`Create as Disqualified — ${business.website_dead_phrase || 'website defunct'}`}
                              >
                                DQ (dead site)
                              </Button>
                            )}
                            {sfAvailability === 'add' && business.website && !business.isEnriched && (
                              <Button
                                variant="secondary"
                                size="sm"
                                loading={!!business.isEnriching}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (business.isEnriching) return;
                                  enrichSingleBusiness(business);
                                }}
                                title={business.enrichmentError
                                  ? `Last attempt failed: ${business.enrichmentError}. Click to retry.`
                                  : 'Pull firmographics from Waterfall, then open the Add-to-Salesforce modal'}
                              >
                                {business.isEnriching
                                  ? 'Enriching…'
                                  : business.enrichmentError ? 'Retry enrich' : 'Enrich + Add to SF'}
                              </Button>
                            )}
                            {sfAvailability === 'add' && !business.isEnriched && !business.isEnriching && business.enrichmentError && (
                              <span className="enriched-badge enriched-badge--error" title={business.enrichmentError}>
                                ✕ Enrich failed
                              </span>
                            )}
                            {sfAvailability === 'add' && business.isEnriched && (() => {
                              // Show what enrichment actually returned, not just that it ran.
                              // Waterfall fills any subset of {employee_count, industry,
                              // annual_revenue, linkedin_url} — show each as its own chip.
                              // When nothing was found, render a muted "no data" badge so the
                              // rep knows the call succeeded but came back empty (vs. error).
                              const ap = business.apollo_data;
                              const li = ap?.linkedin_url || business.linkedin_url;
                              const chips: { label: string; title?: string }[] = [];
                              if (ap?.employee_count) chips.push({ label: `${ap.employee_count} emp`, title: `Employee count: ${ap.employee_count}` });
                              if (ap?.industry) chips.push({ label: ap.industry, title: `Industry: ${ap.industry}` });
                              if (ap?.annual_revenue) chips.push({ label: `$${ap.annual_revenue}`, title: `Annual revenue: ${ap.annual_revenue}` });
                              if (li) chips.push({ label: 'LinkedIn', title: li });
                              if (chips.length === 0) {
                                return (
                                  <span
                                    className="enriched-badge enriched-badge--empty"
                                    title="Waterfall returned no firmographics for this domain"
                                  >
                                    ✓ Enriched — no data
                                  </span>
                                );
                              }
                              return (
                                <span
                                  className="enriched-badge"
                                  title={`Pulled from Waterfall: ${chips.map(c => c.label).join(' · ')}`}
                                >
                                  ✓
                                  {chips.map((c, i) => (
                                    <span
                                      key={i}
                                      className="enriched-chip"
                                      title={c.title}
                                    >
                                      {c.label}
                                    </span>
                                  ))}
                                </span>
                              );
                            })()}
                            {sfAvailability === 'add' && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddSfBusiness(business);
                                }}
                                title="Create this Account in Salesforce"
                              >
                                + Add to Salesforce
                              </Button>
                            )}
                            {(sfAvailability === 'yours' || sfAvailability === 'claimed') && sfMatch && (
                              <>
                                {/* Order: Disposition | Tier | View in SF →
                                    Reads left-to-right as "what state is it in /
                                    how strong is it / open it" — the actual rep
                                    workflow when triaging. */}
                                {(() => {
                                  const current = sfMatch.type || '';
                                  const inOptions = (SF_ACCOUNT_TYPE_OPTIONS as readonly string[]).includes(current);
                                  return (
                                    <label
                                      className="disposition-edit"
                                      data-type={current}
                                      onClick={(e) => e.stopPropagation()}
                                      title={current === 'Customer'
                                        ? 'Customer is set by the Net ACV workflow — switch to DQ or DNC only'
                                        : 'Edit disposition in Salesforce'}
                                    >
                                      <span className="disposition-edit-label">Disposition:</span>
                                      <select
                                        className="disposition-edit-select"
                                        value={inOptions ? current : ''}
                                        disabled={!sfMatch.id || sfMatch.id === 'pending'}
                                        onChange={(e) => handleInlineDispositionChange(
                                          business, sfMatch, e.target.value as SfAccountType,
                                        )}
                                      >
                                        {!inOptions && (
                                          <option value="" disabled>{current || '—'}</option>
                                        )}
                                        {SF_ACCOUNT_TYPE_OPTIONS.map(t => (
                                          <option key={t} value={t}>{t}</option>
                                        ))}
                                      </select>
                                    </label>
                                  );
                                })()}
                                <label
                                  className="tier-edit"
                                  data-tier={sfMatch.tier || ''}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Edit Tier in Salesforce"
                                >
                                  <span className="tier-edit-label">Tier:</span>
                                  <select
                                    className="tier-edit-select"
                                    value={sfMatch.tier || ''}
                                    disabled={!sfMatch.id || sfMatch.id === 'pending'}
                                    onChange={(e) => handleInlineTierChange(business, sfMatch, e.target.value)}
                                  >
                                    <option value="">—</option>
                                    {SF_TIER_OPTIONS.map(t => (
                                      <option key={t} value={t}>Tier {t}</option>
                                    ))}
                                  </select>
                                </label>
                                {(() => {
                                  const saving = !sfMatch.id || sfMatch.id === 'pending';
                                  return (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      loading={saving}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (saving) return;
                                        window.open(`https://plix.my.salesforce.com/${sfMatch.id}`, '_blank', 'noopener,noreferrer');
                                      }}
                                      title={saving ? 'Saving to Salesforce…' : 'Open this Account in Salesforce'}
                                    >
                                      {saving ? 'Saving…' : 'View in SF →'}
                                    </Button>
                                  );
                                })()}
                              </>
                            )}
                          </div>

                          {business.enrichmentError && (
                            <div className="enrichment-error">
                              {business.enrichmentError}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </>
              </div>
            )}

            <GoogleMap
              mapContainerClassName="map"
              // Never hand Maps an invalid center — setCenter(NaN/null) throws
              // inside Maps during commit (cross-origin "Script error.").
              center={isValidLatLng(mapCenter) ? mapCenter : { lat: 39.5, lng: -98.35 }}
              zoom={mapZoom}
              options={{
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false
              }}
              onClick={handleMapClick}
              onRightClick={handleMapRightClick}
              onLoad={(map) => {
                mapRef.current = map;
                // Fit to customers only if at least one has real coordinates —
                // fitBounds() on an EMPTY LatLngBounds throws inside Maps, which
                // surfaces as a cross-origin "Script error." during commit.
                const bounds = new google.maps.LatLngBounds();
                let pinned = 0;
                customers.forEach(customer => {
                  if (isValidLatLng({ lat: customer.latitude, lng: customer.longitude })) {
                    bounds.extend({ lat: customer.latitude as number, lng: customer.longitude as number });
                    pinned++;
                  }
                });
                if (pinned > 0) {
                  map.fitBounds(bounds);
                  const c = bounds.getCenter();
                  setMapCenter({ lat: c.lat(), lng: c.lng() });
                }
              }}
              onUnmount={() => {
                mapRef.current = null;
              }}
            >
              {/* Team Activity overlay — AoE-style cluster pins.
                  Hidden when the user has their own search results to focus on. */}
              {showTeamActivity && searchResults.length === 0 && teamActivity.clusters.map((c, i) => {
                // Skip clusters without real coordinates — OverlayViewF feeds the
                // position straight into new google.maps.LatLng() during draw(),
                // which throws inside Maps (cross-origin "Script error.") on commit.
                if (!isValidLatLng(teamClusterPositions[i])) return null;
                // Visual scaling — log-ish so a 25-search cluster doesn't dwarf a 2-search one.
                const size = Math.min(70, Math.max(22, 18 + Math.log2(1 + c.search_count) * 9));
                const ageMs = Date.now() - c.latest_ts * 1000;
                const ageHrs = ageMs / 3_600_000;
                // Fade older clusters; bright = recent (within 24h)
                const opacity = ageHrs < 1 ? 1.0 : ageHrs < 6 ? 0.92 : ageHrs < 24 ? 0.78 : ageHrs < 72 ? 0.55 : 0.35;
                // Pulse class: 'hot' = activity in last hour, 'warm' = last 24h
                const pulseClass = ageHrs < 1 ? 'hot' : ageHrs < 24 ? 'warm' : '';
                return (
                  // OverlayViewF (functional) writes left/top synchronously to
                  // the DOM inside draw(); the class OverlayView routes every
                  // reposition through React setState, so during a zoom
                  // animation the pin lags the map transform and visibly drifts
                  // off its coordinate before snapping back. See PR notes.
                  <OverlayViewF
                    key={`team-cluster-${i}`}
                    position={teamClusterPositions[i]}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
                  >
                    <div
                      className={`team-cluster-pin vertical-${c.top_vertical} ${pulseClass} ${hoveredClusterIdx === i ? 'hovered' : ''}`}
                      style={{ width: size, height: size, opacity }}
                      title={`${c.search_count} team searches  ·  top: ${c.top_terms[0]?.term || ''}`}
                      onMouseEnter={() => setHoveredClusterIdx(i)}
                      onMouseLeave={() => setHoveredClusterIdx(prev => (prev === i ? null : prev))}
                      onClick={() => openTeamActivityCluster(c.lat, c.lng)}
                    >
                      <span className="team-cluster-count">{c.search_count}</span>
                    </div>
                  </OverlayViewF>
                );
              })}

              {/* Hide existing-customer pins whenever the map is showing search
                  results or an imported SF report — both views already encode
                  current customers (yellow markers / Customer-type green) and
                  the global blue customer layer just adds visual noise on top. */}
              {showCustomerPins && searchResults.length === 0 && !sfReportMeta && customers.map((customer) => (
                customer.latitude && customer.longitude && (
                  <Marker
                    key={customer.id}
                    position={{ lat: customer.latitude, lng: customer.longitude }}
                    onClick={() => handleCustomerClick(customer)}
                    icon={{
                      url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                    }}
                    // Keep customer pins above search-result markers so
                    // selecting a customer never lands on a covered pin.
                    zIndex={1000}
                  />
                )
              ))}

              {/* Name the customer behind the pin you just clicked. This existed
                  until 7578820 ("fixes", Apr 2025) as a bottom-panel header and
                  was dropped in the Lead Finder overhaul, leaving a click that
                  drew a search circle around a customer it never named.

                  Gated on the same condition as the pins themselves, so the
                  popup is never left floating over a hidden layer. */}
              {selectedCustomer && !pinInfoDismissed && showCustomerPins
                && searchResults.length === 0 && !sfReportMeta
                && selectedCustomer.latitude && selectedCustomer.longitude && (
                <InfoWindow
                  position={{ lat: selectedCustomer.latitude, lng: selectedCustomer.longitude }}
                  onCloseClick={() => setPinInfoDismissed(true)}
                >
                  <div className="info-window">
                    {/* Same label the customer dropdown and SearchPanel use, so
                        the popup names them the way the rep just picked them. */}
                    <h3>{customerLabel(selectedCustomer)}</h3>
                    {/* The tag is what Summit files them under and for ~a third
                        of customers it is a different company name entirely --
                        the part a rep needs to look them up. Suppressed when it
                        only repeats the label. */}
                    {differentName(selectedCustomer.id, customerLabel(selectedCustomer)) && (
                      <p className="info-window-customer">Customer tag: {selectedCustomer.id}</p>
                    )}
                    {differentName(selectedCustomer.name, customerLabel(selectedCustomer)) && (
                      <p>Billed as: {selectedCustomer.name}</p>
                    )}
                    <p>{selectedCustomer.address}</p>
                    <p>{selectedCustomer.city}, {selectedCustomer.state} {selectedCustomer.zip}</p>
                    {/* account_executive carries a sentinel rather than being
                        absent when unset, so an emptiness check is not enough. */}
                    {selectedCustomer.account_executive
                      && selectedCustomer.account_executive !== 'Not Assigned' && (
                      <p className="info-window-ae">AE: {selectedCustomer.account_executive}</p>
                    )}
                  </div>
                </InfoWindow>
              )}

              {/* SF Accounts are merged into the unified search-result marker
                  loop above via getCombinedResults() — no separate layer.
                  Their pin color reflects ownership (green=yours, red=claimed)
                  and they participate in hover-linking with the sidebar. */}

              {/* News article markers - grouped by location with count */}
              {showNewsPins && (() => {
                const mapNews = filteredNewsArticles.length > 0 ? filteredNewsArticles : newsArticles;
                const clusters = new Map<string, { lat: number; lng: number; articles: NewsArticle[] }>();
                mapNews.forEach(a => {
                  if (!a.location?.lat || !a.location?.lng) return;
                  const key = `${a.location.lat.toFixed(2)},${a.location.lng.toFixed(2)}`;
                  if (!clusters.has(key)) {
                    clusters.set(key, { lat: a.location.lat, lng: a.location.lng, articles: [] });
                  }
                  clusters.get(key)!.articles.push(a);
                });
                return Array.from(clusters.entries()).map(([key, cluster]) => (
                  <Marker
                    key={`news-${key}`}
                    position={{ lat: cluster.lat, lng: cluster.lng }}
                    onClick={() => {
                      setSelectedNewsCluster(cluster);
                      setSelectedBusiness(null);
                    }}
                    label={cluster.articles.length > 1 ? {
                      text: String(cluster.articles.length),
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    } : undefined}
                    icon={{
                      url: cluster.articles.some(a => a.severity === 'BREAKING')
                        ? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                        : cluster.articles.some(a => a.severity === 'HIGH')
                        ? 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png'
                        : 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
                    }}
                    title={cluster.articles.length === 1
                      ? cluster.articles[0].headline
                      : `${cluster.articles.length} articles near ${cluster.articles[0].location?.city || 'this area'}`
                    }
                  />
                ));
              })()}

              {selectedNewsCluster && (
                <InfoWindow
                  position={{ lat: selectedNewsCluster.lat, lng: selectedNewsCluster.lng }}
                  onCloseClick={() => setSelectedNewsCluster(null)}
                >
                  <div className="news-info-window">
                    {selectedNewsCluster.articles.map((article, i) => (
                      <div key={i} className="news-info-article">
                        <div className="news-info-meta">
                          <span className={`news-severity news-severity-${(article.severity || 'MEDIUM').toLowerCase()}`}>
                            {article.severity}
                          </span>
                          <span className="news-category-badge">{article.category}</span>
                          <span className="news-date">{article.date}</span>
                        </div>
                        <a
                          href={article.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="news-info-headline"
                        >
                          {article.headline}
                        </a>
                        <p className="news-info-summary">{article.summary}</p>
                        <button
                          className="news-prospect-btn"
                          onClick={() => handleProspectArea(
                            { lat: article.location.lat, lng: article.location.lng },
                            article.suggestedTerms
                          )}
                        >
                          <FaCrosshairs /> Prospect Area
                        </button>
                        {i < selectedNewsCluster.articles.length - 1 && <hr />}
                      </div>
                    ))}
                  </div>
                </InfoWindow>
              )}

              {/* Result markers. Two render paths — flat or clustered — chosen
                  by the Cluster toggle in the sidebar.
                  PERF NOTES for the clustered path:
                  - The hovered marker is NOT pulled out of the cluster list
                    (that recomputed clusters on every hover, which is what
                    made it feel slow). Instead a separate overlay marker is
                    rendered at zIndex 9999 on top — the cluster list stays
                    referentially stable so MarkerClusterer doesn't re-index.
                  - Icon objects are pulled from getMarkerIcon's cache so
                    MarkerF's prop-equality keeps the underlying google.maps.Marker
                    alive across renders.
                  - MarkerF (memoized) is used instead of the class Marker. */}
              {!showHeatmap && (() => {
                const filteredAll = getFilteredResults()
                  .filter((b) => isValidLatLng(b.location));
                const hovered = hoveredBusinessKey
                  ? filteredAll.find(
                      (b) => `${b.name}|${b.address}` === hoveredBusinessKey,
                    )
                  : null;

                // Single palette across cards AND markers so the sidebar and
                // the map tell the same color story. Same hex values as the
                // .result-item.sf-* / .existing-customer CSS rules. Order
                // matters: existing-customer wins over ownership state.
                const colorFor = (business: Business): string => {
                  if (business.is_existing_customer) return '#eab308';
                  const av = getAvailability(business, currentSfUserId);
                  if (av === 'yours') return '#10b981';
                  if (av === 'add') return '#ec4899';
                  if (av === 'claimed') return '#ef4444';
                  return '#94a3b8'; // slate-400 default (checking / no_website)
                };

                const renderMarker = (
                  business: Business,
                  index: number,
                  clusterer?: any,
                ) => {
                  const hoverKey = `${business.name}|${business.address}`;
                  // In the clustered path we never grow the in-cluster marker
                  // — the overlay below handles the lift. Keeps icons stable
                  // → MarkerF prop-equality holds → no marker churn on hover.
                  const isHovered = !clusteringEnabled && hoveredBusinessKey === hoverKey;
                  return (
                    <MarkerF
                      key={`business-${index}-${hoverKey}`}
                      position={business.location}
                      clusterer={clusterer}
                      onClick={() => handleBusinessClick(business)}
                      onMouseOver={() => {
                        hoverSourceRef.current = 'map';
                        setHoveredBusinessKey(hoverKey);
                      }}
                      onMouseOut={() => {
                        hoverSourceRef.current = null;
                        setHoveredBusinessKey((prev) =>
                          prev === hoverKey ? null : prev,
                        );
                      }}
                      icon={getMarkerIcon(
                        colorFor(business),
                        isHovered,
                        !!business.is_existing_customer,
                      )}
                      zIndex={isHovered ? 9999 : 600}
                    />
                  );
                };

                // Hover-lift overlay used by the clustered path. Rendered as a
                // sibling at z=9999 so the rep always sees the highlighted pin
                // even when its neighbors are bundled into a count bubble. The
                // original (in-cluster) marker stays put under it.
                const hoverOverlay = clusteringEnabled && hovered ? (
                  <MarkerF
                    key={`business-hovered-${hovered.name}|${hovered.address}`}
                    position={hovered.location}
                    onClick={() => handleBusinessClick(hovered)}
                    icon={getMarkerIcon(
                      colorFor(hovered),
                      true,
                      !!hovered.is_existing_customer,
                    )}
                    zIndex={9999}
                  />
                ) : null;

                if (clusteringEnabled) {
                  return (
                    <>
                      <MarkerClustererF
                        averageCenter
                        minimumClusterSize={3}
                        gridSize={80}
                        styles={CLUSTER_STYLES}
                      >
                        {(clusterer) => (
                          <>{filteredAll.map((b, i) => renderMarker(b, i, clusterer))}</>
                        )}
                      </MarkerClustererF>
                      {hoverOverlay}
                    </>
                  );
                }
                // Flat (unclustered) path — original behavior.
                return <>{filteredAll.map((b, i) => renderMarker(b, i))}</>;
              })()}

              {/* Multi-branch markers (purple) */}
              {multiBranchResults.map((branch, index) => (
                isValidLatLng(branch.location) ? (
                  <Marker
                    key={`branch-${index}`}
                    position={branch.location}
                    icon={{
                      url: 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png'
                    }}
                    title={`${branch.name} - ${branch.address}`}
                  />
                ) : null
              ))}

              {selectedBusiness && isValidLatLng(selectedBusiness.location) && (
                <InfoWindow
                  position={selectedBusiness.location}
                  onCloseClick={() => setSelectedBusiness(null)}
                >
                  <div className="info-window">
                    <h3>{selectedBusiness.name}</h3>
                    {/* A yellow pin says "this is a customer" but not which one.
                        Name the tag when it differs from the business name —
                        that's the string you'd search Summit for. */}
                    {selectedBusiness.is_existing_customer && (
                      <p className="info-window-customer">
                        {customerReference(selectedBusiness)
                          ? `Customer: ${customerReference(selectedBusiness)}`
                          : 'Existing Plix customer'}
                      </p>
                    )}
                    <p>{selectedBusiness.address}</p>
                    {selectedBusiness.rating && <p>Rating: {selectedBusiness.rating}</p>}
                    {selectedBusiness.website && (
                      <a
                        href={externalUrl(selectedBusiness.website) || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website"
                      >
                        Visit Website
                      </a>
                    )}
                  </div>
                </InfoWindow>
              )}

              {customPin && customPinActive && (
                <Marker
                  position={customPin}
                  draggable
                  onDragEnd={(e) => {
                    if (!e.latLng) return;
                    const next = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                    setCustomPin(next);
                    if (circleRef.current) {
                      circleRef.current.setCenter(next);
                    }
                  }}
                />
              )}
            </GoogleMap>
          </div>

          {(selectedCustomer || customPinActive) && !searchPanelHidden && (
            <SearchPanel
              mode={selectedCustomer ? 'customer' : 'pin'}
              customerName={selectedCustomer?.description || selectedCustomer?.name}
              customerAddress={selectedCustomer ? `${selectedCustomer.address}, ${selectedCustomer.city}, ${selectedCustomer.state} ${selectedCustomer.zip}` : undefined}
              accountExecutive={selectedCustomer?.account_executive}
              pinLat={customPin?.lat}
              pinLng={customPin?.lng}
              selectedVertical={selectedVertical}
              verticals={verticals}
              onVerticalChange={handleVerticalChange}
              searchTerms={searchTerms}
              currentInput={currentInput}
              onSearchTermsChange={setSearchTerms}
              onCurrentInputChange={setCurrentInput}
              searchRadius={searchRadius}
              isCircleDragging={isCircleDragging}
              isSearching={isSearching}
              onSearch={handleSearchDispatch}
              onClear={handleClearSearch}
              savedSearches={savedSearches}
              showSavedSearchesDropdown={showSavedSearchesDropdown}
              onToggleSavedSearches={() => setShowSavedSearchesDropdown(!showSavedSearchesDropdown)}
              onLoadSavedSearch={(search) => {
                setSearchTerms([...search.searchTerms]);
                setCurrentInput('');
                setShowSavedSearchesDropdown(false);
              }}
              onDeletePin={handleDeletePin}
              searchTermsUsed={searchTermsUsed}
              searchingTerm={searchingTerm}
              searchProgress={searchProgress}
            />
          )}
        </div>

      {/* Floating bulk-select action bar — shown whenever 1+ "Not in SF" rows are checked. */}
      {bulkSelectedKeys.size > 0 && !bulkAddOpen && (
        <div className="bulk-action-bar" role="region" aria-label="Bulk selection actions">
          <span className="bulk-action-bar-count">{bulkSelectedKeys.size} selected</span>
          <button
            type="button"
            className="bulk-action-bar-clear"
            onClick={() => setBulkSelectedKeys(new Set())}
            disabled={bulkEnriching}
          >
            Clear
          </button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setBulkAddOpen(true)}
            disabled={bulkEnriching}
            title="Skip enrichment and go straight to the bulk add modal"
          >
            Add Selected to Salesforce
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={bulkEnriching}
            onClick={enrichAndBulkAdd}
            disabled={bulkEnriching}
            title="Pull firmographics from Waterfall for every selected row, then open the Add-to-Salesforce modal with the data prefilled"
          >
            {bulkEnriching
              ? `Enriching ${bulkSelectedKeys.size}…`
              : `Enrich + Add ${bulkSelectedKeys.size} to Salesforce`}
          </Button>
        </div>
      )}

      {bulkAddOpen && (() => {
        // Resolve selected keys against the current results so the modal only
        // ever sees Businesses still in the list (stale keys from a prior
        // search are silently dropped).
        // Compute groupings ONCE for the bulk run, then build a per-business
        // lookup of sibling locations so multi-location franchise rows carry
        // their other branches into AE Notes (matches the single-add modal).
        // getGroupedResults can be expensive; calling it inside the .map below
        // would be O(N²).
        const groups = getGroupedResults();
        const otherLocsByKey = new Map<string, ReturnType<typeof getGroupedResults>[number]['locations']>();
        for (const g of groups) {
          for (const member of g.locations) {
            const memberKey = `${member.name}|${member.address}`;
            otherLocsByKey.set(memberKey, g.locations.filter(l => l !== member));
          }
        }
        const selectedBiz: BulkAddBusiness[] = searchResults
          .filter(b => bulkSelectedKeys.has(`${b.name}|${b.address}`))
          .map(b => {
            const bizKey = `${b.name}|${b.address}`;
            const siblings = otherLocsByKey.get(bizKey) || [];
            return {
              key: bizKey,
              name: b.name,
              website: b.website,
              phone: b.phone,
              address: b.address,
              location: b.location,
              rating: b.rating,
              user_ratings_total: b.user_ratings_total,
              linkedin_url: b.linkedin_url || (b.salesforce_matches?.[0]?.linkedin_url ?? undefined),
              instagram_url: b.instagram_url || (b.salesforce_matches?.[0]?.instagram_url ?? undefined),
              facebook_url: b.facebook_url || (b.salesforce_matches?.[0]?.facebook_url ?? undefined),
              x_url: b.x_url || (b.salesforce_matches?.[0]?.x_url ?? undefined),
              yelp_url: b.yelp_url || (b.salesforce_matches?.[0]?.yelp_url ?? undefined),
              company_email: b.company_email || (b.salesforce_matches?.[0]?.company_email ?? undefined),
              // Waterfall headcount (when the row was pre-enriched). Flows
              // through the modal into each per-row bulk-create payload so the
              // SF NumberOfEmployees gets written on insert.
              employee_count: b.apollo_data?.employee_count ?? undefined,
              // Google Places signals — flow into the AE Notes prefill (last
              // review recency + business_status flag for closed shops).
              last_review_age_days: b.last_review_age_days,
              business_status: b.business_status,
              // Sibling locations from the same franchise card. Empty for
              // single-location rows — buildFranchiseAeNotes() degrades to
              // the plain prefill in that case.
              other_locations: siblings.map(l => ({
                name: l.name,
                address: l.address,
                phone: l.phone,
                rating: l.rating,
                user_ratings_total: l.user_ratings_total,
              })),
            };
          });
        return (
          <BulkAddToSalesforceModal
            businesses={selectedBiz}
            onClose={() => setBulkAddOpen(false)}
            onSubmit={(rows) => {
              // Snapshot the selected Business objects so we can do optimistic
              // updates and rollback by reference — selectedBiz holds only the
              // narrowed shape, so we re-resolve from searchResults by key.
              const targetsByKey = new Map<string, Business>();
              for (const b of searchResults) {
                const k = `${b.name}|${b.address}`;
                if (bulkSelectedKeys.has(k)) targetsByKey.set(k, b);
              }

              // Optimistic UI: rows the rep marked DQ/DNC vanish from the list;
              // everything else flips to in_sf with a 'pending' SF id (overwritten
              // when the bulk POST returns).
              setSearchResults(prev => {
                let next = [...prev];
                rows.forEach((r, i) => {
                  const target = targetsByKey.get(selectedBiz[i]?.key || '');
                  if (!target) return;
                  if (SF_BLOCKED_TYPES.has(r.type.toLowerCase())) {
                    next = next.filter(b => b !== target);
                  } else {
                    next = next.map(b => b === target ? {
                      ...b,
                      salesforce_status: 'in_sf',
                      salesforce_matches: [{
                        id: 'pending',
                        name: r.name,
                        website: r.website,
                        owner_id: currentSfUserId || undefined,
                        owner_name: 'Johann Hauswald',
                        type: r.type,
                      }],
                    } : b);
                  }
                });
                return next;
              });

              const requestPromise = fetch(
                `${API_BASE_URL}/api/prospecting/salesforce/create-accounts-bulk`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ accounts: rows }),
                },
              ).then(async (resp) => {
                const payload = await resp.json();
                if (!resp.ok) throw new Error(payload.error || `Bulk create failed (${resp.status})`);
                return payload as { created: number; matched: number; failed: number; results: Array<{ index: number; id?: string; url?: string; error?: string }> };
              });

              toast.promise(requestPromise, {
                loading: `Adding ${rows.length} accounts to Salesforce…`,
                success: (result) => {
                  // Patch optimistic 'pending' ids with real ones row-by-row.
                  setSearchResults(prev => prev.map(b => {
                    if (b.salesforce_matches?.[0]?.id !== 'pending') return b;
                    const k = `${b.name}|${b.address}`;
                    const idx = selectedBiz.findIndex(s => s.key === k);
                    if (idx < 0) return b;
                    const r = result.results.find(x => x.index === idx);
                    if (!r || !r.id) return b;
                    return {
                      ...b,
                      salesforce_matches: (b.salesforce_matches || []).map((m, i) =>
                        i === 0 ? { ...m, id: r.id! } : m,
                      ),
                    };
                  }));
                  setBulkSelectedKeys(new Set());
                  const parts = [`${result.created} created`];
                  if (result.matched) parts.push(`${result.matched} already in SF`);
                  if (result.failed) parts.push(`${result.failed} failed`);
                  const label = `Bulk add: ${parts.join(', ')}`;
                  // One created → deep-link to that record. Several → link to the
                  // Salesforce Accounts list (recently viewed) so the rep can open
                  // them without spawning a tab per account.
                  const createdUrls = result.results
                    .filter(r => r.id && r.url)
                    .map(r => r.url as string);
                  if (createdUrls.length === 1) {
                    return sfToastWithLink(label, createdUrls[0]);
                  }
                  if (createdUrls.length > 1) {
                    let listUrl: string | undefined;
                    try {
                      listUrl = `${new URL(createdUrls[0]).origin}/lightning/o/Account/list?filterName=Recent`;
                    } catch { /* leave undefined → plain label */ }
                    return sfToastWithLink(label, listUrl);
                  }
                  return label;
                },
                error: (err) => {
                  // Roll back every optimistic row on failure.
                  setSearchResults(prev => prev.map(b => {
                    if (b.salesforce_matches?.[0]?.id === 'pending') {
                      return { ...b, salesforce_status: 'not_in_sf', salesforce_matches: [] };
                    }
                    return b;
                  }));
                  return `Bulk add failed: ${err instanceof Error ? err.message : 'unknown error'}`;
                },
              }, { success: { duration: 8000 } });
            }}
          />
        );
      })()}

      {lightboxBusiness && (lightboxBusiness.site_images?.length ?? 0) > 0 && (
        <PhotoLightbox
          title={lightboxBusiness.name}
          website={lightboxBusiness.website}
          images={lightboxBusiness.site_images || []}
          onClose={() => setLightboxBusiness(null)}
        />
      )}
      {addSfBusiness && (() => {
        // Multi-location franchise card: find the sibling locations so they
        // can flow into the AE Notes prefill. Single-location cards return
        // [] here, so the modal's prefill stays unchanged from the original.
        const group = getGroupedResults().find(g => g.primary === addSfBusiness)
                    ?? getGroupedResults().find(g => g.locations.includes(addSfBusiness));
        const otherLocations = group
          ? group.locations
              .filter(l => l !== addSfBusiness)
              .map(l => ({
                name: l.name,
                address: l.address,
                phone: l.phone,
                rating: l.rating,
                user_ratings_total: l.user_ratings_total,
              }))
          : [];
        return (
        <AddToSalesforceModal
          business={addSfBusiness}
          otherLocations={otherLocations}
          ownerName="Johann Hauswald"
          onClose={() => setAddSfBusiness(null)}
          onSubmit={(form: AddToSalesforcePayload) => {
            // Capture the Business reference from parent state so the closures
            // below get the full row object (modal only knows the narrower
            // AddToSalesforceBusiness shape).
            const target = addSfBusiness;
            if (!target) return;
            // Match rows by a stable key, NOT object reference: background work
            // (SF check, LinkedIn/phone backfill) rebuilds Business objects in
            // the 1–3s before the POST returns, so a reference check would miss
            // the row and leave the optimistic id stuck at 'pending' (→ a bad
            // "View in SF" URL). The key survives those rebuilds.
            const targetKey = `${target.name}|${target.address}`;
            // Optimistically flip the row to 'in_sf' / 'yours' immediately so
            // the UI feels instant. If the user marked it Disqualified / Do
            // Not Contact, drop it instead. The real SF id is patched in once
            // the background request returns.
            const blocked = SF_BLOCKED_TYPES.has(form.type.toLowerCase());
            if (blocked) {
              setSearchResults(prev => prev.filter(b => `${b.name}|${b.address}` !== targetKey));
            } else {
              setSearchResults(prev => prev.map(b => {
                if (`${b.name}|${b.address}` !== targetKey) return b;
                return {
                  ...b,
                  salesforce_status: 'in_sf',
                  salesforce_matches: [{
                    id: 'pending',  // overwritten when the POST returns
                    name: form.name,
                    website: form.website,
                    owner_id: currentSfUserId || undefined,
                    owner_name: 'Johann Hauswald',
                    type: form.type,
                  }],
                };
              }));
            }

            const requestPromise = fetch(`${API_BASE_URL}/api/prospecting/salesforce/create-account`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // Pass enrichment if the rep already clicked Enrich; otherwise the
              // server enriches on add so the new Account still gets firmographics.
              body: JSON.stringify({ ...form, employee_count: target.apollo_data?.employee_count }),
            }).then(async (resp) => {
              const payload = await resp.json();
              // Duplicate-rule alert (409, structured) — hand off to the
              // DuplicateAlertModal instead of dying with a generic toast. We
              // still throw so toast.promise rolls back the optimistic row.
              if (resp.status === 409 && payload?.error === 'duplicates_detected') {
                setDupAlert({
                  alert: payload as DuplicateAlertPayload,
                  form, target, targetKey, saving: false,
                });
                const err = new Error('Possible duplicate — see alert');
                (err as Error & { isDuplicateAlert?: boolean }).isDuplicateAlert = true;
                throw err;
              }
              if (!resp.ok) throw new Error(payload.error || `Salesforce create failed (${resp.status})`);
              return payload as AddToSalesforceResult;
            });

            toast.promise(requestPromise, {
              loading: `Creating ${form.name} in Salesforce…`,
              success: (result) => {
                if (!blocked) {
                  // Patch the real SF id onto the optimistic row (matched by key).
                  setSearchResults(prev => prev.map(b => {
                    if (`${b.name}|${b.address}` !== targetKey) return b;
                    return {
                      ...b,
                      salesforce_matches: (b.salesforce_matches || []).map((m, i) =>
                        i === 0 ? { ...m, id: result.id } : m,
                      ),
                    };
                  }));
                }
                const label = result.matched_existing
                  ? `Already in Salesforce (owned by ${result.existing_owner_name || 'another rep'})`
                  : blocked
                    ? `Created in Salesforce — hidden as ${form.type}`
                    : `Created in Salesforce`;
                return sfToastWithLink(label, result.url);
              },
              error: (err) => {
                // Duplicate-alert path: roll back silently — the
                // DuplicateAlertModal already explains the situation, a generic
                // toast on top would be noise.
                const isDup = !!(err as { isDuplicateAlert?: boolean })?.isDuplicateAlert;
                // Roll back the optimistic update on failure (matched by key).
                if (blocked) {
                  setSearchResults(prev =>
                    prev.some(b => `${b.name}|${b.address}` === targetKey) ? prev : [...prev, target],
                  );
                } else {
                  setSearchResults(prev => prev.map(b => {
                    if (`${b.name}|${b.address}` !== targetKey) return b;
                    return { ...b, salesforce_status: 'not_in_sf', salesforce_matches: [] };
                  }));
                }
                if (isDup) {
                  // toast.promise requires a string here; return empty so the
                  // toast itself is effectively dismissed and the modal owns the UX.
                  return '';
                }
                return `Failed to create ${form.name}: ${err instanceof Error ? err.message : 'unknown error'}`;
              },
            }, { success: { duration: 8000 } });
          }}
        />
        );
      })()}
      {dupAlert && (
        <DuplicateAlertModal
          alert={dupAlert.alert}
          attemptedName={dupAlert.form.name}
          attemptedWebsite={dupAlert.form.website}
          saving={dupAlert.saving}
          onCancel={() => setDupAlert(null)}
          onCreateAnyway={() => {
            // Re-POST the same form with allow_save=true. The backend appends
            // Sforce-Duplicate-Rule-Header: allowSave=true so Alert-mode rules
            // pass; Block-mode rules will still 409 (and the modal warns about
            // that case via the `blocked` banner).
            setDupAlert(prev => prev && { ...prev, saving: true });
            const dup = dupAlert;
            // Re-apply optimistic update for the row (same key-based pattern
            // as the original create flow).
            setSearchResults(prev => prev.map(b => {
              if (`${b.name}|${b.address}` !== dup.targetKey) return b;
              return {
                ...b,
                salesforce_status: 'in_sf',
                salesforce_matches: [{
                  id: 'pending', name: dup.form.name, website: dup.form.website,
                  owner_id: currentSfUserId || undefined,
                  owner_name: 'Johann Hauswald', type: dup.form.type,
                }],
              };
            }));
            const retry = fetch(`${API_BASE_URL}/api/prospecting/salesforce/create-account`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...dup.form, allow_save: true,
                employee_count: dup.target?.apollo_data?.employee_count }),
            }).then(async (resp) => {
              const payload = await resp.json();
              if (!resp.ok) throw new Error(payload.error || `Salesforce create failed (${resp.status})`);
              return payload as AddToSalesforceResult;
            });
            toast.promise(retry, {
              loading: `Creating ${dup.form.name} anyway…`,
              success: (result) => {
                setSearchResults(prev => prev.map(b => {
                  if (`${b.name}|${b.address}` !== dup.targetKey) return b;
                  return {
                    ...b,
                    salesforce_matches: (b.salesforce_matches || []).map((m, i) =>
                      i === 0 ? { ...m, id: result.id } : m,
                    ),
                  };
                }));
                setDupAlert(null);
                return sfToastWithLink(`Created in Salesforce`, result.url);
              },
              error: (err) => {
                // Block-mode rule, or any other failure: roll the row back and
                // leave the alert open with saving=false so the rep can decide.
                setSearchResults(prev => prev.map(b => {
                  if (`${b.name}|${b.address}` !== dup.targetKey) return b;
                  return { ...b, salesforce_status: 'not_in_sf', salesforce_matches: [] };
                }));
                setDupAlert(prev => prev && { ...prev, saving: false });
                return `Override blocked: ${err instanceof Error ? err.message : 'unknown error'}`;
              },
            });
          }}
        />
      )}
    </div>
  );
};

export default ProspectingPage;